/**
 * The action clock in the browser (Phase 59.3): over-the-shoulder real time.
 *
 * Game builds the world as for any match, then hands each frame here: input → this unit's
 * intent → fixed PHYS_DT ticks of the Match (one tick at a time, so a mouse turn or a jump
 * press lands on exactly one tick) → unit meshes, camera, bullets, effects, HUD. Logic stays
 * in Match / core; this file only reads state and turns input into intents.
 *
 * Input is taken in the capture phase on window and stopped there while the host is live, so
 * the turn game's keys and camera drag never see it (` still opens the dev console).
 * Mouse look uses pointer lock: click the view to play, Esc to release.
 */

import type { ImpactResolution } from '../core/resolveImpact';
import { structureFrame } from '../core/hits';
import { shellExplosion } from '../core/explosions';
import { ExplosionField, burstDetail } from '../rendering/explosionLook';
import type { ExplosionClass } from '../constants';
import { airstrikeBlast, airstrikesLeft } from '../core/airstrike';
import type { RespawnPick } from '../core/respawnPick';
import type { CoverHeld } from '../ui/coverCue';
import { coverPointsNear } from '../core/coverMap';
import { coverSlotsNear } from '../core/coverSlots';
import { aheadOf, coverFace, coverHoldFor, digFace, gapTo, leanSpot, pickCover, pickSlot, pressedSpot, slotFor, slotHoldFor, walkTo, SLOT_LEAN_BONUS_PEOPLE, TAKE_COVER } from './takeCover';
import { lawSpentConfig } from './lawSpent';
import { TiltShift } from '../rendering/tiltShift';
import { PERSON_PATH, cameraLook, placeCamera } from '../rendering/cameraLook';
import { OrderRadial } from '../ui/orderRadial';
import { CommandRing } from '../ui/commandRing';
import { previewCall, standingCalls, isCallSub, shipDestLabel, waveShipCall, type CallShape, type CallSub } from '../ui/callMarks';
import { CallMarksView, CALL_MARK_LOOK } from '../rendering/callMarks';
import { CommandCard, type CardAction } from '../ui/commandCard';
import { commandLayout, RING_F, RING_G, ringWithout } from '../ui/commandLayout';
import { podsLeft } from '../core/pods';
import { VERBS, SUPPLY_DROP_VERBS, hiddenVerbs, contextualInputs, dismountInputs, propNear, rideInputs, underCursor, verbInputs, type Here, type MenuInput } from '../core/orderMenu';
import { CommandOverlay, commandViewEnabled, friendlyMarkersOn } from '../rendering/commandOverlay';
import { PlanetTableView, planetTableSetting } from './planetTableView';
import { RadarCoverage, radarCoverageSetting, radarNote, RADAR_MS } from '../ui/radarCoverage';
import { RadarReachView } from '../rendering/radarReachView';
import { commandTable, pickSquad, type CommandTable } from '../core/commandTable';
import { scoreTable, matchShape, howWon, teamName } from '../core/scoreboard';
import { setScoreboardSource, showEndCard, hideEndCard } from '../ui/scoreboardView';
import * as THREE from 'three';
import type { AmmoType, Bullet, ShipCrash, CoverHides, CoverHold, CoverPoint, CoverSide, CoverSlot, DropshipFlight, GrenadeKind, GroundWeaponKind, PlanetState, PlayerState, Stance, StructureState, UnitIntent, Vec3 } from '../types';
import { dcos, dsin } from '../core/dmath';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub, uprightFrame } from '../math';
import { LADDER_DIORAMA, LADDER_ENTRY, LADDER_LAST, PROP_SNAP_PEOPLE, WheelSteps, carry, centreOf, commandLadder, glide, ladderAt, pan, turn, type LadderStop } from './commandCamera';

/** 65.15: a thrown grenade leaves this far above the aim (radians): a lob. */
const THROW_LOFT = 0.35;
/** 67.70: the aimed call's mark is asked again at least this often (s) while it is held still: a call spent, the picture newer. */
const CALL_AIM_EVERY = 0.5;
import { weaponSpec, weaponOf, roundsLeft, reserveLeft, primaryOf, sidearmOf, mountedGunSpec, windShare } from '../core/weapons';
import { quantizeIntent, type Match, type ActionEvent, type SimPresenter } from '../match/match';
import { seatActor } from '../match/input';
import type { PlayerMesh } from '../rendering/renderer';
import type { ParkedShipView } from '../rendering/farFieldPlay';
import { structureHealth } from '../core/body';
import { updateAnimation } from '../rendering/playerModel';
import { frameLerp } from '../rendering/camera';
import { getPlayerWorldPosition, isGameOver, checkWinCondition, arcDistance } from '../core/gameState';
import { getSurfaceRadius } from '../core/surface';
import { headingOf, obstaclesNear, planetObstacles, rightOf, turnHeading, type ObstacleRef } from '../core/surfaceWalk';
import { leanOffset } from '../core/lean';
import { aimDirection, hitboxCenter, muzzlePoint, pointInPerson, segmentCovered } from '../core/bullets';
import { chooseAimTarget, holdAimTarget, muzzleBlocked, aimNearOn, AIM_NEAR_PERSONS, aimAssistDeg, assistEye, assistSees, steadyAssistTarget, easeAssist, solvedAimWant, assistBodies, ASSIST_RETURN_S, leadPoint, windAimPoint, yawToward, windAimStep, WIND_AIM_EVERY_S, type AimHit, type AimHold, type AssistHold, type WindFlight, assistAppliesTo, showLanding, marchAimTarget, firstBodyAlong, AIM_FINE_PERSONS, AIM_CONVERGE_MIN_PERSONS, type AimTarget, holdConverge, glideConverge, type ConvergeHold } from './aimTarget';
import { pointBlocked, planetShapes, shapesNear, primDistance, firstMovingHit } from '../core/hits';
import { seatChoiceInReach, vehicleInReach, moveUpSeat, crewSwitchSeat, seatPoint, seatTaken, seatUnit, hullOffersSeats } from '../core/seats';
import { BODIES, type BodyKey } from '../constants/body';
import { SEAT_POINTS } from '../constants/hitboxes';
import { solveAimPitchDrag, shotFall, predictShot, aimRayStopsOn } from '../core/aimSolve';
import { roundReach } from '../core/ballistics';
import { actionWindAccel, windAt, windSeconds } from '../core/wind';
import { ACTION_WIND, ENTRENCH, MOUNT_LAUNCHERS } from '../constants/action';
import { groundMaterial } from '../core/groundMaterial';
import { DIG_GROUND } from '../core/dig';
import { aimMode, type AimMode } from './aimMode';
import { pauseToCommand, menuHoldsMatch } from './commandPause';
import { squadOf } from '../core/squads';
import { toMember, memberFireToggle, DIRECT_ORDER_SECONDS } from '../core/memberOrders';
import { weatherEffectHere } from '../core/weather';
import { topoEnabled } from '../rendering/topoOverlay';
import { GroundHud } from '../ui/groundHud';
import { killLines, type KillLine } from '../ui/killFeed';
import { groundHudView, pitTraverse, objectiveBeat, consequenceBeat, carryBeatAfter, holdBreaks, columnBeat, carryBeacons, type StripLine } from '../ui/groundHudView';
import { rescueBeat } from '../ui/rescueBeat';
import { L0_KEYS, l0KeyIs, l0Keys } from '../ui/l0Keys';
import { tankGunnerHelp, tankCommanderHelp } from '../ui/seatHelp';   // 69.36
import { squadCards, squadsForKey, squadLeaders, type SquadCard, type SquadReport } from '../core/squadCards';
import { personEyePoint, personLineOfSight, personPointOn } from '../core/fineTerrain';
import type { IconId } from '../ui/commandIcons';
import { gravityAccel } from '../core/physics';
import { PHYS_DT, TEAMS, BANDAGE, BEACON, G, COVER_POSE_SIZE, AIM_YAW_MAX, GAIT, MOUNTED_GUNS, DROPSHIP } from '../constants';
import { buildPlayHmg, hmgLiveMount, showHmgLive, setGunParts, gunShotPose, gunReloadPose, HMG_ON_CRADLE, type Parted } from '../rendering/gunLooks';
import { carriesRocket } from '../core/rocketStock';
import { weaponCycle, carriesPanzerfaust, carriesMissile } from '../core/loadout';
import { shipMotion } from '../core/antiDropshipMissile';
import { ANTI_DROPSHIP_MISSILE_DIALS } from '../constants/weapons';
import { MissileFlights } from '../rendering/missileFlights';
import { AntiDropshipMissileOverlay } from '../ui/antiDropshipMissileOverlay';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { PersonView, muzzleBlend, type CoverFace, type GroundProbe, type PersonRide } from '../rendering/personView';
import { buildVehicleModel, bakeVehicleModel, updateVehicleModel, damageVehicleModel, buildVehicleWreck, setTruckLoad, truckLoadShare, VEHICLE_KINDS, type VehicleModel, type VehicleKind, type CrewRole } from '../rendering/vehicleModels';
import { poseMount, firedMuzzle } from '../rendering/technicalMounts';   // 69.21 step 5: the launchers' mounts in play
import { poseMortarCrew, mortarLayOf, MORTAR_RIG } from '../rendering/mortarLooks';
import { buildTank, recoilBarrel, damageTank, poseCupola, buildTankRound, buildSabot, poseSabot, TANK_PICKS, TANK_GUN as TANK_GUN_LOOK } from '../rendering/tankLooks';
const _tankZ = new THREE.Vector3(0, 0, 1), _tankV = new THREE.Vector3();   // 69.29: the tank's look (the owner's picks)   // 69.23: the mortar carrier's rig in play
import { buildVehicleLook, buildVehicleShape, D_VEHICLES, type DVehicle } from '../rendering/vehicleLooks';
import { TANK } from '../constants/action';
import type { TankRound, SeatState } from '../types/body';
import { tankRoundSpec } from '../core/weapons';
import { tankMuzzle } from '../core/turret';
import { drawGunsight, dropMarks, GUNSIGHT_FOV_SHARE } from './gunsight';
import { SCORCHED } from '../rendering/renderer';
import { buildHmgModel, bakeHmgModel, aimHmg, buildHmgWreck, type HmgModel } from '../rendering/hmgModel';
import { dressHmgD, dressHmgWreckD } from '../rendering/footholdLooks';
import { rideOf, ridersAboard } from '../ui/dropshipMarks';
import { gunshipLines } from '../ui/gunshipStatus';
import { buildRideCabin, updateRideCabin, rideEye, rideShelter, disposeRideCabin, RIDE_LOOK, type RideCabin } from '../rendering/rideCabin';
import { NEAR_MIN } from '../rendering/cameraDepth';
import { structureBaseRadius } from '../core/buildSeat';
import { buildDropship, mountContainer, mountVehicle, updateDropship, bakeDropship, bakeContainer, buildTroopContainer, setContainerRamp, nacelleTiltFor, easeTilt, easeRamp, type DropshipModel, type TroopContainer } from '../rendering/dropshipLooks';
import { parkedHelis } from '../core/helipad';
import { shipBodyOf } from '../core/shipBody';
import { HELIPAD_LOOK } from '../rendering/helipadLook';
import { boardingOpen, boardingSortie, dropshipsLeft, gunshipsLeft, helisLeft, parkedShips, shipEta, shipTakeoff, type DropshipPose } from '../core/dropship';
import { flightNow } from '../core/shipNow';
import { shipInReach } from '../core/piloted';
import { crashPose } from '../core/shipCrash';
import { FallingShip, WreckView, crashImpact, lostNacelles } from '../rendering/shipCrashLook';
import { pdMuzzle } from '../rendering/pointDefenceLooks';
import { SHIP_RIDE } from '../core/shipRide';
import { buildEmplacementModel, layTurret } from '../rendering/emplacementModel';
import { PERSON_UNITS, type PersonKit } from '../rendering/personModel';
import { BrassField, BRASS_LOOK, EJECT, brassShown } from '../rendering/brass';
import { bikeHeading } from '../core/drive';
import { vehicleFrame, type VehicleFrame } from '../core/vehicleFrame';
import { inHull, liftOverHull } from './seatCamera';
import { drives } from '../core/body';
import { SilhouetteCrowd } from '../rendering/silhouetteCrowd';
import { GroundWearView } from '../rendering/groundWearView';
import { ActionSound } from './actionSound';
import { CANNON_VOICES, cannonFuses, type CannonKey } from '../audio/autocannonMix';
import { tickLag } from '../audio/gunfireMix';
import { DeathPresenter } from './deathPresenter';
import { corpseConfig, type DeathCause } from './corpseStore';
import type { DeathPush } from '../rendering/deathRagdoll';
import { LEAN_RENDER_EVERY } from './autoplay';
import { actionConductorState, actionMatchPhase } from '../audio/actionMusic';
import type { ConductorState } from '../audio/conductor';
import { Tracers } from '../rendering/tracers';
import { ObjectiveMarkers, type ObjectiveMarkerSite, type MarkerGround } from '../rendering/objectiveMarkers';
import { standingOn, activeSites } from '../core/capturePoints';
import { sitesAsKnown, knownObjectives } from '../core/objectiveIntel';
import { muzzleFlashParts } from '../rendering/muzzleFlash';
import { flashMaterial, releaseFlashMaterial } from '../rendering/flashMaterials';
import { buildGunshipView, updateGunshipView, gunshipFired, turretFired, GuidedRockets, type GunshipView } from '../rendering/gunshipLook';
import { FlareField, COUNTERMEASURE_LOOK, TURRET_TRACERS, AIRBURST_LOOK, drawAirburst, drawTurretKill, type FlareSeen } from '../rendering/countermeasureLooks';
import { flareAt } from '../core/decoys';
import { mountedGunMuzzle } from './mountedMuzzle';
import { windFelt, smokeDrift } from '../rendering/windView';
import { bandagesLeft, bandageRefusal, healProgress, nearestPatient, medicReach } from '../core/medical';
import { crateInReach, supplyNeeds } from '../core/supply';
import { beaconsLeft, standingBeacon } from '../core/beacon';
import { mortarSetupSeconds } from '../core/mortar';
import { Landers } from '../rendering/lander';

const _mz = new THREE.Vector3();
const _bp = new THREE.Vector3(), _bo = new THREE.Vector3(), _ba = new THREE.Vector3(), _bu = new THREE.Vector3(), _bf = new THREE.Vector3();   // (65.47: a case's throw)
import { unitPaletteFor, type UnitPalette } from '../rendering/unitPalette';
import { buildHelicopter, bakeHelicopter, updateHelicopter, type HeliModel } from '../rendering/helicopterLooks';
import { buildUtilityDrone } from '../rendering/utilityDrone';
import { hullName } from '../rendering/orbitShips';
import { droneRunPose, dronesFree } from '../core/supplyDrone';
import { setTruckAmmo } from '../rendering/ammoBoxes';   // 69.34: the truck bed's rounds
import { drawnHelmet } from '../rendering/armourLooks';
import { skipWhenHidden } from '../rendering/hiddenWalk';
import { handOf, handSide, loadPlayerHand, type Hand } from '../rendering/handedness';
import { MotorPools, motorPoolShown } from '../rendering/motorPoolPlay';
import { RearmArms } from './rearmArms';
import { rearmArmShown } from '../rendering/rearmArm';
import { LighterViews } from '../rendering/lighterLook';
import { countOf, defaultOn } from '../core/defaultOn';

export interface ActionHostOptions {
  match: Match;
  scene: THREE.Scene;
  /** S3: the ground radius drawn at a direction (the fine-ground patch's, tapering to the mesh); absent = the mesh. */
  drawnGround?: (planet: PlanetState, dir: Vec3) => number;
  /** 208's follow-up: a structure's base as drawn (its distance from its planet's centre: seated on its plinth, rendering/groundSeat.ts), or null when it is not drawn; absent = the sim's base (core/buildSeat.ts structureBaseRadius). */
  drawnBase?: (structureId: number) => number | null;
  /** 69.74 step 2b: whether a structure is drawn by the fog (its side's, or seen); absent = all are. The parked helicopters show with their pad. */
  structureShown?: (structureId: number) => boolean;
  camera: THREE.PerspectiveCamera;
  canvas: HTMLElement;
  playerMeshes: Map<number, PlayerMesh>;
  /** 63.3's follow-up: a structure's drawn group (the renderer's), so a gun pit's gun-layer is drawn in its seat as drawn. */
  structureGroup?: (id: number) => THREE.Object3D | undefined;
  /** The unit the viewer plays, or null to watch (all bots; the camera rides the first unit alive). */
  humanId: number | null;
  /** Simulation speed multiplier (autoplay fast-forward). */
  timeScale?: number;
  /** 66.4: a host step before every tick (the stand-in commanders re-plan here, as in the harness). */
  beforeTick?: () => void;
  onGameOver: (winnerTeam: number | null) => void;
  /** R after the match: play again. */
  onRestart?: () => void;
  /** M: leave for the main menu. */
  onExit?: () => void;
  /** Stop (a draw) after this many ticks — the duel harness's time limit. */
  tickLimit?: number;
  /** I8: the tablet key (ui/l0Keys.ts: Q since 68.2b, G before) opens / puts away the drone tablet (Game owns it; the host idles its input meanwhile, see setSuspended). */
  onDroneTablet?: () => void;
  /** The corner map's zoom: − out, = in (owner: the minimap gets zoom, not pan). */
  onMapZoom?: (dir: number) => void;
  /** I9: V rides your latest drone's camera (the feed takes its own keys once up; Game owns it). */
  onDroneFeed?: () => void;
  /** 64.11: a seat's shell landed — Game presents the impact as it does a turn shot's (effects, the crater, the props). */
  onShellImpact?: (e: Extract<ActionEvent, { kind: 'shellImpact' }>) => void;
  /** 69.24: a crash's piece's impact — its ground only (the crater drawn, what stands on it re-seated); the crash's look is crashDown's */
  onCrashGround?: (r: ImpactResolution) => void;
}

const MOUSE_SENSITIVITY = 0.0022;   // radians per pixel
const MAX_PITCH = 1.3;
const MAX_TICKS_PER_FRAME = 240;
/** 68.2b: F held this long (ms) opens the ground's order menu; a shorter press is a tap (the contextual order). */
const L0_HOLD_MS = 200;
/** 68.5: a member picked for the next order stays picked this long (s). */
const MEMBER_PICK_SECONDS = 10;
/** 68.3: the strip's cards (and what you see of your other squads) are refreshed every this many frames. */
const CARD_FRAMES = 10;
/** 68.2b: how far (people) the ground's *here* reaches along your line of sight past the aim's 80, the weather allowing. */
const HERE_REACH = 400;
// over the shoulder: behind, above, to the right — person-scale, from the scale profile (core/scaleProfile.ts)
const CONVERGE = 14;               // watching a bot, the camera looks at its aim line this far out (driving, it converges where the line of sight lands — 67.10)
const ZOOM_MIN = 0.6, ZOOM_MAX = 3.5;   // the wheel's range on the shoulder boom (owner, Oct 10: "can't zoom out")
const ZOOM_KEY = 'orbituary-action-zoom';
function loadZoom(): number { try { const v = Number(localStorage.getItem(ZOOM_KEY)); return Number.isFinite(v) && v >= ZOOM_MIN && v <= ZOOM_MAX ? v : 1; } catch { return 1; } }
function saveZoom(z: number): void { try { localStorage.setItem(ZOOM_KEY, String(z)); } catch { /* storage unavailable */ } }
const STREAK = 2.5;                 // a tracer's shortest streak (units, × k)
/** 63.1d: the drawn transport — its ramp's seconds to let down or close, its front wheels' steer (as the bike's), and the driver's hands above the hatch's anchor (model units). */
const TRANSPORT_LOOK = { rampSeconds: 1.2, steerMax: 0.45, wheelbase: 2.05 };
/**
 * 63.16 (the owner: *"camera for dropship in flight as dropbox sitter drags across ground sometimes if you press v while in
 * flight"*): the usual view of a unit aboard a ship follows the ship as drawn, not the unit's own spot (the sim pins a rider to
 * the ground under its ship: core/dropship.ts pinBoarded) — its pivot `pivot` people over the ship's foot, the boom `back`
 * people behind and `up` over it (× the zoom), the camera at least `clear` people over the ground.
 */
export const RIDE_CHASE = { pivot: 3, back: 14, up: 3, clear: 1 };
/** 67.60's switch-on: within this many people of the player's unit (or with it inside) a vehicle's hinged parts are live. */
export const VEHICLE_LIVE_PEOPLE = 12;
/**
 * 67.60: a vehicle's crew lids by its seats: 1 open, 0 shut. 67.63 (the owner: "Driver can just be in the vehicle, gunner sticks
 * head out"): only the gunner's opens while its weapon seat is crewed; the driver and the spotter sit inside, their lids shut.
 */
function vehicleCrew(s: StructureState): Partial<Record<CrewRole, number>> {
  // 69.29: the tank's lids — the commander's (the cupola) open while crewed; 249 (the owner: "we don't need the gunner of the
  // tank to be sticking out"): the turret gunner rides inside, its hatch shut
  if (s.type === 'tank') {
    const commander = (s.seats ?? []).findIndex((q, k) => q.role === 'weapon' && k !== TANK.turretSeat);
    return { driver: 0, gunner: 0, spotter: commander >= 0 && s.seats?.[commander]?.occupant ? 1 : 0 };
  }
  const gunner = (s.seats ?? []).some(q => q.role === 'weapon' && q.occupant !== null) ? 1 : 0;
  return { driver: 0, gunner, spotter: 0 };
}
/** X31: the drawn bike — the lean into a turn (atan of speed × turn rate over `leanG`, people a second², capped), lying on its side when wrecked, the front wheel's steer from the turn over the wheelbase (people). */
const BIKE_LOOK = { leanG: 30, leanMax: 0.6, fallen: 1.45, steerMax: 0.6, wheelbase: 1.2 };
const _bx = new THREE.Vector3(), _by = new THREE.Vector3(), _bz = new THREE.Vector3(), _bm = new THREE.Matrix4();
const _rideAt = new THREE.Vector3(), _rideLook = new THREE.Vector3(), _rideUp = new THREE.Vector3();   // 207 (7): the seat's eye
const _pilotAt = new THREE.Vector3();   // 69.17 step 3b: the pilot's seat
const _cupolaQ = new THREE.Quaternion(), _cupolaDir = new THREE.Vector3();   // 249: the cupola gun's lay
/** 208's follow-up: the landing pad's deck (rendering/footholdLooks.ts landingPad: matting 14 m square, its top 0.11 m up), in people. */
const PAD_DECK = { half: 7 / 1.8, top: 0.11 / 1.8 };
/** 67.15: a tracer streaks the distance its round covers in this long — over a frame and a half, so a fast round draws one line, not a dash jumping in gaps. */
const TRAIL_SECONDS = 1 / 40;
/** 67.15: a round that stops leaves its streak, fading into the hit point, this long (one fired and stopped between two frames still shows). */
const AFTERIMAGE_SECONDS = 0.07;
/**
 * 69.9: a point-defence burst drawn — `tracers` tracer rounds a burst (CIWS, C-RAM) leaving `spacing` seconds apart at `speed`
 * people a second, each a `streak` people long; a miss flies on past the round by `past` of the range.
 */
const PD_TRACERS = { tracers: { ciws: 4, cram: 3 }, spacing: 0.045, speed: 450, streak: 3, past: 0.35 } as const;
/**
 * 67.78 (the owner: "rounds should self destruct after some time as it will look cool"): a missed burst's self-destructing
 * rounds (audio/autocannonMix.ts `cannonFuses`: how many, after what fuse) — each tracer flies on to its fuse, a hair off the
 * line (`scatter` radians), and bursts there: a flash and a dark puff at least `minAngle` across from the camera (so a burst
 * far out still reads), its pop heard from there (game/actionSound.ts `cannonPop`).
 */
const PD_AIRBURST = AIRBURST_LOOK;   // (69.58: shared with the roof turret's misses, rendering/countermeasureLooks.ts)
/** 65.46: a coasting rocket's smoke — a puff every `everySeconds`, `sizePeople` across at first, swelling `grow`× as it fades over `seconds`. */
const ROCKET_PUFF = { everySeconds: 0.06, sizePeople: 0.18, grow: 3, seconds: 1.4, peak: 0.45, color: 0x9a9a96 };
/** Half the slope probes' baseline, in person heights: a person end to end, across the fine ground's half-person facets. */
const SLOPE_BASELINE = 0.5;
const GUN_POWER_RATE = 30;          // 64.11: power per second while W / S is held in a seat
/** 63.1f's follow-up (the owner: "I can't figure out how to turn the cannon"): a shell gun's traverse on A / D (rad/s): heavy, and visible */
const GUN_TRAVERSE_RATE = 0.9;
const MAX_BULLETS = 256;

/** A short-lived effect mesh: it grows by `grow` and fades from `peak` over its life, drifting by `rise` a second; a shared geometry is not disposed with it. */
interface Flash { mesh: THREE.Mesh; life: number; age: number; grow: number; peak?: number; rise?: THREE.Vector3; shared?: boolean; base?: number }

/**
 * 64.10: does the held unit's intent need to go in this frame? When it changed, or when it carries a one-shot impulse
 * (a turn, a jump, a reload, a seat, a bandage, a restock) — however still the rest is. The bandage and the restock
 * were missing: H standing still with the mouse at rest sent nothing (the owner, Oct 10: "I can't bandage myself in lab").
 */
/**
 * 63.8 (the coordinator's ruling, for the owner's hands): the player's gait this tick, and whether its click puts the gun
 * up. The gun is up while fire is held and for GAIT.readySeconds after the last click that fired: moving, the shooting
 * walk; down, the move (a click from it fires at once, unaimed — the gun was down on that tick — and puts it up). Shift
 * held past GAIT.sprintHold and moving, the sprint (no fire) whatever the gun does — and a click the sprint eats raises
 * nothing (supervisor-1's page look: after a sprint the player walked the slow walk for a second with no shot fired).
 * Taking cover's own walk is at the walk; standing, no gait.
 */
export function playerGait(o: { moving: boolean; coverWalk: boolean; shiftHeldFor: number | null; sinceFire: number; fire: boolean }): { gait: 'move' | 'sprint' | undefined; raised: boolean } {
  const gunUp = o.sinceFire < GAIT.readySeconds;
  const sprinting = o.shiftHeldFor !== null && o.shiftHeldFor >= GAIT.sprintHold;
  const gait = !o.moving || o.coverWalk ? undefined : sprinting ? 'sprint' as const : gunUp ? undefined : 'move' as const;
  return { gait, raised: o.fire && gait !== 'sprint' };
}

export function intentToSend(cur: UnitIntent | undefined, it: UnitIntent): boolean {
  const same = !!cur && cur.forward === it.forward && cur.right === it.right && cur.aimPitch === it.aimPitch && cur.fire === it.fire && (cur.stance ?? 'stand') === (it.stance ?? 'stand') && (cur.weapon ?? 'rifle') === (it.weapon ?? 'rifle')
    && cur.cover?.pose === it.cover?.pose && cur.cover?.side === it.cover?.side && cur.cover?.out === it.cover?.out   // 66.7: taking, peeking from or leaving cover
    && (cur.aimYaw ?? 0) === (it.aimYaw ?? 0)   // 65.25a cut 2: the aim across
    && !!cur.dig === !!it.dig;   // 66.8: the dig key pressed or let go, standing still
  const sameGun = (cur?.gun?.power ?? 0) === (it.gun?.power ?? 0) && cur?.gun?.weaponKey === it.gun?.weaponKey;
  // 69.17 step 3b: a pilot's controls held (each axis), and taking or leaving the seat an impulse
  const a = cur?.fly ?? {}, b = it.fly ?? {};
  const sameFly = !!cur?.fly === !!it.fly && (['forward', 'turn', 'strafe', 'lift', 'throttle', 'yaw', 'pitch', 'roll'] as const).every(k => (a[k] ?? 0) === (b[k] ?? 0)) && !!a.jets === !!b.jets;
  return !(same && sameGun && sameFly && it.turn === 0 && !it.jump && !it.reload && !it.seat && !it.heal && !it.resupply && !it.pilot && !it.caseDrop);
}


const MAP_SHOWN_KEY = 'orbituary-hud-map';
/** 67.35: the corner map's toggle, remembered per browser (a convenience: unreadable storage means shown). */
function readMapShown(): boolean { try { return localStorage.getItem(MAP_SHOWN_KEY) !== '0'; } catch { return true; } }
function writeMapShown(on: boolean): void { try { localStorage.setItem(MAP_SHOWN_KEY, on ? '1' : '0'); } catch { /* storage unavailable */ } }

/** 68.39: a verb's note and warning, said after it (an airstrike's count left; friendlies in its reach). */
const extraOf = (r: { note?: string; warn?: string }): string => `${r.note ? ` (${r.note})` : ''}${r.warn ? ` — ${r.warn}` : ''}`;

/** 67.73: a flash or smoke puff is drawn while at least this many pixels across (sub-pixel ones cost a draw each, seen from orbit). */
const FLASH_MIN_PX = 1;
/** 67.73: a vehicle is drawn while at least this many pixels across (taking it as `VEHICLE_ACROSS_PEOPLE` long: the bike's 1.2, the 6×6's 4). */
const VEHICLE_MIN_PX = 1.5, VEHICLE_ACROSS_PEOPLE = 2.5;

/** fix/69.21-second-tube: how long a click on a mounted launcher is held for its round (seconds: past the mount's own interval). */
const LAUNCHER_CLICK_HOLD = 1.5;

/** 69.30: a round type as the rearm cue names it. */
const AMMO_WORD: Record<AmmoType, string> = { tankHe: 'HE', tankSolid: 'SOLID', rocket: 'rocket', missile: 'missile', mortarBomb: 'bomb', lightShell: 'shell' };

export class ActionHost {
  private readonly o: ActionHostOptions;
  private tickPresenter: SimPresenter | null = null;
  private over = false;
  private keys = new Set<string>();
  private mouseDown = false;
  private pendingYaw = 0;
  /** Where the player looks: the mouse's pitch (the camera's). */
  private pitch = 0;
  /**
   * 67.10: the pitch the gun fires at. `solved`: lifted for the drop so the shot lands on what the
   * crosshair rests on (core/aimSolve.ts, as the bots aim); `direct`: the look pitch itself.
   */
  private firePitch = 0;
  private readonly aim: AimMode = aimMode();
  /** Whether what the crosshair rests on is within the gun's reach (the crosshair dims when not). */
  private inReach = true;
  /** The impact dot's smoothed screen position (null = hidden). */
  private dot: { x: number; y: number } | null = null;
  /** 65.25a: the body the shot's solution is for (aim assist), and a seated gun's elevation for it (null: the lay's). */
  private assist: { id: number; at: Vec3 } | null = null;
  private gunAimPitch: number | null = null;
  /** 65.25a cut 2: the aim across sent with the intent (radians, + right; 0: along the heading) — the assisted body's lead and the wind. */
  private aimYaw = 0;
  /** 65.25a cut 2: the assisted body's motion as the host has watched it (its aim point, when, and its velocity, smoothed). */
  private assistMotion = new Map<number, { at: Vec3; t: number; v: Vec3 }>();
  /** 65.25f: the body the assist holds (its pick kept through a ridge's flicker), and its correction eased (radians over the unassisted aim; when). */
  private assistHold: AssistHold | null = null;
  private assistEase = { pitch: 0, yaw: 0, t: 0 };
  /** 65.39: when the assist last had a pick (its correction stays capped ASSIST_RETURN_S after). */
  private assistLastAt = -Infinity;
  /** 65.25e: the last wind solve — for which body and round, when, and its offset from the body's lead (aimTarget.ts windAimPoint). */
  private windAim: { id: number; share: number; t: number; offset: Vec3 } | null = null;
  /** Where the line of sight lands, in units from the eye (the camera converges there); null until the first frame. */
  private converge: number | null = null;
  /** 67.35: the ground HUD (lane E's mock) and what only the host knows for it */
  private ground!: GroundHud;
  private xh = { friend: false, far: false, blocked: false };
  /** when you took this body (match seconds): the keys card is open for its first seconds */
  private heldSince = 0;
  /** ? held: the keys card open */
  private helpHeld = false;
  /** the corner map shown (N; remembered in this browser) */
  private mapShown = readMapShown();
  /** Decision 136: the HUD's own cost a frame, a rolling mean (ms) — for the measurement and F3. */
  hudMs = 0;
  /** 67.35: where the game mounts the corner map (ui/topoMap.ts, built at HUD_MAP_SIZE). */
  get mapSlot(): HTMLElement { return this.ground.mapSlot; }
  /** 67.32 (d): the aim target held through a sudden jump nearer */
  private aimHold: AimHold | null = null;
  private convergeDrawn: number | null = null;
  /** 65.45 (b): the converge's range held through a crest's flicker (aimTarget.ts holdConverge). */
  private convergeHold: ConvergeHold | null = null;
  /** The shoulder boom's length, × this (wheel; persisted). 1 = the profile's boom. */
  private zoom = loadZoom();
  /** 66.7 (a): the shoulder drawn (−1 … 1, gliding to the cover's open side) for the unit it was drawn for, and the peek's lean and rise drawn (person heights) */
  private shoulderDrawn: { unitId: number; side: number; lean: number; rise: number } | null = null;
  /** 67.21: the player's strong hand (Settings; default right) — the unit they control draws with it, the camera rides over its shoulder */
  private playerHand: Hand = loadPlayerHand();
  /** 67.21: every other unit's hand, from the match seed (cached per id) */
  private hands = new Map<number, Hand>();
  /** X31: the bikes, drawn here (they move every tick; the structure's own mesh is empty), made on first sight */
  private bikeViews = new Map<number, VehicleModel>();
  /** 63.1d: each transport's driver's hand anchors (its hatch rim), and how far its ramp is let down (0 … 1, eased). */
  /** 63.1d's follow-up: the form each transport is drawn in — whole, damaged once hurt (63.1e's damaged form), the wreck when destroyed. */
  private vehicleForms = new Map<number, 'whole' | 'damaged' | 'wreck'>();
  /** 67.60's switch-on: each vehicle's hinges as built — live (it turns them) or at rest (baked as they stood), and its crewed lids */
  private vehicleHinges = new Map<number, string>();
  /** 67.60: a live vehicle's crew lids, eased open and shut (role → share) */
  private lidShares = new Map<number, Partial<Record<CrewRole, number>>>();
  private rampDown = new Map<number, number>();
  /** 65.22: each emplaced HMG drawn, aimed by its gunner (and its wreck once destroyed). */
  /** 69.3b: the dropships in the air (by flight id) and the containers standing (by structure id; the wreck once broken). */
  private droneViews = new Map<number, { carry: THREE.Mesh; empty: THREE.Mesh }>();   // 69.32: each supply drone on a run, by its run's id (with its crate, and without)
  /** 69.59 step 1: each helicopter's flight as drawn (look C), its bank and its doors eased */
  private heliViews = new Map<number, { m: HeliModel; bank?: number; doors?: number }>();
  private dropshipViews = new Map<number, { m: DropshipModel; payload: THREE.Object3D; tilt?: number; bank?: number; ramp?: number; parts?: number[]; fall?: FallingShip; gun?: GunshipView }>();   // (`tilt`: the nacelles' drawn tilt, eased: dropshipLooks easeTilt; 63.16: `ramp` its container's ramp as drawn, eased: easeRamp; 69.3b, the ship's crash: `parts` the flight's as last drawn — which nacelles its crash lost — and `fall` its fall's look; 63.13 step 2: `gun`, a gunship's parts)
  /** 63.13 step 2: the gunships' guided rockets in flight (made at the first) */
  private guidedRockets: GuidedRockets | null = null;
  /** 69.3b, the ship's crash: each falling ship's velocity as last drawn (world units a second), for its impact's spray (dropped at its `shipCrashed`). */
  private fallen = new Map<number, THREE.Vector3>();
  /** 69.3b, the ship's crash: the wrecks standing (rendering/shipCrashLook.ts WreckView), by structure id. */
  private wreckViews = new Map<number, WreckView>();
  private containerViews = new Map<number, { root: THREE.Group; wreck: boolean; c: TroopContainer; ramp: number }>();
  /**
   * DECISIONS 207 (7): the ride in — the flight the player rides while down (ui/dropshipMarks.ts rideOf), its cabin once hung
   * in the ship's slot, and where the player looks in it; `off` the usual waiting view (the ride view's key), for this ride.
   */
  private ride: { key: number; unitId: number; cabin: RideCabin | null; yaw: number; pitch: number; off: boolean; kits: Map<number, PersonKit> } | null = null;
  /** 208: this frame's reading of the ride (ui/dropshipMarks.ts rideOf: its ship's key — a flight's id, or −1 − team parked on its pad — the riders aboard and the player's seat) */
  private rideNow: ReturnType<typeof rideOf> = null;
  /** the cabin's box kept from the weather's fall while the seat's view is drawn (the camera carries it: weatherView reads it) */
  private readonly shelterBox = { toBox: new THREE.Matrix4(), half: new THREE.Vector3() };
  /** 65.36: a vehicle's .50 (the cupola's, the pintle's) live for the player at its grips — on the model it was made on (a rebuilt one, 67.60's rehinge, gets its own) — and its last shot (the host's clock) */
  private vehicleHmgs = new Map<number, { m: VehicleModel; live?: { group: THREE.Group; gun: { parts: Parted['parts'] } }; firedAt?: number }>();
  private hmgViews = new Map<number, { m: HmgModel; wreck: THREE.Group | null; /** 65.36: the live mount while the player is at its grips (made the first time), and its last shot (the host's clock) */ live?: { group: THREE.Group; gun: { parts: Parted['parts'] } }; firedAt?: number }>();
  /** 63.3's follow-up: each gun pit's seat anchors (lane E's model, meshes stripped) under its drawn group: the gun-layer's place, the handwheel's grips. */
  private pitSeats = new Map<number, { group: THREE.Object3D; root: THREE.Object3D; ride: PersonRide }>();
  private jumpQueued = false;
  private readonly cleanup: (() => void)[] = [];
  private readonly lastPos = new Map<number, Vec3>();
  private readonly wasAirborne = new Map<number, boolean>();
  private readonly flashes: Flash[] = [];
  /** 64.11: one sphere per shell in flight, pooled (a shell is planet-scale: drawn as the turn game's are sized). */
  private readonly shellMeshes: THREE.Mesh[] = [];
  private readonly tracers: Tracers;
  /** 67.15: streaks of rounds that stopped, fading into where they stopped. */
  private afterimages: Array<{ head: Vec3; tail: Vec3; age: number }> = [];
  /** 69.9: the point-defence bursts' tracer rounds in flight (from the drawn muzzle along the line to the round, `len` world units). */
  private pdStreams: Array<{ from: Vec3; dir: Vec3; len: number; age: number; pop?: { key: CannonKey; planetIndex: number; done?: boolean } }> = [];
  private readonly hud: HTMLDivElement;
  private readonly el: Record<string, HTMLElement> = {};
  private hitMarkerUntil = 0;
  /** 65.28: when a round last came near the controlled unit (the host's clock): the cue's pulse */
  private nearMissAt: number | undefined;
  private hurtUntil = 0;
  private time = 0;
  private camInit = false;
  private watchPitch = 0;
  /** The boom's candidate shapes (core/hits.ts shapesNear's scratch). */
  private readonly nearIds: number[] = [];
  private softCapture = false;
  private readonly presenter: SimPresenter;
  /** Ticks stepped (for the smoke's done check and the HUD clock). */
  ticks = 0;
  /** This frame's simulation: ms inside the match's advance and the ticks it ran (the F3 overlay). */
  lastSim = { ms: 0, ticks: 0 };
  /** 65.38: the tick count when this frame's ticks began (a tick's place in its frame times its sounds). */
  private frameTick0 = 0;
  /** 59.6c possession: the unit the viewer drives now (null = watching), and the team they command (null = all bots). */
  controlled: number | null;
  readonly team: number | null;

  constructor(o: ActionHostOptions) {
    this.o = o;
    this.controlled = o.humanId;
    this.team = o.humanId === null ? null : o.match.state.players.find(p => p.id === o.humanId)?.teamIndex ?? null;
    // The commander drives one unit; the rest of the team are bots (they used to stand idle)
    if (this.team !== null && this.controlled !== null) o.match.apply(o.match.stamp(seatActor(this.team), { kind: 'possess', target: { unit: this.controlled } }));
    // Bullet tracers (67.15): one draw of screen-space ribbons, refilled every frame from state.bullets and the afterimages
    this.tracers = new Tracers(o.scene, MAX_BULLETS * 2);

    this.sound = new ActionSound(o.match, o.camera, () => (o.timeScale ?? 1) > 1.5);
    this.hud = this.buildHud();
    this.bindInput();

    const noop = () => {};
    this.presenter = {
      holdFlightEnd: () => false, flightEndReached: noop, launched: noop, probeScan: noop,
      geyserDeflect: noop, impacted: noop, lost: noop, flightOver: noop,
      airborne: air => { for (const l of killLines(this.o.match.state, air.events)) this.feed(l); },   // 69.83: a fall's death in the feed
      actionEvents: (events, gameOver) => this.onEvents(events, gameOver),
    };
    const me = this.human();
    this.pitch = 0;   // level: the player's aim starts where they can see, not where the bot was staring
    if (me) this.weapon = weaponOf(me);   // 65.13 / 65.14: the held unit's primary, not always the rifle
    // 67.14: the pause overlay's SCOREBOARD reads this game's record while it runs
    setScoreboardSource(() => scoreTable(o.match.state, o.match.record, this.human()?.id ?? null, o.match.actionTick));
    // 68.13: ?diorama=1 opens in the commander's view at the diorama stop (with the view enabled), for the profiler and a look
    try { if (commandViewEnabled() && new URLSearchParams(location.search).get('diorama') === '1') { this.commanding = true; this.commandStopTarget = this.commandStop = LADDER_DIORAMA; } } catch { /* no URL */ }
  }

  /** The unit the viewer controls now (possession can change it), or null when watching. */
  controlledUnit(): PlayerState | null { return this.human(); }

  /**
   * I8: while the drone tablet is open the host takes no input — no keys, no mouse look, no pointer
   * lock — so the tablet's own handlers get them. The operator stands still anyway (droneBusy).
   */
  setSuspended(on: boolean): void {
    this.suspended = on;
    this.keys.clear(); this.mouseDown = false; this.jumpQueued = false;
    if (on) { this.softCapture = false; if (document.pointerLockElement) document.exitPointerLock(); }
  }
  private suspended = false;

  /**
   * L1, read-only: the commander's diorama view (O, behind the Lab's Commander view row). The camera pulls up from the
   * shoulder into an overview of the team's ground (one world: the same planet, the same scene); an overlay draws the
   * team's squads with their orders and the enemy as the team knows it; a click shows a squad's order and members.
   */
  private commanding = false;
  private commandBlend = 0;
  /** 68.14 follow-up: the commander's table is on screen (the overlay draws the enemy; the shoulder's markers stand down). */
  get tableShowing(): boolean { return this.commandBlend > 0.05; }
  /** 67.37: lane D's stop for the ears — its focus on the ground, the blend, the stop (null on the ground) */
  private ears: { focus: Vec3; blend: number; stop: number } | null = null;
  /** 67.37: how far into the commander's view the camera is (0 the shoulder … 1), for the weather's lowering (Game). */
  get commandListen(): number { return this.ears?.blend ?? 0; }
  /** 67.38: where the commander listens from (the stop's focus on the ground) once well into the view; null on the ground. */
  get commandFocus(): Vec3 | null { return this.ears && this.ears.blend > 0.5 ? this.ears.focus : null; }
  private commandSel = new Set<number>();
  /** 68.33: the place (the base, a relay, a FOB of ours; 166: or a supply truck) whose card is open at the table, clicked with no squad hit. */
  private commandPlace: RespawnPick | null = null;
  /** 68.4: the cursor, a drag in progress, the radial, the last order's words. */
  private cursor = { x: 0, y: 0 };
  private dragFrom: { x: number; y: number } | null = null;
  private radial: OrderRadial | CommandRing | null = null;
  private radialSince = 0;
  /**
   * 68.41, layout C (the Lab's Command layout; ui/commandLayout.ts): the rings (today's, or C's F and G), which is open,
   * when G went down on the ground (a tap throws the grenade, held it opens the calls), and the table's command card.
   */
  private rings: { today?: OrderRadial; f?: CommandRing; g?: CommandRing } = {};
  private ringKey: 'f' | 'g' = 'f';
  private gDownAt: number | null = null;
  /** 69.47 step 1: when K went down (a tap sets a charge on its timer, held CHARGE_HOLD_S on the wire); null: not held. */
  private kDownAt: number | null = null;
  private card: CommandCard | null = null;
  private readonly layoutC = commandLayout() === 'c';
  /** 68.2b, the menu on the ground: when F went down (ms; a tap is shorter than L0_HOLD_MS), the mouse's travel since
   * the menu opened (pointer locked: fed to the radial as an offset from the crosshair), *here* where the crosshair
   * rests (placeCrosshair), and *here* as the menu opened. */
  private l0FDownAt: number | null = null;
  private l0Vec = { x: 0, y: 0 };
  private here: Here | null = null;
  private openHere: Here | null = null;
  /** 68.3: the squads the menu orders (null: your own, the default; the number keys pick), the strip's cards and what
   * the viewer last knew of each squad (core/squadCards.ts, refreshed every CARD_FRAMES frames), and the hold-Tab
   * picker (the radial with your teammates round the crosshair). */
  private l0Sel: number[] | null = null;
  /** 68.5, one member's direct order: the teammate under the crosshair when F went down (its menu, or a tap picks it), the
   * member picked for the next order (until it is given, Esc, or MEMBER_PICK_SECONDS), and whom the open menu orders. */
  private fOnMate: number | null = null;
  private memberPick: { id: number; until: number } | null = null;
  private menuFor: number | null = null;
  private cards: SquadCard[] = [];
  private cardReports = new Map<number, SquadReport>();
  private cardFrame = CARD_FRAMES - 1;   // (the first frame refreshes: the strip and the leaders from the start)
  private tabDownAt: number | null = null;
  private picker: OrderRadial | null = null;
  /** 68.3b: who leads each squad (both teams: a leader reads from afar), refreshed with the cards. */
  private leaders = new Set<number>();
  private commandNote: { text: string; until: number } | null = null;
  /**
   * 68.13: where the commander's view stands on its stops — 0 the overview, 1 the sheet's squad stop, 2 the diorama
   * (lane E's PERSON_PATH, rendering/cameraLook.ts); the wheel moves the target, the camera eases to it.
   */
  private commandStop = LADDER_ENTRY;
  private commandStopTarget = LADDER_ENTRY;
  /**
   * 68.14: the frame the stops stand round — a centre on the ground (gliding to the team, or at the squad stop and
   * closer to the selection, or where you panned) and a facing latched as you enter (carried along the ground, turned
   * with Q / E). The wheel steps once a flick; leaving keeps the stop, so O returns you where you were.
   */
  private cmdCentre: Vec3 | null = null;
  private cmdFacing: Vec3 | null = null;
  private cmdPan: Vec3 | null = null;
  private panKeys = new Set<string>();
  private wheelSteps = new WheelSteps();
  private radialHere: Here | null = null;
  /** 68.18: where the table's menu opened (a transport under it is the one to ride), and a ride waiting for its destination. */
  private radialAt = { x: 0, y: 0 };
  private pendingRide: { transportId: number; squads: number[] } | null = null;
  private panFrom: { x: number; y: number } | null = null;
  private underWarnedAt = -Infinity;
  /** 68.14: the pick under the cursor, refreshed when the cursor or the view moves (the ring before the click). */
  private hover: { at: string; here: Here | null } = { at: '', here: null };
  /** 68.17: the topo overlay on (L; the Lab's row starts it) */
  private topoOn = topoEnabled();
  /**
   * 68.17: the overlay's strength — full at lane D's squad, near, close and diorama stops, half at the overview, gone by
   * the orbit and on the way back to the shoulder (the camera's blend).
   */
  get topoAmount(): number {
    if (!this.topoOn || !this.ears) return 0;
    const s = this.commandStop;
    return this.ears.blend * (1 - 0.5 * Math.min(1, Math.max(0, s - LADDER_DIORAMA)) - 0.5 * Math.min(1, Math.max(0, s - LADDER_ENTRY)));
  }
  private baseFov = 0;
  private tilt: TiltShift | null = null;
  private stopCam = new THREE.PerspectiveCamera();
  private commandOverlay: CommandOverlay | null = null;
  /** 68.42: the table at planet scale (the Lab's Planet table row; null when off) — at the ladder's overview and orbit stops, globe or flat map */
  private planetTable: PlanetTableView | null = null;
  private readonly planetTableStart = planetTableSetting();
  /** 68.43: the side's radar coverage, sampled while the command view shows — the reach always (wherever there are mounts), the detail with the Lab row Radar detail or R. */
  private readonly radarCover = new RadarCoverage();
  private radarDetail = radarCoverageSetting();
  private radarReach: RadarReachView | null = null;
  private commandPicks: CommandTable | null = null;
  /** 68.7: friendly markers in the shoulder view (the Lab's setting; K flips it). */
  private friendliesKey: boolean | null = null;
  private get friendlies(): boolean { return this.friendliesKey ?? friendlyMarkersOn(); }

  /** 67.19: each team's tickets when first seen (the match keeps only what is left) */
  private startTickets: number | null = null;

  /** 67.19: the L0 music's reading of the fight (Game hands it to the conductor). */
  musicState(): ConductorState {
    const state = this.o.match.state, me = this.human() ?? this.viewUnit();
    const tickets = state.modeState.respawn?.tickets;
    const counts = tickets ? Object.values(tickets) : null;
    if (counts && this.startTickets === null) this.startTickets = Math.max(...counts);
    const team = me?.teamIndex ?? this.team ?? 0;
    const enemies = state.players.filter(p => p.teamIndex !== team);
    const phase = actionMatchPhase(counts, this.startTickets, enemies.filter(p => p.alive).length, enemies.length);
    return actionConductorState(this.sound.pulse, me?.alive ? me.health / 100 : 1, phase);
  }

  /** 65.32: what X steps through, for the hint — the player's own loadout says so; held by another unit, that unit's own slot. */
  private cycleText(): string {
    const h = this.human();
    if (!h) return 'pistol / rifle';
    const list = weaponCycle(h, this.o.match.state.config).join(' / ');
    if (h.carry) return `${list} (your loadout)`;
    const first = this.o.humanId === null ? undefined : this.o.match.state.players.find(p => p.id === this.o.humanId);
    return first?.carry && first.id !== h.id ? `${list} (your loadout stays with your first unit)` : list;
  }

  private human(): PlayerState | null {
    const id = this.controlled;
    return id === null ? null : this.o.match.state.players.find(p => p.id === id) ?? null;
  }

  /**
   * 69.81 (the owner: "when you get killed don't immediately bring camera back to spawn, leave camera by body until respawn so
   * you can see who did it and what's going on, don't change ability to tab to other player or go into overview mode"): the
   * player's own unit down — the spot its body fell, where the camera stays (the mouse turning it) till the respawn brings it
   * back. None once it lives again, in the overview (or easing to or from it), or for a unit taken with Tab (alive: its own view).
   */
  private deathCam(): { id: number; feet: Vec3; heading: Vec3 } | null {
    const h = this.human();
    if (!h || h.alive || this.deathSpot?.id !== h.id) { if (h?.alive) this.deathSpot = null; return null; }
    return this.commanding || this.commandBlend > 0 ? null : this.deathSpot;
  }

  /** The unit the camera rides: the human, else the first living unit. */
  private viewUnit(): PlayerState | null {
    const players = this.o.match.state.players;
    const h = this.human();
    if (h && h.alive) return h;
    // Down (a skirmish brings you back): ride a living teammate until the wave
    if (h) return players.find(p => p.alive && p.teamIndex === h.teamIndex) ?? h;
    // Commanding without a unit: ride one of yours
    if (this.team !== null) return players.find(p => p.alive && p.teamIndex === this.team) ?? players[0] ?? null;
    return players.find(p => p.alive) ?? players[0] ?? null;
  }

  /** S3: the ground as drawn here — the fine patch where it is drawn, else the mesh. */
  /**
   * 208's follow-up (the owner, of the parked wave ship: *"the inside of the box is maybe bigger than the outside of the box
   * when parked on the landing pad"*): how far the side's pad's deck stands over the drawn ground at `dir` (world units; 0 off
   * the pad, or with no pad standing). The pad is seated on its spot (core/buildSeat.ts: its base the coarse ground plus its
   * lift, a plinth under it) — drawn, without a lift, at the highest ground round its 14 m rim — so on a slope its deck stood
   * a person or more over the ground at its centre (measured 1.1 and 1.24 people at a push's two pads), and the ship parked on
   * that ground was sunk in it to the shoulders: the deck cutting the container over the riders' heads, the box outside
   * reading smaller than inside. The deck as drawn (`drawnBase`), else the sim's base.
   */
  private padDeck(team: number, dir: Vec3, planet: PlanetState): number {
    const state = this.o.match.state, pad = state.shipPads?.[team], s = pad ? state.structures.find(x => x.id === pad.padId) : undefined;
    if (!s || s.health <= 0) return 0;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, a = normalize(dir), b = normalize(s.surfaceDir);
    if (Math.acos(Math.max(-1, Math.min(1, a.x * b.x + a.y * b.y + a.z * b.z))) * planet.radius > PAD_DECK.half * person) return 0;
    return Math.max(0, (this.o.drawnBase?.(s.id) ?? structureBaseRadius(planet, s)) + PAD_DECK.top * person - this.groundAt(planet, a));
  }

  /** 69.74 step 2b: the helipad's deck at `dir` over the drawn ground there (its slab seated on its plinth); 0 off it or with it down. */
  private heliDeck(team: number, dir: Vec3, planet: PlanetState): number {
    const state = this.o.match.state, pad = state.dropships?.pads?.[team], s = pad ? state.structures.find(x => x.id === pad.structureId) : undefined;
    if (!s || s.health <= 0) return 0;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    return Math.max(0, (this.o.drawnBase?.(s.id) ?? structureBaseRadius(planet, s)) + (HELIPAD_LOOK.deck / 1.8) * person - this.groundAt(planet, normalize(dir)));
  }

  private groundAt(planet: PlanetState, dir: Vec3): number {
    return this.o.drawnGround ? this.o.drawnGround(planet, dir) : getSurfaceRadius(planet, dir);
  }

  /**
   * The drawn ground's slope under a figure: rise per unit along its right and its front, from
   * central differences a person apart (four probes; the sphere's curvature cancels). The baseline
   * spans the fine ground's facets (a vertex every half person), so crossing one does not jolt the
   * figure; the view smooths it in time besides. The figure's feet follow it (personModel `slope`). 67.60: `lift`, the four
   * probes' mean over the ground at the centre (world units) — the body's drawn height follows that average (GROUND_LOOK).
   */
  private slopeAt(planet: PlanetState, n: Vec3, heading: Vec3): { x: number; z: number; lift: number } {
    const d = SLOPE_BASELINE * PERSON_BODY_HEIGHT * personSetOf(this.o.match.state).k, a = d / planet.radius;
    const right = { x: n.y * heading.z - n.z * heading.y, y: n.z * heading.x - n.x * heading.z, z: n.x * heading.y - n.y * heading.x };   // up × front = the figure's +x
    const at = (t: Vec3, s: number) => { const v = { x: n.x + t.x * a * s, y: n.y + t.y * a * s, z: n.z + t.z * a * s }, l = Math.hypot(v.x, v.y, v.z); return this.groundAt(planet, { x: v.x / l, y: v.y / l, z: v.z / l }); };
    const r1 = at(right, 1), r0 = at(right, -1), f1 = at(heading, 1), f0 = at(heading, -1);
    return { x: (r1 - r0) / (2 * d), z: (f1 - f0) / (2 * d), lift: (r1 + r0 + f1 + f0) / 4 - this.groundAt(planet, n) };
  }

  /** 67.60: the drawn ground as a person view samples it (its feet's), one a planet; null without a drawn ground (the sphere's own is smooth). */
  private readonly groundProbes = new Map<PlanetState, GroundProbe>();
  private groundProbe(planet: PlanetState | undefined): GroundProbe | null {
    if (!planet || !this.o.drawnGround) return null;
    let g = this.groundProbes.get(planet);
    if (!g) { g = { centre: planet.position, radiusAt: d => this.groundAt(planet, d) }; this.groundProbes.set(planet, g); }
    return g;
  }

  /**
   * S3: a grounded person drawn standing on the drawn ground (the sim stands them on the fine ground; the two agree where
   * the patch is drawn). 67.50 (3): never drawn buried — with the Far ridges dial on, a far man stands on the lifted ground,
   * drawn up to the lift above his body (rendering/groundLift.ts).
   */
  private feetDrawn(p: PlayerState, planets: PlanetState[]): Vec3 {
    if (!this.o.drawnGround) return getPlayerWorldPosition(p, planets);
    const planet = planets[p.planetIndex];
    const n = normalize(p.surfaceDir);
    return add(planet.position, scale(n, this.groundAt(planet, n)));
  }

  // ---------------------------------------------------------------------------
  // Frame
  // ---------------------------------------------------------------------------

  frame(dt: number): void {
    this.time += dt;
    // 64.1: the Match owns the accumulator and the backlog rule. 64.10: this frame's intent goes in once, as an
    // input stamped for the first tick the frame runs (its turn turns on that tick only), and only when it differs
    // from the intent in force; the tick count and limit after each tick
    this.intentSent = false;
    this.tickPresenter ??= {
      ...this.presenter,
      beforeTick: () => { if (!this.intentSent) { this.intentSent = true; this.sendIntent(); } this.o.beforeTick?.(); },
      afterTick: () => {
        this.ticks++;
        if (this.o.tickLimit !== undefined && this.ticks >= this.o.tickLimit && !this.over) this.onEvents([], true);
      },
    };
    const simStart = performance.now(), ticksBefore = this.ticks;
    this.frameTick0 = this.ticks;   // 65.38: each tick's sounds keep its place in the frame
    // 68.2b: F held past a tap opens the order menu on the ground; 68.6 at L0 (on by default): while it is open the match
    // does not advance — no tick passes, no bot acts or deliberates — and an order given is stamped at the paused tick
    if (this.l0FDownAt !== null && !this.radial?.open && !this.commanding && performance.now() - this.l0FDownAt >= L0_HOLD_MS) this.openL0Orders();
    if (this.gDownAt !== null && !this.radial?.open && !this.commanding && performance.now() - this.gDownAt >= L0_HOLD_MS) this.openL0Calls();   // 68.41: G held (layout C)
    if (this.tabDownAt !== null && !this.picker?.open && !this.commanding && performance.now() - this.tabDownAt >= L0_HOLD_MS) this.openPicker();   // 68.3: hold Tab
    if (++this.cardFrame >= CARD_FRAMES) { this.cardFrame = 0; this.refreshCards(); }
    const paused = menuHoldsMatch({ commanding: this.commanding, radial: !!this.radial?.open, picker: !!this.picker?.open });   // 68.6: the ground's menus and the table's
    if (!paused) this.o.match.advance(dt * (this.o.timeScale ?? 1), this.tickPresenter, { maxSteps: MAX_TICKS_PER_FRAME, until: () => this.over });
    this.lastSim = { ms: performance.now() - simStart, ticks: this.ticks - ticksBefore };   // the F3 overlay's SIM line
    // (paused, what moves with the match's time holds still: the units, the falls, the tracers, the sound, the flashes)
    const simDt = paused ? 0 : dt;
    this.updateUnits(simDt);
    this.deaths?.update(simDt, this.o.match.actionTick * PHYS_DT);   // X33: falls settle, corpses age and go
    this.deathCauses.clear();
    this.updateBullets(simDt);
    this.sound.setCommand(this.ears);
    this.sound.frame(simDt, this.human(), id => this.personViews.get(id)?.gait ?? null);   // 67.25: the steps follow the drawn feet
    // 63.5: the soil's wear seen — chewed earth on each worn cell, read from the state (rebuilt when the wear changes)
    if (this.o.match.state.groundWear?.length) (this.wearView ??= new GroundWearView(this.o.scene)).update(this.o.match.state, dt, (pl, d) => this.groundAt(pl, d));
    this.updateShells();
    this.updateMissiles();
    this.updateFlares(simDt);
    this.updateFlashes(simDt);
    this.brass?.update(simDt, this.o.camera.position);   // 65.47
    this.explosions?.update(simDt);   // 67.54
    this.updatePoints();
    // 67.75: each side's motor pool, drawn from the first frame's state (the Lab's *Motor pool* row, or with lighters on; human
    // scale only), its stacks kept to the stock (69.10: frames stacked as the robots bring them)
    const ms = this.o.match.state, lighters = !!ms.config.lighters && personSetOf(ms).k < 1;
    if (this.motorPools === undefined) this.motorPools = (motorPoolShown() || lighters || !!ms.config.motorPool) && personSetOf(ms).k < 1 ? new MotorPools(this.o.scene, ms, this.o.structureGroup) : null;   // (69.14: wherever the pool is simulated, its stacks, arm and robots are drawn — they are its shapes)
    this.motorPools?.update(this.o.camera.position, ms, dt);
    // 69.31: each dump's arm, loading the vehicle the sim rearms there (the Lab's *Dump arm* row, with Ammunition at the dumps; human scale)
    if (this.rearmArms === undefined) this.rearmArms = this.armsDrawn() ? new RearmArms(id => this.o.structureGroup?.(id), id => this.bikeViews.get(id), (pi, d) => this.groundAt(this.o.match.state.planets[pi], d)) : null;
    this.rearmArms?.update(ms, this.o.match.actionTick, simDt, this.o.camera.position);
    // 69.10 step 5: the lighters — the ship's run, its strip, the robots' and the stacker's carry into the pool
    if (lighters) {
      this.explosions ??= new ExplosionField(this.o.scene, PERSON_BODY_HEIGHT * personSetOf(ms).k);
      (this.lighterViews ??= new LighterViews(this.o.scene, ms)).update(ms, this.o.match.actionTick, dt, this.o.camera.position, (pl, d) => this.groundAt(pl, d), this.motorPools, this.explosions);
    }
    this.updateCamera(dt);
    this.updateCommandView();
    this.updateCard();   // 68.41 (layout C)
    this.updateHud();
  }

  // ---------------------------------------------------------------------------
  // 68.4: orders on the table — selection, the radial, *here*, the inputs
  // ---------------------------------------------------------------------------

  /** Where a squad's marker is on screen (the overlay's projection). */
  private markerAt(m: { id: number; planetIndex: number; centre: Vec3 | null }): { x: number; y: number } | null {
    if (!m.centre || !this.commandOverlay) return null;
    if (this.planetTable?.active) return this.planetTable.squadAt.get(m.id) ?? null;   // 68.42: where the planet table drew it (its group's plate)
    const drawn = this.commandOverlay.drawnAt.get(m.id);
    if (drawn) return drawn;   // where its block was drawn (stacked apart from its neighbours)
    return this.commandOverlay.project(this.o.match.state, this.o.camera, m.planetIndex, m.centre, personSetOf(this.o.match.state).eyeHeight);
  }

  /** A click picks the squad under the cursor (shift adds or removes it; empty ground clears); a drag takes every squad inside the box. */
  private finishSelect(x: number, y: number, shift: boolean): void {
    const from = this.dragFrom; this.dragFrom = null;
    const t = this.commandPicks;
    if (!from || !t) return;
    const ids = t.squads.filter(m => m.alive).map(m => m.id);
    if (Math.abs(x - from.x) > 6 || Math.abs(y - from.y) > 6) {
      const x0 = Math.min(x, from.x), x1 = Math.max(x, from.x), y0 = Math.min(y, from.y), y1 = Math.max(y, from.y);
      const inside = t.squads.filter(m => { const p = this.markerAt(m); return m.alive && p && p.x >= x0 && p.x <= x1 && p.y >= y0 && p.y <= y1; }).map(m => m.id);
      this.commandSel = new Set(shift ? [...this.commandSel, ...inside] : inside);
      return;
    }
    // 68.18: a ride waiting for its destination takes this click
    if (this.pendingRide && this.team !== null) {
      const ride = this.pendingRide; this.pendingRide = null;
      const here = this.hereAtCursor();
      if (!here) { this.note('Point at the ground'); return; }
      const r = rideInputs(this.o.match.state, this.team, ride.squads, here, ride.transportId);
      if ('refused' in r) { this.note(r.refused); return; }
      this.issue(r.inputs, 'ride there');
      return;
    }
    const hit = pickSquad(t, m => this.markerAt(m), x, y);
    if (hit === null) {
      if (!shift) this.commandSel = new Set();
      // 68.33: no squad there — a place of ours drawn near the click opens its card (22 px), else none
      let best: RespawnPick | null = null, bd = 22 * 22;
      for (const pl of this.commandOverlay?.placesAt.values() ?? []) { const d = (pl.x - x) ** 2 + (pl.y - y) ** 2; if (d < bd) { bd = d; best = pl.at; } }
      // 166: or a supply truck of ours (its card: cargo and leg; the card reads it by id, as a FOB's)
      for (const [id, p] of this.commandOverlay?.trucksAt ?? []) { const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d < bd) { bd = d; best = { structureId: id }; } }
      this.commandPlace = shift ? this.commandPlace : best;
      return;
    }
    this.commandPlace = null;
    // 68.35: a merged block stands for its squads — a click selects them all (selected, they keep their own blocks: expanded)
    const group = (this.planetTable?.active ? this.planetTable.groupOf.get(hit) : this.commandOverlay?.groupOf.get(hit)) ?? [hit];   // (68.42: the planet table's group)
    if (shift) { const s = new Set(this.commandSel); const all = group.every(id => s.has(id)); for (const id of group) { if (all) s.delete(id); else s.add(id); } this.commandSel = s; }
    else this.commandSel = new Set(group);
    void ids;
  }

  /** 1–9: that squad (the table's order); 0: every squad. */
  private selectByNumber(n: number): void {
    const live = (this.commandPicks?.squads ?? []).filter(m => m.alive);
    this.commandSel = new Set(n === 0 ? live.map(m => m.id) : live[n - 1] ? [live[n - 1].id] : []);
  }

  /**
   * 67.70: the call being aimed now and where — the radial's or layout C's ring's lit Mark ▸ call (at where it opened: the
   * table's cursor, the ground's crosshair), or the card's armed one (at the cursor) — or null.
   */
  private aimedCall(): { sub: CallSub; here: Here | null } | null {
    const lit = this.radial?.open ? (this.radial instanceof CommandRing ? this.radial.current() : this.radial.peek()) : null;
    if (lit && lit.verb === 'mark' && isCallSub(lit.sub)) return { sub: lit.sub, here: this.commanding ? this.radialHere : this.openHere };   // (on the ground: the crosshair's when F or G was held)
    const armed = this.card?.armed;
    if (armed && armed.verb === 'mark' && isCallSub(armed.sub)) return { sub: armed.sub, here: this.hover.here };
    return null;
  }

  /** 67.70: the calls' marks, a frame — the aimed one's shape re-asked when its point moves, the standing ones from the state. */
  private updateCallMarks(): void {
    const state = this.o.match.state, team = this.team;
    if (team === null || (!state.airstrikes && !state.dropships && !state.pods && !state.shipPads)) { if (this.callMarks?.count) this.callMarks.update(null, [], state.planets, 1, this.o.camera, () => ''); return; }
    const aim = this.aimedCall(), tick = this.o.match.actionTick;
    let shape: CallShape | null = null;
    if (aim?.here) {
      const d = aim.here.surfaceDir, key = `${aim.sub}|${aim.here.planetIndex}|${d.x},${d.y},${d.z}`;
      if (!this.callAim || this.callAim.key !== key || this.time - this.callAim.at > CALL_AIM_EVERY) this.callAim = { key, at: this.time, shape: previewCall(state, team, aim.sub, aim.here, tick, this.controlled) };
      shape = this.callAim.shape;
    } else this.callAim = null;
    const standing = standingCalls(state, team, tick);
    const wave = state.shipPads ? waveShipCall(state, team, tick) : null;   // 209 (5): the parked wave ship while it takes riders
    if (wave) standing.push(wave);
    if (!shape && !standing.length && !this.callMarks) return;
    this.callMarks ??= new CallMarksView(this.o.scene, (pl, dir) => this.groundAt(pl, dir));
    const palette = unitPaletteFor(TEAMS[team % TEAMS.length]);
    // (the aimed label under the menu while one is open over its point: the radial's or the ring's outer edge)
    const below = shape && this.radial?.open ? CALL_MARK_LOOK.belowMenu : 0;
    this.callMarks.update(shape, standing, state.planets, PERSON_BODY_HEIGHT * personSetOf(state).k, this.o.camera, s => (s.sub === 'airstrike' ? CALL_MARK_LOOK.strike : palette.flash), below);
  }

  /** *Here* at L1: where the cursor's ray meets the viewed planet (its surface sphere). */
  private hereAtCursor(): Here | null {
    const u = this.viewUnit();
    if (!u) return null;
    if (this.planetTable?.active && this.planetTable.projection === 'map') {   // 68.42: on the flat map, the place under the cursor is the map's
      const m = this.planetTable.hereAt(this.cursor.x, this.cursor.y);
      return m ? { planetIndex: m.planetIndex, surfaceDir: m.dir } : null;
    }
    const state = this.o.match.state;
    const planet = state.planets[u.planetIndex];
    const ndc = new THREE.Vector2(this.cursor.x / window.innerWidth * 2 - 1, -(this.cursor.y / window.innerHeight) * 2 + 1);
    const ray = new THREE.Raycaster(); ray.setFromCamera(ndc, this.o.camera);
    // 68.14: the drawn ground, not the nominal sphere (at the diorama's slant the sphere put *here* people away from the cursor):
    // from where the ray enters a shell over the highest ground, march to the first point under the terrain, then bisect
    const P = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
    const o = ray.ray.origin, dv = ray.ray.direction;
    const toC = P.clone().sub(o), along = toC.dot(dv), miss2 = toC.lengthSq() - along * along;
    const outer = planet.radius * 1.25;   // over the highest ground (the night forest stands 7 % over the nominal radius: a 4 % shell began the march underground and found nothing)
    if (miss2 > outer * outer) return null;
    const half = Math.sqrt(outer * outer - miss2);
    let t0 = Math.max(0, along - half);
    const tEnd = along + half;
    const under = (t: number) => { const p = o.clone().addScaledVector(dv, t); const r = p.distanceTo(P); return r <= this.groundAt(planet, normalize({ x: p.x - P.x, y: p.y - P.y, z: p.z - P.z })); };
    if (under(t0)) return null;
    const step = Math.max(PERSON_BODY_HEIGHT * personSetOf(state).k * 0.5, (tEnd - t0) / 600);
    let t1 = -1;
    for (let t = t0 + step; t <= tEnd; t += step) { if (under(t)) { t1 = t; break; } t0 = t; }
    if (t1 < 0) return null;
    for (let i = 0; i < 14; i++) { const m = (t0 + t1) / 2; if (under(m)) t1 = m; else t0 = m; }
    const hit = o.clone().addScaledVector(dv, t1);
    const here: Here = { planetIndex: u.planetIndex, surfaceDir: normalize({ x: hit.x - P.x, y: hit.y - P.y, z: hit.z - P.z }) };
    // a rock, a tree or a ruin right there: *here* is it (its id, so the order can name it)
    const prop = propNear(state, here, PROP_SNAP_PEOPLE * PERSON_BODY_HEIGHT * personSetOf(state).k);
    return prop ? { planetIndex: u.planetIndex, surfaceDir: prop.surfaceDir, prop: { kind: prop.kind, id: prop.id, label: prop.label } } : here;
  }

  /** The ring to open (68.41: layout C's F or G; else today's one), made once and kept. */
  private menu(which: 'f' | 'g' = 'f'): OrderRadial | CommandRing {
    this.ringKey = which;
    if (!this.layoutC) return (this.rings.today ??= new OrderRadial());
    if (which === 'f') return (this.rings.f ??= new CommandRing(RING_F));
    // G less its wedges hidden whole (69.32 step 2b: Supply with drone drops off), made again when that changes (never while open)
    const g = ringWithout(RING_G, this.poolHidden());
    if (this.rings.g?.wedges !== g && !this.rings.g?.open) { this.rings.g?.dispose(); this.rings.g = new CommandRing(g); }
    return this.rings.g!;
  }

  /** 68.41: the side's calls' counts, for the G ring and the card ("verb/sub" → its badge). */
  private callBadges(): Map<string, string> {
    const state = this.o.match.state, team = this.team, m = new Map<string, string>();
    if (team === null) return m;
    if (state.airstrikes) m.set('mark/airstrike', `×${airstrikesLeft(state, team)}`);
    if (state.dropships) { const n = `×${dropshipsLeft(state, team)}`; m.set('mark/dropship', n); m.set('mark/dropshipVehicle', n); }
    if (state.pods) m.set('mark/podDrop', `×${podsLeft(state, team)}`);
    if (countOf(state.config, 'gunships')) m.set('mark/gunship', `×${gunshipsLeft(state, team)}`);   // 63.13
    if (countOf(state.config, 'helicopters')) m.set('mark/helicopter', `×${helisLeft(state, team)}`);   // 69.59 step 1
    if (state.supplyDrones) { const n = `×${dronesFree(state, team, this.o.match.actionTick)}`; for (const s of SUPPLY_DROP_VERBS) m.set(s, n); }   // 69.32 step 2b: the drones free
    return m;
  }

  /** 63.1n with 67.75: the motor pool's verbs hidden where the side has no pool; 69.10: the lighters' where it has no strip (68.40: the radial is crowded already). */
  private poolHidden(): ReadonlySet<string> | undefined {
    return hiddenVerbs(this.o.match.state, this.team);
  }

  private openRadial(which: 'f' | 'g' = 'f'): void {
    this.radial = this.menu(which);
    this.radial.show(this.cursor.x, this.cursor.y, { hidden: this.poolHidden(), banner: pauseToCommand() ? 'PAUSED · GIVING ORDERS' : undefined, ...(which === 'g' ? { badges: this.callBadges(), footer: "The side's calls" } : {}) });   // 68.6 at the table
    this.radialSince = this.time;
    this.radialHere = this.hereAtCursor();   // 68.14: *here* is where the menu opened, not where the cursor went to pick a wedge
    this.radialAt = { ...this.cursor };
  }

  /** F released: the verb under the cursor, or (a tap, nothing chosen) the contextual order for what lies under it. */
  private releaseRadial(): void {
    if (!this.radial?.open) return;
    const tap = this.time - this.radialSince < 0.22;
    const choice = this.radial.close();
    const team = this.team;
    if (team === null) { this.note('Watching: no orders'); return; }
    const squads = [...this.commandSel];
    const state = this.o.match.state;
    const here = this.radialHere ?? this.hereAtCursor();
    this.radialHere = null;
    if (!choice) {
      if (!tap) return;   // released in the centre: cancelled
      if (!here) { this.note('Point at the ground'); return; }
      const under = underCursor(state, team, this.o.match.actionTick, here, 2 * personSetOf(state).eyeHeight * 3, squads);
      const r = contextualInputs(state, team, squads, here, under);
      if ('refused' in r) { this.note(r.refused); return; }
      this.issue(r.inputs, r.words);
      return;
    }
    // 68.18: Ride with the menu opened on a transport — that one; the next click is where to (Esc cancels)
    if (choice.verb === 'go' && choice.sub === 'ride') {
      const tid = this.transportNear(this.radialAt.x, this.radialAt.y);
      if (tid !== null) {
        if (!squads.length) { this.note('Select a squad first'); return; }
        this.pendingRide = { transportId: tid, squads };
        this.note('Ride: click where to (Esc: cancel)');
        return;
      }
    }
    const verb = VERBS.find(v => v.key === choice.verb)!;
    const r = verbInputs(state, team, squads, choice.verb, choice.sub, here, this.controlled);
    if ('refused' in r) { this.note(r.refused); return; }
    const sub = verb.sub?.find(x => x.key === choice.sub)?.label;
    this.issue(r.inputs, `${verb.glyph} ${sub ?? verb.label}`.toLowerCase() + extraOf(r));
  }

  /** 68.18: the team's transport whose plate was drawn nearest a screen point (within 28 px), or null. */
  private transportNear(x: number, y: number): number | null {
    let best: number | null = null, bd = 28 * 28;
    for (const [id, p] of this.commandOverlay?.transportsAt ?? []) { const d = (p.x - x) ** 2 + (p.y - y) ** 2; if (d < bd) { bd = d; best = id; } }
    return best;
  }

  /** Every input from the team's seat through apply; the note says what went through and why anything was refused. */
  private issue(inputs: MenuInput[], words: string, who?: string): void {
    const team = this.team!;
    let ok = 0; let why = '';
    for (const body of inputs) {
      // 68.23 (decision 153): your orders are tagged yours, so the bot commander leaves those squads to you
      const r = this.o.match.apply(this.o.match.stamp(seatActor(team), body.kind === 'order' || body.kind === 'coordinate' ? { ...body, by: 'human' as const } : body));
      if (r.ok) ok++; else why ||= r.reason ?? 'refused';
    }
    const names = inputs.every(b => b.kind === 'reserves' || b.kind === 'vehicles' || b.kind === 'respawnAt' || b.kind === 'airstrike' || b.kind === 'mortarCall' || b.kind === 'dropship' || b.kind === 'spawnAt' || b.kind === 'podDrop') ? 'the team' : (this.commandPicks?.squads ?? []).filter(m => this.commandSel.has(m.id)).map(m => m.label.split(' ')[0]).join(', ');   // 68.4: the reserve is the team's
    this.note(ok ? `${who ?? (names || 'the team')}: ${words}${why ? ` (some refused: ${why})` : ''}` : `refused: ${why || 'nothing to order'}`);
  }

  private note(text: string, seconds = 3.5): void { this.commandNote = { text, until: this.time + seconds }; }
  /** 69.80: each carry's last beat (its objective's id), told with the next within CARRY_BEAT_MERGE_SECONDS. */
  private lastCarry = new Map<number, { team: number; how: 'taken' | 'dropped' | 'returned'; at: number }>();
  /** 69.80: each escort's last stop by contact (its herringbone), so the march after it is told as moving again */
  private columnHalted = new Set<number>();
  /** 69.80: each hold objective's clock as last seen, so a clock run back to 0 is said (holdBreaks). */
  private holdClocks = new Map<number, number>();

  // ---------------------------------------------------------------------------
  // 68.2b: the order menu on the ground (L0) — the table's radial and verbs (68.4: ui/orderRadial.ts, core/orderMenu.ts)
  // round the crosshair, for your own squad, the match paused while it is open (68.6)
  // ---------------------------------------------------------------------------

  /**
   * *Here* on the ground: where the crosshair rests (placeCrosshair, within the aim's 80 people); past that the same line
   * of sight goes on over the ground to what you can see — the weather's sight, at most HERE_REACH people — so a far ridge
   * is a place to send a squad though no shot reaches it. Past that, none. Asked for only when an order is given.
   */
  private hereNow(): Here | null {
    if (this.here) return this.here;
    const u = this.human();
    if (!u || u.mount) return null;
    const state = this.o.match.state, ps = personSetOf(state), planet = state.planets[u.planetIndex], person = PERSON_BODY_HEIGHT * ps.k;
    const muzzle = muzzlePoint(u, state.planets, ps.eyeHeight, ps), ray = aimDirection(normalize(u.surfaceDir), headingOf(u), this.pitch);
    const reach = Math.min(HERE_REACH * person, weatherEffectHere(state, u.planetIndex, u.surfaceDir, this.o.match.actionTick * PHYS_DT).sight * ps.k);
    for (let d = 80 * person; d <= reach; d += 2 * person) {
      const p = add(muzzle, scale(ray, d));
      const dir = normalize(sub(p, planet.position));
      if (dist(p, planet.position) <= this.groundAt(planet, dir)) return { planetIndex: u.planetIndex, surfaceDir: dir };
    }
    return null;
  }

  /** The contextual order on the ground: with a *here*, the table's (an enemy the team knows: engage; a point: take; a free gun: crew; ground: go here); without, Follow me. */
  private l0Contextual(squadIds: number[], here: Here | null): { inputs: MenuInput[]; words: string; icon: IconId } | { refused: string } {
    const state = this.o.match.state, team = this.team!, me = this.controlled;
    // 63.1i: in a seat (no *here* from one) with the squad aboard something — everyone out
    if (!here && this.human()?.mount) {
      const out = dismountInputs(state, team, squadIds, null);
      if (!('refused' in out)) return { inputs: out.inputs, words: 'everyone out', icon: 'dismount' };
    }
    if (!here) {
      const r = verbInputs(state, team, squadIds, 'go', 'follow', null, me);   // 69.2d: Follow lives on Go's arc; the no-place default stays one click
      return 'refused' in r ? r : { inputs: r.inputs, words: 'follow me', icon: 'follow' };
    }
    const under = underCursor(state, team, this.o.match.actionTick, here, 2 * personSetOf(state).eyeHeight * 3, squadIds);
    const r = contextualInputs(state, team, squadIds, here, under);
    if ('refused' in r) return r;
    return { ...r, icon: under.kind === 'enemy' ? 'engage' : under.kind === 'point' ? 'take' : under.kind === 'gun' ? 'crew' : under.kind === 'dismount' ? 'dismount' : 'go' };
  }

  /** 68.3: the squads the menu orders — the ones the number keys picked (still standing), else the squad of the unit you hold. */
  private l0Squads(): number[] {
    const me = this.human(), state = this.o.match.state;
    const live = (id: number) => state.squads?.some(s => s.id === id && s.team === this.team && s.unitIds.some(u => state.players.some(p => p.id === u && p.alive)));
    const picked = this.l0Sel?.filter(live) ?? [];
    if (picked.length) return picked;
    const own = me ? squadOf(state, me.id) : null;
    return own ? [own.id] : [];
  }

  /** Whom an order goes to, in words (the strip's numbers). */
  private l0Who(ids: readonly number[]): string {
    const own = this.cards.find(c => c.own);
    if (ids.length === 1 && own && ids[0] === own.id) return 'your squad';
    if (ids.length > 1 && ids.length === this.cards.length) return 'all squads';
    const named = ids.map(id => this.cards.find(c => c.id === id)).filter(Boolean).map(c => `${c!.number} (${c!.label})`);
    return named.length ? `squad${named.length > 1 ? 's' : ''} ${named.join(', ')}` : 'your squad';
  }

  /** 68.3: the strip's cards — what you know of your squads now (core/squadCards.ts), the reports kept between calls. */
  private refreshCards(): void {
    this.leaders = squadLeaders(this.o.match.state);
    if (this.team === null) { this.cards = []; return; }
    const state = this.o.match.state, me = this.human() ?? null;
    const ps = personSetOf(state);
    const eye = me ? personEyePoint(me, state.planets, ps, ps.losEyeHeight) : null;
    const sight = me ? weatherEffectHere(state, me.planetIndex, me.surfaceDir, this.o.match.actionTick * PHYS_DT).sight * ps.k : 0;   // (62.54: the weather where you stand)
    // you see a teammate as a bot sees an enemy: in range, a clear line from your eye to theirs
    const sees = (u: PlayerState): boolean => {
      if (!eye || !me || u.planetIndex !== me.planetIndex) return false;
      const ee = personEyePoint(u, state.planets, ps, ps.losEyeHeight);
      return dist(ee, eye) <= sight && personLineOfSight(eye, ee, state);
    };
    const r = squadCards(state, this.team, this.commanding ? null : me, this.o.match.actionTick, this.cardReports, sees, PHYS_DT);
    this.cards = r.cards; this.cardReports = r.reports;
  }

  /** 68.3b: the strip's lines from the cards — the status as X23's marker glyph, the order as lane E's icon, the selection ringed. */
  private stripLines(): StripLine[] {
    const sel = new Set(this.l0Squads());
    const marker = (s: SquadCard['status']): StripLine['marker'] =>
      s === 'in contact' ? 'contact' : s === 'hurt' || s === 'down' ? 'hurt' : s === 'crewing' ? 'crewing' : s === 'scouting' ? 'scouting'
        : s === 'withdrawing' ? 'withdrawing' : s === 'holding' || s === 'free' || s === null ? 'holding' : 'moving';
    const icon = (k: string | null): StripLine['orderIcon'] =>
      k === 'move' ? 'go' : k === 'hold' ? 'hold' : k === 'escort' ? 'follow' : k === 'take' ? 'take' : k === 'engage' ? 'engage' : k === 'crew' ? 'crew'
        : k === 'bound' || k === 'flank' ? 'tactics' : k === 'withdraw' ? 'withdraw' : k === 'mark' ? 'mark' : k === 'formation' ? 'formation'
        : k === 'callIn' || k === 'fireMission' ? 'callFire' : null;
    const state = this.o.match.state;
    return this.cards.map(c => {
      const ids = state.squads?.find(s => s.id === c.id)?.unitIds ?? [];
      return {
        number: c.number, label: c.label, own: c.own, selected: sel.has(c.id), spent: c.spent,
        members: c.members.map((m, i) => ({ alive: m.alive, health: m.health, leader: ids[i] !== undefined && ids[i] === c.leader })),
        marker: marker(c.status), orderIcon: icon(c.orderKind), order: c.order, freshness: c.freshness, age: c.age,
      };
    });
  }

  /** 1–4 (and on), 0: the squads the menu orders now, said under the crosshair. */
  private selectSquads(n: number): void {
    this.refreshCards();
    const ids = squadsForKey(this.cards, n);
    if (!ids.length) { this.note(n === 0 ? 'No squads to select' : `No squad ${n}`); return; }
    this.l0Sel = ids;
    this.note(`Ordering ${this.l0Who(ids)}`);
  }

  private openL0Orders(): void {
    const me = this.human();
    const state = this.o.match.state;
    this.refreshCards();
    this.openHere = this.hereNow();   // (the aim stands still while the menu is open)
    this.l0Vec = { x: 0, y: 0 };
    this.mouseDown = false;
    // 68.5: on a teammate, its own menu (what needs no place: follow me, fire / hold fire, withdraw; the middle picks it for
    // the next order); with a member picked, the menu orders that member at *here*
    const member = this.fOnMate ?? this.pickedMember();
    this.menuFor = member;
    if (member !== null) {
      const onIt = this.fOnMate !== null;
      const ctx = !onIt && this.team !== null ? this.memberContextual(member, this.openHere) : null;
      const off = new Set(['formation', 'tactics', ...(onIt || !this.openHere ? ['go', 'hold', 'mark'] : [])]);
      this.radial = this.menu('f');
      this.radial.show(window.innerWidth / 2, window.innerHeight / 2, {
        disabled: off, hidden: this.poolHidden(),
        banner: pauseToCommand() ? 'PAUSED · GIVING ORDERS' : undefined,
        footer: `Ordering ${this.unitName(member)} alone · ${DIRECT_ORDER_SECONDS} s, or till it is done`,
        hub: onIt ? { icon: null, label: 'pick for the next order' } : ctx && !('refused' in ctx) ? { icon: ctx.icon, label: ctx.words } : { icon: null, label: 'cancel' },
      });
      return;
    }
    const ids = this.l0Squads();
    const ctx = ids.length && this.team !== null ? this.l0Contextual(ids, this.openHere) : null;
    const own = me ? squadOf(state, me.id) : null;
    const others = own ? own.unitIds.filter(id => id !== me!.id && state.players.some(p => p.id === id && p.alive)).length : 0;
    const who = this.l0Who(ids);
    this.radial = this.menu('f');
    this.radial.show(window.innerWidth / 2, window.innerHeight / 2, {
      disabled: this.openHere ? undefined : new Set(VERBS.filter(v => ['go', 'hold', 'tactics', 'mark'].includes(v.key)).map(v => v.key)), hidden: this.poolHidden(),
      banner: pauseToCommand() ? 'PAUSED · GIVING ORDERS' : undefined,
      footer: !ids.length ? 'No squad to order' : who === 'your squad' ? `Your squad · ${others} with you` : `Ordering ${who}`,
      hub: ctx && !('refused' in ctx) ? { icon: ctx.icon, label: ctx.words } : { icon: null, label: 'cancel' },
    });
  }

  /** F released on the ground: what the menu points at — or a tap (it never opened) or the middle: the contextual order. */
  private releaseL0Orders(): void {
    const open = !!this.radial?.open;
    const choice = open ? this.radial!.close() : null;
    this.l0FDownAt = null;
    const me = this.human(), team = this.team;
    const state = this.o.match.state;
    // 68.5: one member's direct order — a tap (or the middle of its menu) on a teammate picks it; then the next order is its
    const onMate = this.fOnMate; this.fOnMate = null;
    const member = open ? this.menuFor : onMate ?? this.pickedMember();
    this.menuFor = null;
    if (member !== null && me && team !== null) {
      if (!choice && (onMate !== null || (open && member === onMate))) {
        if (this.memberPick?.id === member && !open) { this.memberPick = null; this.note('Ordering your squad again'); return; }
        this.memberPick = { id: member, until: this.time + MEMBER_PICK_SECONDS };
        this.note(`${this.unitName(member)}: the next order is theirs — aim, then F (Esc: your squad)`);
        return;
      }
      const here = open ? this.openHere : this.hereNow();
      let r: { inputs: MenuInput[]; words: string } | { refused: string };
      if (!choice) {
        r = this.memberContextual(member, here);   // a tap, or the middle: the contextual order (as the squad's menu)
      } else if (choice.verb === 'fire') {
        const inputs = memberFireToggle(state, member, choice.sub);
        r = inputs.length ? { inputs, words: choice.sub === 'holdFire' || (choice.sub === null && !state.players.find(p => p.id === member)?.holdFire) ? 'hold fire' : 'fire at will' } : { refused: 'It is so already' };
      } else {
        const sq = squadOf(state, member);
        const verb = VERBS.find(v => v.key === choice.verb)!;
        const base = sq ? verbInputs(state, team, [sq.id], choice.verb, choice.sub, here, me.id) : { refused: 'In no squad' };
        const one = 'refused' in base ? base : toMember(base.inputs, member, this.o.match.actionTick, PHYS_DT);
        r = 'refused' in one ? one : { inputs: one.inputs, words: (verb.sub?.find(x => x.key === choice.sub)?.label ?? verb.label).toLowerCase() };
      }
      if ('refused' in r) { this.note(r.refused); return; }
      this.memberPick = null;
      this.issue(r.inputs, r.words, this.unitName(member));
      return;
    }
    const ids = this.l0Squads();
    if (!me || team === null || !ids.length) { this.note('No squad to order'); return; }
    const here = open ? this.openHere : this.hereNow();
    const who = this.l0Who(ids);
    if (!choice) {
      const r = this.l0Contextual(ids, here);
      if ('refused' in r) { this.note(r.refused); return; }
      this.issue(r.inputs, r.words, who);
      return;
    }
    const verb = VERBS.find(v => v.key === choice.verb)!;
    const r = verbInputs(state, team, ids, choice.verb, choice.sub, here, me.id);
    if ('refused' in r) { this.note(r.refused); return; }
    this.issue(r.inputs, (verb.sub?.find(x => x.key === choice.sub)?.label ?? verb.label).toLowerCase() + extraOf(r), r.inputs.every(b => b.kind === 'airstrike' || b.kind === 'dropship') ? undefined : who);
  }

  /** 68.41, layout C: G held on the ground — the side's calls round the crosshair (its hub marks here), the match paused as F's. */
  private openL0Calls(): void {
    this.openHere = this.hereNow();
    this.l0Vec = { x: 0, y: 0 };
    this.mouseDown = false;
    this.radial = this.menu('g');
    const needHere = this.openHere ? undefined : new Set(['support', 'reinforce', 'mark']);
    this.radial.show(window.innerWidth / 2, window.innerHeight / 2, {
      disabled: needHere, badges: this.callBadges(),
      banner: pauseToCommand() ? 'PAUSED · GIVING ORDERS' : undefined,
      footer: "The side's calls",
      hub: this.openHere ? { icon: 'mark', label: 'Mark here' } : { icon: null, label: 'cancel' },
    });
  }

  /** 68.41: G released on the ground with the calls open — what they point at, or (the hub) a mark here. */
  private releaseL0Calls(): void {
    this.gDownAt = null;
    if (!this.radial?.open) return;
    const choice = this.radial.close() ?? (this.openHere ? { verb: 'mark', sub: 'mark' } : null);
    const me = this.human(), team = this.team;
    if (!choice || !me || team === null) return;
    const verb = VERBS.find(v => v.key === choice.verb)!;
    const r = verbInputs(this.o.match.state, team, this.l0Squads(), choice.verb, choice.sub, this.openHere, me.id);
    if ('refused' in r) { this.note(r.refused); return; }
    this.issue(r.inputs, (verb.sub?.find(x => x.key === choice.sub)?.label ?? verb.label).toLowerCase() + extraOf(r));
  }

  /** 68.41: the ring released by a number key or a click (layout C) — given as its key's release would be. */
  private ringPicked(): void {
    if (this.commanding) { this.releaseRadial(); return; }
    if (this.ringKey === 'g') this.releaseL0Calls(); else this.releaseL0Orders();
  }

  /** 68.41: an open ring cancelled (Esc, the right button): nothing given. */
  private ringCancel(): void {
    this.radial?.close();
    this.radialHere = null;
    this.l0FDownAt = null; this.gDownAt = null; this.fOnMate = null; this.menuFor = null;
  }

  /** 68.41, the card at the table: a button pressed — a page, an entry given now (no place), or one armed for a click. */
  private cardAction(a: CardAction): void {
    if (!a || a.kind === 'page' || a.kind === 'arm') return;
    this.giveAtTable(a.entry, null);
  }

  /** 68.41: an entry given from the table (the card's, at a place or none), through today's verbs. */
  private giveAtTable(x: { verb: string; sub: string | null }, here: Here | null): boolean {
    const team = this.team;
    if (team === null) { this.note('Watching: no orders'); return false; }
    const verb = VERBS.find(v => v.key === x.verb)!;
    const r = verbInputs(this.o.match.state, team, [...this.commandSel], x.verb, x.sub, here, this.controlled);
    if ('refused' in r) { this.note(r.refused); return false; }
    const sub = verb.sub?.find(y => y.key === x.sub)?.label;
    this.issue(r.inputs, `${verb.glyph} ${sub ?? verb.label}`.toLowerCase() + extraOf(r));
    return true;
  }

  /** 68.41: the card this frame — shown at the table in layout C, its entries following the selection. */
  private updateCard(): void {
    if (!this.layoutC) return;
    const on = this.commanding && this.team !== null && this.commandBlend > 0.5;
    if (!on) { this.card?.show(false); return; }
    if (!this.card) { this.card = new CommandCard(); this.card.onAction = a => this.cardAction(a); }
    const names = (this.commandPicks?.squads ?? []).filter(m => this.commandSel.has(m.id)).map(m => m.label.split(' ')[0]);
    if (this.commandSel.size) this.card.set(ringWithout(RING_F, this.poolHidden()), `Orders · ${names.join(', ') || 'squads'}`);
    else this.card.set(ringWithout(RING_G, this.poolHidden()), "The side's calls · select squads for their orders", this.callBadges());   // (63.1n with 67.75: the pool's verbs only with a pool)
    this.card.show(true);
    this.card.cursor(this.cursor);
  }

  /** 68.5: the teammate the crosshair is on — its body within half a person of the aim line, the nearest such, in sight. */
  private teammateUnderCrosshair(): PlayerState | null {
    const u = this.human();
    if (!u || u.mount) return null;
    const state = this.o.match.state, ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const muzzle = muzzlePoint(u, state.planets, ps.eyeHeight, ps), ray = aimDirection(normalize(u.surfaceDir), headingOf(u), this.pitch);
    const eye = personEyePoint(u, state.planets, ps, ps.losEyeHeight);
    let best: { p: PlayerState; along: number } | null = null;
    for (const p of this.teammates()) {
      if (!p.alive || p.id === u.id || p.planetIndex !== u.planetIndex || p.mount) continue;
      const v = sub(hitboxCenter(p, state.planets, ps.hitboxCenter, ps), muzzle);
      const along = v.x * ray.x + v.y * ray.y + v.z * ray.z;
      if (along <= 0 || along > HERE_REACH * person || (best && along >= best.along)) continue;
      if (len(sub(v, scale(ray, along))) > 0.5 * person) continue;
      if (!personLineOfSight(eye, personEyePoint(p, state.planets, ps, ps.losEyeHeight), state)) continue;
      best = { p, along };
    }
    return best?.p ?? null;
  }

  /** 68.5: the member picked for the next order, while it stands and the pick lasts. */
  private pickedMember(): number | null {
    const m = this.memberPick;
    if (!m) return null;
    if (this.time > m.until || !this.o.match.state.players.some(p => p.id === m.id && p.alive)) { this.memberPick = null; return null; }
    return m.id;
  }

  /** 68.5: the contextual order (engage that, crew it, take it, go there — "take that cover") for one member. */
  private memberContextual(member: number, here: Here | null): { inputs: MenuInput[]; words: string; icon: IconId } | { refused: string } {
    const sq = squadOf(this.o.match.state, member);
    if (!sq) return { refused: 'In no squad' };
    const r = this.l0Contextual([sq.id], here);
    if ('refused' in r) return r;
    const one = toMember(r.inputs, member, this.o.match.actionTick, PHYS_DT);
    return 'refused' in one ? one : { inputs: one.inputs, words: r.words, icon: r.icon };
  }

  /** A unit by its own name (the team's is yours anyway: 'Sidereal Conservancy Bravo' reads 'Bravo'). */
  private unitName(id: number): string {
    const p = this.o.match.state.players.find(q => q.id === id);
    if (!p) return `unit ${id}`;
    const team = TEAMS[p.teamIndex]?.name;
    return team && p.name.startsWith(`${team} `) ? p.name.slice(team.length + 1) : p.name;
  }

  // 68.3: hold Tab — the picker: your living teammates round the crosshair (the order radial's ring, their names);
  // point at one and release to take it (Esc: nobody); a tap takes the next teammate, as Tab always has
  private pickerUnits: PlayerState[] = [];

  private openPicker(): void {
    const me = this.controlled;
    const state = this.o.match.state;
    const mates = this.teammates().filter(p => p.alive && p.id !== me);
    // at most twelve, the nearest first then in id order round the ring (the ring reads clockwise from the top)
    const here = this.human();
    const near = here ? [...mates].sort((a, b) => dist(a.surfaceDir, here.surfaceDir) - dist(b.surfaceDir, here.surfaceDir)).slice(0, 12) : mates.slice(0, 12);
    this.pickerUnits = near.sort((a, b) => a.id - b.id);
    if (!this.pickerUnits.length) { this.tabDownAt = null; this.note('Nobody else to take'); return; }
    this.refreshCards();
    const cardOf = (p: PlayerState) => this.cards.find(c => state.squads?.find(s => s.id === c.id)?.unitIds.includes(p.id));
    this.picker?.dispose();
    // (a unit by its own name — the team's is yours anyway: 'Sidereal Conservancy Bravo' reads 'Bravo')
    const own = (p: PlayerState) => { const team = TEAMS[p.teamIndex]?.name; return team && p.name.startsWith(`${team} `) ? p.name.slice(team.length + 1) : p.name; };
    this.picker = new OrderRadial(this.pickerUnits.map(p => ({ key: String(p.id), label: own(p), glyph: `${cardOf(p)?.number ?? '·'}` })));
    this.l0Vec = { x: 0, y: 0 };
    this.mouseDown = false;
    this.picker.show(window.innerWidth / 2, window.innerHeight / 2, {
      banner: pauseToCommand() ? 'PAUSED · TAKE A UNIT' : undefined,
      footer: 'Take a unit · its squad number above its name', hub: { icon: null, label: 'cancel' },
    });
  }

  private releasePicker(): void {
    const open = !!this.picker?.open;
    const choice = open ? this.picker!.close() : null;
    this.tabDownAt = null;
    if (!open) { this.take(this.nextTeammate(1)); return; }   // a tap: the next teammate
    if (choice) this.take(Number(choice.verb), true);
  }


  /** Enter or leave the commander's view (no keys, no mouse look, no pointer lock while in it). */
  private setCommanding(on: boolean): void {
    this.commanding = on;
    this.keys.clear(); this.mouseDown = false; this.jumpQueued = false;
    this.panKeys.clear();
    if (on) {
      this.softCapture = false; if (document.pointerLockElement) document.exitPointerLock();
      if (this.commandBlend < 0.05) {
        this.cmdFacing = null; this.cmdCentre = null; this.cmdPan = null;
        // 68.34 (the owner: "commander view should start over the guy you're controlling if you're controlling a guy"): a fresh
        // table opens over the unit you hold, as if panned there — a pan moves on from it, C / Space goes back to the team
        const held = this.human();
        if (held?.alive) { this.cmdPan = normalize(held.surfaceDir); this.cmdCentre = this.cmdPan; }
      }
    }
    else { this.commandSel = new Set(); this.radial?.close(); this.radialHere = null; this.dragFrom = null; }   // 68.14: the stop is kept — the camera leaves from it, and O returns to it
  }

  /** The overview: above the centre of the team and the points on the viewed planet, high enough to see their spread, tilted toward where the view faced. */
  /**
   * 68.13: the commander's view at its current stop — the overview (stop 0) eased into the sheet's squad stop (1) and
   * the diorama (2), placed by lane E's `placeCamera` round the team's centre on its ground, facing as the overview does.
   */
  private commandPose(u: PlayerState): { pos: Vec3; look: Vec3; up: Vec3; fov: number; tilt: number } {
    // 68.14: one ladder by distance (game/commandCamera.ts), every stop placed by lane E's placeCamera round the frame
    const ov = this.overviewPose(u);
    const state = this.o.match.state;
    const planet = state.planets[u.planetIndex];
    const c = normalize(sub(ov.look, planet.position));
    const at = ladderAt(this.ladder(u, ov), this.commandStop);
    const look = { distance: at.distance, pitch: at.pitch, rise: 0, side: 0, fov: at.fov, focus: at.focus, tilt: at.tilt, hud: {}, stop: at.name, rung: at.name === 'orbit' ? 'theater' as const : 'command' as const };
    placeCamera(this.stopCam, look, new THREE.Vector3(ov.look.x, ov.look.y, ov.look.z), new THREE.Vector3(c.x, c.y, c.z), new THREE.Vector3(ov.up.x, ov.up.y, ov.up.z), new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z));
    const dir = new THREE.Vector3(); this.stopCam.getWorldDirection(dir);
    const tgt = this.stopCam.position.clone().addScaledVector(dir, at.distance * (1 - at.focus) + this.stopCam.position.distanceTo(new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z)) * at.focus);
    return { pos: { x: this.stopCam.position.x, y: this.stopCam.position.y, z: this.stopCam.position.z }, look: { x: tgt.x, y: tgt.y, z: tgt.z }, up: { x: this.stopCam.up.x, y: this.stopCam.up.y, z: this.stopCam.up.z }, fov: at.fov, tilt: at.tilt };
  }

  /** 68.14: the commander's ladder for this frame (the overview's distance fits the team's spread). */
  private ladder(u: PlayerState, ov = this.overviewPose(u)): LadderStop[] {
    const state = this.o.match.state, planet = state.planets[u.planetIndex];
    return commandLadder({ person: PERSON_BODY_HEIGHT * personSetOf(state).k, radius: planet.radius, fit: len(sub(ov.pos, ov.look)), baseFov: this.baseFov || 55 });
  }

  /**
   * 68.14: glide the frame's centre to what the view is about — where you panned; else at the squad stop and closer the
   * selected squads; else the team and the points — and carry the latched facing along; WASD / arrows pan at the
   * stop's own scale, Q / E turn.
   */
  private updateCommandFrame(u: PlayerState, dt: number): void {
    const state = this.o.match.state;
    const planet = state.planets[u.planetIndex];
    const team = this.team ?? u.teamIndex;
    const all = [
      ...state.players.filter(p => p.alive && p.teamIndex === team && p.planetIndex === u.planetIndex).map(p => p.surfaceDir),
      ...(state.modeState.points?.sites ?? []).filter(s => s.planetIndex === u.planetIndex).map(s => s.surfaceDir),
    ];
    const sel = this.commandSel.size && this.commandStopTarget <= LADDER_DIORAMA
      ? (this.commandPicks?.squads ?? []).filter(s => this.commandSel.has(s.id) && s.centre && s.planetIndex === u.planetIndex).map(s => s.centre!) : [];
    const want = this.cmdPan ?? centreOf(sel) ?? centreOf(all) ?? normalize(u.surfaceDir);
    this.cmdCentre = this.cmdCentre ? glide(this.cmdCentre, want, frameLerp(0.08, dt)) : want;
    const c = this.cmdCentre;
    this.cmdFacing = carry(this.cmdFacing ?? headingOf({ surfaceDir: normalize(u.surfaceDir), heading: u.heading }), c);
    if (!this.commanding || !this.panKeys.size) return;
    const k = this.panKeys;
    const fwd = (k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0);
    const right = (k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0);
    const spin = (k.has('e') ? 1 : 0) - (k.has('q') ? 1 : 0);
    if (spin) this.cmdFacing = turn(this.cmdFacing, c, spin * 1.2 * dt);
    if (fwd || right) {
      // the stop's own scale: the overview's height, the sheet's distances (people), the orbit's (radii)
      const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
      const span = this.commandSpan(u);
      const from = this.cmdPan ?? c;
      this.cmdPan = pan(from, this.cmdFacing, fwd * span * 0.7 * dt, right * span * 0.7 * dt, planet.radius);
      this.cmdCentre = glide(c, this.cmdPan, 0.5);
    }
  }

  /** 68.14: how far the view stands from its ground at the current stop (the overview's height, the stops' capped distances, the orbit's) — pan speeds scale by it. */
  private commandSpan(u: PlayerState): number {
    return ladderAt(this.ladder(u), this.commandStop).distance;
  }

  /** 68.14: drag the ground with the right (or middle) button: the point under the cursor follows it. */
  private dragPan(dx: number, dy: number): void {
    const u = this.viewUnit();
    if (!u || !this.cmdCentre || !this.cmdFacing) return;
    const planet = this.o.match.state.planets[u.planetIndex];
    const k = this.commandSpan(u) * 1.1 / window.innerHeight;   // ground per pixel near the centre (the stops' fields of view, roughly)
    this.cmdPan = pan(this.cmdPan ?? this.cmdCentre, this.cmdFacing, dy * k, -dx * k, planet.radius);
    this.cmdCentre = glide(this.cmdCentre, this.cmdPan, 0.6);
  }

  private overviewPose(u: PlayerState): { pos: Vec3; look: Vec3; up: Vec3 } {
    const state = this.o.match.state;
    const planet = state.planets[u.planetIndex];
    const team = this.team ?? u.teamIndex;
    const dirs = [
      ...state.players.filter(p => p.alive && p.teamIndex === team && p.planetIndex === u.planetIndex).map(p => p.surfaceDir),
      ...(state.modeState.points?.sites ?? []).filter(s => s.planetIndex === u.planetIndex).map(s => s.surfaceDir),
    ];
    if (!dirs.length) dirs.push(u.surfaceDir);
    const c = this.cmdCentre ?? normalize(dirs.reduce((a, d) => add(a, d), { x: 0, y: 0, z: 0 }));
    let extent = 0;
    for (const d of dirs) extent = Math.max(extent, Math.acos(Math.max(-1, Math.min(1, d.x * c.x + d.y * c.y + d.z * c.z))) * planet.radius);
    const ps = personSetOf(state);
    const H = Math.max(40 * ps.eyeHeight, extent * 2.4);   // the spread fits the view's half-angle with a margin
    const f = this.cmdFacing ?? carry(headingOf({ surfaceDir: normalize(u.surfaceDir), heading: u.heading }), c);
    const ground = add(planet.position, scale(c, this.groundAt(planet, c)));   // 68.14: the drawn ground, not the nominal sphere
    return { pos: add(add(ground, scale(c, H)), scale(f, -0.5 * H)), look: ground, up: f };
  }

  /** Draw the commander's table over the overview (fading with the camera move). */
  private updateCommandView(): void {
    const u = this.viewUnit();
    const state = this.o.match.state;
    const rec = this.o.match.record;
    if (this.commandBlend < 0.05) {
      this.commandPicks = null;
      this.radarReach?.hide();
      if (this.friendlies && u) {
        this.commandOverlay ??= new CommandOverlay();
        const ps = personSetOf(state);
        this.commandOverlay.drawFriendlies(state, this.o.camera, commandTable(state, this.team ?? u.teamIndex, this.o.match.actionTick, rec, PHYS_DT), this.controlled, ps.eyeHeight, ps.eyeHeight * 1.5);   // above the heads
      } else this.commandOverlay?.hide();
      return;
    }
    if (!u) return;
    const team = this.team ?? u.teamIndex;
    this.commandOverlay ??= new CommandOverlay();
    this.commandPicks = commandTable(state, team, this.o.match.actionTick, rec, PHYS_DT);
    // 68.43: the radars the side may draw (its own, the enemy's it has seen), sampled within RADAR_MS a frame until done: the
    // reach's line in planet space (built once a grid), the detail on the overlay when asked
    const rt0 = performance.now();
    const radars = this.radarCover.update(state, team, RADAR_MS, this.radarDetail);
    if (radars.reach.length) this.radarReach ??= new RadarReachView(this.o.scene);
    this.radarReach?.update(state, radars.reach, Math.min(1, (this.commandBlend - 0.05) / 0.6), null);
    this.commandOverlay.radarCostMs = performance.now() - rt0;   // (the frame budget's readout, 136: sampling while it lasts, then the lines' upkeep)
    this.commandOverlay.radar = radars.detail;
    this.commandOverlay.radarHeads = radars.reach;
    const drag = this.dragFrom && (Math.abs(this.cursor.x - this.dragFrom.x) > 6 || Math.abs(this.cursor.y - this.dragFrom.y) > 6) ? { x0: this.dragFrom.x, y0: this.dragFrom.y, x1: this.cursor.x, y1: this.cursor.y } : null;
    const note = this.commandNote && this.commandNote.until > this.time ? this.commandNote.text : '';
    // 68.14: the ring where the next order lands (re-picked only when the cursor or the camera moved)
    const cam = this.o.camera.position;
    const key = `${this.cursor.x},${this.cursor.y},${cam.x.toFixed(3)},${cam.y.toFixed(3)},${cam.z.toFixed(3)}`;
    if (this.hover.at !== key) this.hover = { at: key, here: this.radial?.open ? this.radialHere : this.hereAtCursor() };
    const hv = this.radial?.open ? this.radialHere : this.hover.here;
    const hover = hv && this.commandBlend > 0.95 ? { planetIndex: hv.planetIndex, dir: hv.surfaceDir, label: this.pendingRide ? 'ride here' : hv.prop?.label ?? '' } : null;
    this.commandOverlay.onRespawnHere ??= at => { this.issue([{ kind: 'respawnAt', at }], 'respawn here'); };   // 68.33: the card's button
    // 68.42: at the overview and the orbit the planet table draws the world (globe or flat map); the overlay keeps its card
    const alpha = Math.min(1, (this.commandBlend - 0.05) / 0.6);
    const far = this.planetTableStart !== 'off' && this.commandBlend > 0.5 && this.commandStop >= LADDER_ENTRY - 0.5;
    if (far) {
      this.planetTable ??= new PlanetTableView(this.o.scene, this.planetTableStart === 'map' ? 'map' : 'globe', () => { this.hover = { at: '', here: null }; });
      this.planetTable.update({ state, team, tick: this.o.match.actionTick, picks: this.commandPicks, camera: this.o.camera, zoom: this.commandStop >= LADDER_ENTRY + 0.5 ? 'planet' : 'theatre', centre: this.cmdCentre, alpha, selected: this.commandSel, now: this.time });
    } else this.planetTable?.hide();
    const onMap = !!this.planetTable?.active && this.planetTable.projection === 'map';   // (the overlay's rings project through the 3D camera: none over the flat map)
    this.commandOverlay.radarNote = this.radarDetail ? radarNote(state, radars.detail.length, !!this.planetTable?.active) : null;   // 68.43: with the detail asked for, why nothing is drawn
    this.commandOverlay.draw(state, this.o.camera, this.commandPicks, this.commandSel, alpha, personSetOf(state).eyeHeight, drag, note, onMap ? null : hover, onMap ? null : this.controlled, this.commandPlace, !!this.planetTable?.active);   // 68.28: the unit you hold; 68.33: a place's card
  }

  // ---------------------------------------------------------------------------
  // 59.6 capture points, 67.69's look (rendering/objectiveMarkers.ts): a ring on the ground (owner's colour), a fill for
  // the capture under way (capturer's colour), a soft column of light with the point's letter at its top — read from
  // state each frame, as the viewer's side knows the points (69.2a: a point known only roughly stands at its circle)
  // ---------------------------------------------------------------------------

  private markers: ObjectiveMarkers | null = null;
  /** 67.75: the sides' motor pools (undefined until the first frame decides; null when not drawn). */
  private motorPools: MotorPools | null | undefined = undefined;
  /** 69.31: the dumps' arms (null: not drawn; undefined: not yet asked) */
  private rearmArms: RearmArms | null | undefined = undefined;
  /** 69.31: the dumps' arms drawn, and the tanks built with their ammunition doors */
  private armsDrawn(): boolean { const ms = this.o.match.state; return rearmArmShown() && !!ms.config.munitionsSupply && personSetOf(ms).k < 1; }
  /** 69.10 step 5: the lighters' look (made at the first frame with lighters on). */
  private lighterViews: LighterViews | null = null;
  private markerGround = new Map<number, { dir: Vec3; planet: PlanetState; ground: THREE.Vector3; up: THREE.Vector3 }>();
  /** 69.75: the drawn ground the rings drape over, one a planet state (a crater's new state, a new one: the rings re-laid). */
  private markerDrapes = new WeakMap<PlanetState, MarkerGround>();
  private markerDrape(planet: PlanetState): MarkerGround {
    let d = this.markerDrapes.get(planet);
    if (!d) { d = { centre: planet.position, radiusAt: dir => this.groundAt(planet, dir), planet }; this.markerDrapes.set(planet, d); }
    return d;
  }

  private updatePoints(): void {
    const state = this.o.match.state;
    const all = state.modeState.points?.sites;
    if (!all || all.length === 0) return;
    const radius = state.config.modeConfig.captureRadius ?? personSetOf(state).captureRadius;
    const ps = personSetOf(state), tick = this.o.match.actionTick;
    this.markers ??= new ObjectiveMarkers(this.o.scene, PERSON_BODY_HEIGHT * ps.k, radius);
    const team = this.team;
    const known = team === null ? all : sitesAsKnown(state, team, all, tick);
    const exact = team === null || !state.config.roughStart ? null : new Map(knownObjectives(state, team, tick).map(k => [k.siteId, k.resolved] as const));
    const active = new Set(activeSites(state).map(s => s.id));
    const colour = (t: number | null) => t === null ? null : TEAMS[t % TEAMS.length].primary;
    const same = (a: Vec3, b: Vec3) => a.x === b.x && a.y === b.y && a.z === b.z;
    const sites: ObjectiveMarkerSite[] = known.map((site, i) => {
      // the ground under it: the ring's lift is person-scale (× k_human; classic unchanged), re-found when the place moves
      // or the planet's state is a new one (69.75: a crater under it)
      let g = this.markerGround.get(site.id);
      const planet = state.planets[site.planetIndex];
      if (!g || !same(g.dir, site.surfaceDir) || g.planet !== planet) {
        const n = normalize(site.surfaceDir);
        const at = add(planet.position, scale(n, this.groundAt(planet, n) + 0.12 * ps.k));
        g = { dir: site.surfaceDir, planet, ground: new THREE.Vector3(at.x, at.y, at.z), up: new THREE.Vector3(n.x, n.y, n.z) };
        this.markerGround.set(site.id, g);
      }
      const taking = site.capturer !== null && site.progress > 0;
      // both sides on it shows only to a side standing there; a capture under way only as the side knows it
      const on = same(site.surfaceDir, all[i].surfaceDir) ? standingOn(state, all[i]) : null;
      const contested = taking || (on !== null && on.size > 1 && (team === null || on.has(team)));
      return {
        letter: String.fromCharCode(65 + i), ground: g.ground, up: g.up, drape: this.markerDrape(planet),
        owner: colour(site.owner), capturer: taking ? colour(site.capturer) : null, progress: taking ? site.progress : 0,
        contested, active: active.has(site.id), ...(exact && exact.get(site.id) === false ? { rough: true } : {}),
      };
    });
    // 69.61: each carry's item, a marker of its own (ui/groundHudView.ts carryBeacons) — on its carrier while carried, pulsing
    // (its ground re-found only when it moves, as a point's: keyed below the points' ids)
    for (const b of carryBeacons(state)) {
      const key = -1 - b.objectiveId;
      let g = this.markerGround.get(key);
      if (!g || !same(g.dir, b.surfaceDir)) {
        const planet = state.planets[b.planetIndex], n = normalize(b.surfaceDir);
        const at = add(planet.position, scale(n, this.groundAt(planet, n) + 0.12 * ps.k));
        g = { dir: b.surfaceDir, planet, ground: new THREE.Vector3(at.x, at.y, at.z), up: new THREE.Vector3(n.x, n.y, n.z) };
        this.markerGround.set(key, g);
      }
      sites.push({ letter: '\u2605', ground: g.ground, up: g.up, owner: colour(b.team), capturer: null, progress: 0, contested: b.carried, active: true, item: true });
    }
    // the overview has its own pins: the column and the letter go as it comes in
    this.markers.sync(sites, this.o.camera, this.time, 1 - THREE.MathUtils.smoothstep(this.commandBlend, 0.05, 0.6));
  }

  /** This tick's intent from the keys and the mouse; one-shot inputs are consumed here. */
  /** 62.2: the stance the player has chosen (C, Z); sent with the intent. */
  private stance: Stance = 'stand';
  /** Phase 65: the weapon the player wants in hand (X), and a reload asked for (R) — sent with the intent. */
  private weapon: GroundWeaponKind = 'rifle';
  private reloadQueued = false;
  /** 65.7: a bandage asked for (H) — sent with the intent. */
  private healQueued = false;
  /** 69.45: U — the support's case set down or picked up (an impulse). */
  private caseQueued = false;
  /** 66.8: the dig key held (with the Lab's Digging in): the intent digs while the unit stands still; release stops it, the depth kept. */
  private digHeld = false;
  /** 64.11: E asked to get in or out of a seat; seated, the gun's power (W / S) and which of the seat's weapons (X). */
  private seatQueued = false;
  /** 63.1d's follow-up: in a bay with a front seat free, E is held to get out and tapped to move up — when it went down, and the outcome queued. */
  private eHeldSince: number | null = null;
  private upQueued = false;
  private leaveQueued = false;
  /** DECISIONS 250: the seat switch asked for (J) — driver ↔ gunner, sent as `seat: 'switch'`. */
  private switchQueued = false;
  /** A press since the last intent: a click shorter than a frame still fires (the button may be up again by the next frame). */
  private fireQueued = false;
  /** fix/69.21-second-tube: this frame's click (fireQueued before it is cleared), and a launcher's click held till its round goes (the rounds loaded at the click; till when). */
  private fireQueued0 = false;
  private launcherClick: { loaded: number; until: number } | null = null;
  /** 69.29 step 3b: in a tank's turret — the round X asks for next, the gunner's sight (V), its overlay, a main-gun click held till its round goes. */
  private tankRound: TankRound = 'he';
  /** 69.51: X pressed with a round in the breech — the swap goes with the next intent, once. */
  private tankSwapQueued = false;
  private gunsight = false;
  private gunsightEl: HTMLDivElement | null = null;
  /**
   * 69.54 (DECISIONS 264 (1)): the turret's aim — the bearing the player lays (the mouse, A / D), held still in the world
   * while the turret slews to it at its rates (the sim's): the camera and the crosshair follow the aim, the shot's dot marks
   * where the gun's round lands meanwhile. Null out of a turret.
   */
  private turretAim: { bodyId: number; heading: Vec3 } | null = null;
  /** 69.54: the range (world units) to what a seated gun's aim rests on, null when it rests on nothing in the gun's reach. */
  private seatRange: number | null = null;
  private mainClick: { until: number } | null = null;
  private gunPower = 30;
  private gunWeapon = 0;
  private gunTime = 0;
  /** The bandage's word on the HUD for a moment: why H did nothing (not hurt, no bandages, hands busy, the teammate moving). */
  private aidNote: { text: string; until: number } | null = null;
  /** H commits you to stand still for your bandage (a step breaks it off, 65.11): held movement keys are ignored until a fresh one is pressed or the bandage ends. */
  private healHold: { since: number } | null = null;
  /** Whether this frame's intent has gone in (it goes in before the frame's first tick). */
  private intentSent = false;
  /**
   * 66.7 (a), X32: the cover the player took (game/takeCover.ts): the point, where the body stands at it (`at`: pressed to
   * its prop), walking there or holding it, and — once held — what it hides and the side leaned round (kept while you aim,
   * so the pose never changes under the mouse). Any step of theirs, a seat, the air or another unit taken drops it; Shift
   * again leaves it.
   */
  private cover: {
    unitId: number; point: CoverPoint; at: Vec3; phase: 'going' | 'held'; since: number; side?: CoverSide; hides?: NonNullable<CoverHides>;
    /** the spot stood at (the pressed spot, or a peek's step) — not walked to again while it is the one wanted */
    reached?: Vec3;
    /** the walk under way: its spot, and the gap to it as of `at` (the stall check) */
    walk?: { target: Vec3; gap: number; at: number };
    /** the lean's spot, found as the lean began (on that side), kept while it lasts — nothing moves under the mouse */
    lean?: { side: CoverSide; spot: Vec3 };
    /** 66.9: the slot taken (lane F's, a wide rock's face): its end's lean, its middle's rise, its face's distances. */
    slot?: CoverSlot;
  } | null = null;
  /** 66.7 (a): Shift went down with nothing else pressed since (a tap takes or leaves cover on release; Shift with a key is the key's). */
  private coverTap = false;
  private coverQueued = false;
  /** 63.8: when Shift went down (host time, s; null: up) — held past GAIT.sprintHold it is a sprint, shorter a tap for cover. */
  private shiftDownAt: number | null = null;
  /** 63.8: the last shot's host time (s) — the gun is up for GAIT.readySeconds after it (the shooting walk), else down (the move). */
  private lastFireAt = -Infinity;
  /** 63.8: the gait the intent asked for this frame (the crosshair's cue: open and dim in the move, greyed in the sprint). */
  private gaitNow: 'move' | 'sprint' | undefined = undefined;
  /** 66.7 (a): right mouse held — out of the cover, firing, on the side held. */
  private peekHeld = false;
  /** 66.7 (a)'s rework: each unit's cover face (lane E's pressed poses), kept while its hold and its spot are unchanged. */
  private coverFaces = new Map<number, { cover: CoverHold; dir: Vec3; face: CoverFace | null; dug?: number }>();
  /** S7's dials for the spent tubes (game/lawSpent.ts: the Lab's rows, else the page's URL, else today's), read once a battle */
  private readonly spentDials = lawSpentConfig();
  /** 65.47: the spent cases thrown by the shots near the camera (the Lab's *Brass* row; null while it is off) */
  private brass: BrassField | null = brassShown() ? new BrassField() : null;
  /** 63.5: the soil's wear seen (made the first time a cell is worn) */
  private wearView: GroundWearView | null = null;
  /** 66.7 (a): a lean key held in cover (Q left, E right; over low cover either rises) — the laptop's peek (the owner: right mouse held is awkward on a trackpad). */
  private leanKey: 'left' | 'right' | null = null;

  /** 64.10: the held unit's intent as an input through Match.apply — on the grid, sent when it changes or turns. */
  private sendIntent(): void {
    const match = this.o.match;
    if (this.team === null || this.controlled === null) return;
    const h = match.state.players.find(p => p.id === this.controlled);
    if (!h || !h.alive) return;
    let it0 = this.intent();
    if (this.pilotQueued) { this.pilotQueued = false; it0 = { ...it0, pilot: 'take' }; }   // 69.17 step 3b: P on foot, a ship's seat in reach (the sim's to find)
    const it = quantizeIntent(it0);
    if (!intentToSend(match.actionIntents[h.id], it)) return;
    match.apply(match.stamp(seatActor(this.team), { kind: 'intent', unitId: h.id, intent: it }));
  }

  private intent(): UnitIntent {
    if (this.piloting()) return this.pilotIntent();
    const k = this.keys;
    const forward0 = (k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0);
    const right0 = (k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0);
    const turn = this.pendingYaw;
    this.pendingYaw = 0;
    const jump = this.jumpQueued;
    this.jumpQueued = false;
    const reload = this.reloadQueued;
    this.reloadQueued = false;
    const heal = this.healQueued;
    this.healQueued = false;
    const caseDrop = this.caseQueued;   // 69.45
    this.caseQueued = false;
    const mate = heal ? this.medicTarget() : null;   // 65.8: looking at a hurt teammate in reach, the bandage goes on them
    const meNow = this.human();
    if (heal && meNow && this.o.match.state.config.medical !== false) {
      if (mate) {
        const m = this.o.match.actionIntents[mate.id];
        if (m && (m.forward !== 0 || m.right !== 0)) this.aidNote = { text: `${mate.name} must hold still`, until: this.time + 2.5 };
      } else {
        const why = bandageRefusal(meNow);
        if (why) this.aidNote = { text: `can't bandage: ${why}`, until: this.time + 2.5 };
        else this.healHold = { since: this.time };
      }
    }
    // the hold for your own bandage: ends with a fresh movement key (keydown) or when the bandage is done or stopped
    if (this.healHold && (!meNow || (meNow.healLeft === undefined && this.time - this.healHold.since > 0.3))) this.healHold = null;
    const holding = this.healHold !== null;
    // 66.7 (a), X32: take cover — Shift snaps (the walk there), right mouse peeks (the lean's side step), any step away leaves
    const cov = this.coverStep(meNow, forward0 !== 0 || right0 !== 0 || jump);
    const forward = holding ? 0 : cov?.walk?.forward ?? forward0, right = holding ? 0 : cov?.walk?.right ?? right0;
    const fire = this.mouseDown || this.fireQueued;
    this.fireQueued0 = this.fireQueued;   // (fix/69.21-second-tube: this frame's click, for a launcher's hold)
    this.fireQueued = false;
    // 63.8: the gait follows the gun (playerGait)
    const { gait, raised } = playerGait({ moving: forward !== 0 || right !== 0, coverWalk: !!cov?.walk, shiftHeldFor: this.shiftDownAt === null ? null : this.time - this.shiftDownAt, sinceFire: this.time - this.lastFireAt, fire });
    if (raised) this.lastFireAt = this.time;
    this.gaitNow = gait;   // (the crosshair's cue: the same gait the rule chose)
    const me = this.human();
    // 63.1d's follow-up: seated in a bay with a front seat free, E moves up; else seated, E gets out; on foot, E gets in — the
    // seat under the crosshair when it rests on a vehicle in reach (sent as the pick), else the nearest
    const offer = this.seatQueued && !me?.mount ? this.seatOffer() : null;
    if (this.eHeldSince !== null && this.time - this.eHeldSince >= E_HOLD_S) { this.leaveQueued = true; this.eHeldSince = null; }   // held: out
    const seat = this.switchQueued && me?.mount ? 'switch' as const : this.upQueued && me?.mount ? 'up' as const : this.leaveQueued && me?.mount ? 'leave' as const
      : this.seatQueued ? (me?.mount ? 'leave' as const : 'enter' as const) : undefined;
    this.upQueued = false; this.leaveQueued = false; this.switchQueued = false;
    const seatIn = seat ? { seat, ...(offer?.pick ? { seatPick: { structureId: offer.structureId, seat: offer.seat } } : {}) } : {};
    const resupply = this.seatQueued;   // 65.9: E also restocks from the team's crate in reach (after the seats: a person who just sat down takes nothing)
    this.seatQueued = false;
    const dt = Math.min(0.1, Math.max(0, this.time - this.gunTime));
    this.gunTime = this.time;
    const mountedOn = me?.mount ? this.o.match.state.structures.find(s => s.id === me.mount!.bodyId) : undefined;
    // X31: astride a bike — W the throttle, S the brake (and back up from a stop), A / D the bars and the mouse steer; no fire
    // (63.1d: the driver's seat of any body that drives — the transport's too)
    const mySeat = mountedOn?.seats?.[me!.mount!.seat];
    if (mountedOn && drives(mountedOn) && mySeat?.role === 'driver') return { forward, right, turn, aimPitch: this.pitch, fire: false, jump: false, ...seatIn };
    // 69.29 step 3b (DECISIONS 245): a tank's turret — the mouse lays it (at the turret's rates: the sim's), left the main gun
    // (a click held till its round goes, as the launcher's), right the coax beside it, X the round loaded next, R the coax's belt
    if (!mySeat?.main) this.turretAim = null;
    if (mySeat?.main) {
      const main = mySeat.main, ready = main.loaded !== null && main.reloadLeft === undefined;
      // 69.54 (the owner: "Let's also allow for key based turret control"): A / D traverse and W / S elevate at the turret's
      // own rates, as the mouse lays it; the aim is laid here and the turret asked for the whole way to it (the sim slews it
      // at its rate, keeping what is left), the pitch within the gun's elevation
      const k = this.keys, keyTurn = ((k.has('d') || k.has('arrowright') ? 1 : 0) - (k.has('a') || k.has('arrowleft') ? 1 : 0)) * TANK.traverse * dt;
      const keyRise = ((k.has('w') || k.has('arrowup') ? 1 : 0) - (k.has('s') || k.has('arrowdown') ? 1 : 0)) * TANK.elevateRate * dt;
      this.pitch = Math.max(TANK.elevation[0], Math.min(TANK.elevation[1], this.pitch + keyRise));
      const n = normalize(me!.surfaceDir), gun = headingOf(me!), aim = this.layTurretAim(me!, mountedOn!.id, turn + keyTurn);
      const c = { x: aim.y * gun.z - aim.z * gun.y, y: aim.z * gun.x - aim.x * gun.z, z: aim.x * gun.y - aim.y * gun.x };
      const ask = Math.atan2(vecDot(c, n), vecDot(gun, aim));
      if (this.fireQueued0) this.mainClick = { until: this.time + LAUNCHER_CLICK_HOLD };
      if (this.mainClick && this.time >= this.mainClick.until) this.mainClick = null;
      const held = !!this.mainClick && ready;
      if (held) this.mainClick = null;   // (spent: its round goes this tick)
      const swap = this.tankSwapQueued; this.tankSwapQueued = false;   // (69.51: once)
      return { forward: 0, right: 0, turn: ask, aimPitch: this.pitch, fire: fire || held, ...(this.peekHeld ? { fire2: true } : {}), round: this.tankRound, ...(swap ? { swap: true as const } : {}), jump: false, ...(reload ? { reload: true } : {}), ...seatIn };
    }
    // 63.1d step 3: a mounted bullet gun (the cupola's) — the mouse lays it, a click fires, R reloads the belt
    if (mySeat?.guns?.length) return { forward: 0, right: 0, turn, aimPitch: this.gunAimPitch ?? this.pitch, ...(this.aimYaw ? { aimYaw: this.aimYaw } : {}), fire, jump: false, ...(reload ? { reload: true } : {}), ...seatIn };   // (65.25a: the assist's elevation)
    // 69.21 (the owner: "Rocket truck I can't figure out how to fire"): a mounted launcher — the rocket technical's tubes, the
    // missile technical's rail — the mouse lays it, a click fires (the missile's on its seeker's lock or straight), R reloads
    // from its store; it fell through to the seat with nothing to work, which never fires
    if (mySeat?.launcher) {
      // (fix/69.21-second-tube, lane E in play: a second click while the mount was between rounds was dropped — a click's fire
      // lasts the frames it is down; now a click is held till its round goes, up to LAUNCHER_CLICK_HOLD seconds)
      const loaded = mySeat.belt ?? MOUNT_LAUNCHERS[mySeat.launcher].magazine;
      if (this.launcherClick && loaded < this.launcherClick.loaded) this.launcherClick = null;   // (its round went)
      if (this.fireQueued0) this.launcherClick = { loaded, until: this.time + LAUNCHER_CLICK_HOLD };
      const held = !!this.launcherClick && this.time < this.launcherClick.until && loaded > 0 && mySeat.reloadLeft === undefined;
      if (this.launcherClick && !held) this.launcherClick = null;
      // 69.56: the rocket's pitch as solved to land where the crosshair rests (rocketSeatAim), else the look's
      return { forward: 0, right: 0, turn, aimPitch: mySeat.launcher === 'rocket' ? this.gunAimPitch ?? this.pitch : this.pitch, fire: fire || held, jump: false, ...(reload ? { reload: true } : {}), ...seatIn };
    }
    const gunSeat = mySeat?.weapons.length ? mySeat : undefined;
    // 63.1d: a seat with nothing to work (a transport's bay, its driver's below) — look about, E to step out
    if (mountedOn && !gunSeat) return { forward: 0, right: 0, turn, aimPitch: this.pitch, fire: false, jump: false, ...seatIn };
    if (gunSeat) {
      // 64.11: seated — the mouse lays the gun (bearing and elevation), W / S its power, X its shell, click fires;
      // 63.1f's follow-up: A / D traverse it too, at the gun's own pace (the mouse is the quick way)
      this.gunPower = Math.max(5, Math.min(100, this.gunPower + forward * GUN_POWER_RATE * dt));
      const weaponKey = gunSeat.weapons[this.gunWeapon % gunSeat.weapons.length];
      return { forward: 0, right: 0, turn: turn + right * GUN_TRAVERSE_RATE * dt, aimPitch: this.pitch, fire, jump: false, gun: { power: this.gunPower, weaponKey }, ...seatIn };
    }
    // on foot the gun answers the crosshair (67.10): the solved pitch, or the look pitch in `direct`
    // 66.8: digging in while the dig key is held (the Match digs only standing still on ground it can dig — say why not)
    const dig = this.digHeld && this.o.match.state.config.entrench === true;
    if (dig && me && !me.digAt && forward === 0 && right === 0) this.digRefusal(me);
    return { forward, right, turn, aimPitch: this.aim === 'solved' ? this.firePitch : this.pitch, ...(this.aim === 'solved' && this.aimYaw ? { aimYaw: this.aimYaw } : {}), ...(dig ? { dig: true } : {}), fire, jump, stance: cov?.stance ?? this.stance, ...(cov?.hold ? { cover: cov.hold } : {}), ...(gait ? { gait } : {}), weapon: this.weapon, ...(reload ? { reload: true } : {}), ...seatIn, ...(heal ? { heal: true, ...(mate ? { healTarget: mate.id } : {}) } : {}), ...(resupply ? { resupply: true } : {}), ...(caseDrop ? { caseDrop: true } : {}) };
  }

  private onEvents(events: ActionEvent[], gameOver: boolean): void {
    const me = this.controlled;
    // 69.80: a hold's clock run back to 0 is said, the viewer's or the other side's (it emptied without a word)
    if (this.o.match.state.modeState.objectives) {
      const h = holdBreaks(this.o.match.state, this.holdClocks, this.team);
      this.holdClocks = h.held;
      for (const beat of h.beats) this.note(beat, 6);
    }
    this.sound.events(events, me, tickLag(this.ticks - this.frameTick0, this.o.timeScale ?? 1));   // 65.38: at the tick's place in the frame
    for (const e of events) {
      if (e.kind === 'seatRefused' && e.unitId === this.controlled) { this.aidNote = { text: e.reason, until: this.time + 2 }; continue; }   // 63.1q: a wreck, said
      const k = personSetOf(this.o.match.state).k;   // effects are person-sized
      // 69.30 step 1: a round aboard from a dump — the human aboard that tank is told (nothing to press)
      if (e.kind === 'rearmed') {
        if (this.human()?.mount?.bodyId === e.structureId) this.aidNote = { text: `Rearming · ${AMMO_WORD[e.ammo]} +1 · the ${this.o.match.state.structures.find(x => x.id === e.dumpId)?.type === 'supplyTruck' ? 'truck' : 'dump'} ${e.left} left`, until: this.time + 2.5 };   // (step 5: a supply truck's bed)
        continue;
      }
      // 125: an arrival at a person scale comes down in a pod (the person steps out of it)
      if (e.kind === 'respawned' && k < 1) {
        const p = this.o.match.state.players.find(q => q.id === e.unitId);
        if (p && !p.airborne && e.at !== 'dropship')   // (69.3b's follow-up: a dropship's rider walks out of its container, no pod) (this.landers ??= new Landers(this.o.scene)).spawn(p.id, this.feetDrawn(p, this.o.match.state.planets), normalize(p.surfaceDir), unitPaletteFor(TEAMS[p.teamIndex % TEAMS.length]), PERSON_BODY_HEIGHT * k);
        continue;
      }
      if (e.kind === 'fired') {
        // the bullet and the flash come out of the drawn gun: the offset from the sim's start (the eye) is kept and blended away over the first quarter second
        // — 65.22a: a mounted bullet gun's (the HMG's, a cupola's, a pintle's) drawn muzzle, laid as it is drawn, else the firer's own gun
        const pv = this.personViews.get(e.unitId);
        const mounted = this.tankMuzzle(e.unitId, e.bullet) ?? mountedGunMuzzle(this.o.match.state, e.unitId, id => this.hmgViews.get(id)?.m.muzzle ?? this.bikeViews.get(id)?.muzzle);   // (69.29: a tank's gun by its seat and round)
        if (mounted) { const body = this.o.match.state.players.find(q => q.id === e.unitId)?.mount?.bodyId, hv = body !== undefined ? this.hmgViews.get(body) : undefined; if (hv) hv.firedAt = this.time; else { const vh = body !== undefined ? this.vehicleHmgs.get(body) : undefined; if (vh) vh.firedAt = this.time; } }   // 65.36: the .50's shot, for its live mount
        const tube = mounted ? null : this.launcherMuzzle(e.unitId);   // (69.21 step 5: a mounted rocket out of the tube it left)
        const mz = mounted ? mounted.getWorldPosition(_mz) : tube ? tube.getWorldPosition(_mz) : pv?.muzzleWorld(_mz);
        const at = mz ? { x: mz.x, y: mz.y, z: mz.z } : e.bullet.pos;
        if (mz) this.bulletStarts.set(e.bullet.id, { x: at.x - e.bullet.pos.x, y: at.y - e.bullet.pos.y, z: at.z - e.bullet.pos.z });
        // the flash: a white core and a tongue of flame along the shot, drawn additively so they never take the ground's colour, and a puff of smoke
        const shotPlanet = this.o.match.state.players.find(q => q.id === e.unitId)?.planetIndex ?? 0;
        for (const part of muzzleFlashParts(at, e.bullet.vel, normalize(sub(at, this.o.match.state.planets[shotPlanet].position)), PERSON_BODY_HEIGHT * k, this.smokeDriftAt(at, shotPlanet))) {
          this.o.scene.add(part.mesh);
          this.flashes.push({ mesh: part.mesh, life: part.life, age: 0, grow: part.grow, peak: part.peak, rise: part.rise, shared: true, base: part.mesh.scale.x });
        }
        pv?.fired();
        if (this.brass) this.throwBrass(e.unitId, e.bullet.id, e.bullet.pos, pv, mounted, k);
      }
      else if (e.kind === 'hitGround') { this.afterimage(e.bullet, e.point); this.flash(e.point, 0xc8b49a, 0.06 * k, 0.3, 2.2); }
      // 65.28: a round came near someone — their beat; yours, the cue's pulse
      else if (e.kind === 'nearMiss') { this.personViews.get(e.unitId)?.nearMiss(); if (me !== null && e.unitId === me) this.nearMissAt = this.time; }
      // 69.61 step 2: an objective done, told as its beat (§0.13) — yours or theirs, and how many of the side's are done
      else if (e.kind === 'objectiveDone') { const beat = objectiveBeat(this.o.match.state, e.objectiveId, this.team); if (beat) this.note(beat, 6); }
      else if (e.kind === 'itemCarried') { this.note(carryBeatAfter(this.lastCarry.get(e.objectiveId) ?? null, e, this.team, this.time), 6); this.lastCarry.set(e.objectiveId, { team: e.team, how: e.how, at: this.time }); }   // 69.61 step 4; 69.80: told with the one before it
      else if (e.kind === 'columnMarch') {   // 69.61 step 5; 69.80: a halt under contact, then moving again
        const line = columnBeat(e, this.team, this.columnHalted.has(e.objectiveId) ? 'contact' : null);
        if (e.how === 'stopped' && e.why === 'contact') this.columnHalted.add(e.objectiveId); else this.columnHalted.delete(e.objectiveId);
        if (line) this.note(line, 6);
      }
      else if (e.kind === 'rescueCalled' || e.kind === 'rescueLift' || e.kind === 'rescueSmoke' || e.kind === 'rescueDone' || e.kind === 'rescueAborted') { const line = rescueBeat(e, this.team); if (line) this.note(line, 6); }   // 69.62 step 4
      else if (e.kind === 'skyOpened' || e.kind === 'fobLost' || e.kind === 'hqLost') { const beat = consequenceBeat(e, this.team); if (beat) this.note(beat, 6); }   // 69.61 step 3
      // 62.19 (e): the weather held one of your drones, called its strike off, or let it fly on — say why
      else if (e.kind === 'droneGrounded' || e.kind === 'droneCleared') {
        const d = this.o.match.state.drones?.find(x => x.id === e.droneId);
        if (d && this.team !== null && d.teamIndex === this.team) {
          if (e.kind === 'droneCleared') this.note('Drone flying on: the air has cleared');
          else this.note(`${e.recalled ? 'Strike called off' : 'Drone holding'}: ${e.why === 'storm' ? 'a storm' : 'the wind is too strong'}${e.recalled ? ' — it is coming back' : ''}`);
        }
      }
      // S7: a rocket's burst — a person-scale flash the size of its damage circle; its kills in the feed
      else if (e.kind === 'shipCrashed') { this.crashDown(e); for (const r of e.impacts ?? []) this.o.onCrashGround?.(r); }   // 69.3b, the ship's crash: its impact's look; 69.24: and its craters drawn
      else if (e.kind === 'gunshipFire') this.gunshipFire(e);   // 63.13 step 2: a gunship's gun laid and its flash
      else if (e.kind === 'pdBurst') this.pdBurst(e);   // 69.9: a point-defence burst's tracers (and the flak on a ship)
      else if (e.kind === 'droneDown') {
        // 69.32 step 3: a supply drone brought down — a small flash and its smoke where it was (its drawn body goes with its run)
        const st = this.o.match.state, planet = st.planets[e.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(st).k;
        if (planet) {
          this.explosions ??= new ExplosionField(this.o.scene, person);
          const up = vecNormalize(vecSub(e.at, planet.position));
          this.explosions.emit('fireball', { at: e.at, up, life: 0.15, s0: 0.2 * person, s1: 0.7 * person, c0: '#ffd27a' });
          this.explosions.emit('smoke', { at: e.at, up, vel: { x: up.x * 0.8 * person, y: up.y * 0.8 * person, z: up.z * 0.8 * person }, life: 2.5, s0: 0.3 * person, s1: 1.4 * person, c0: '#5a5a5a', c1: '#8a8a8a', drag: 1.2 });
        }
      }
      else if (e.kind === 'intercepted') this.pdIntercept(e);   // 69.9: a round killed in the air
      else if (e.kind === 'decoyPop') this.flarePop(e);   // 69.58 step 2: a flare's pop (the flare itself is drawn from the state)
      else if (e.kind === 'shipIntercept') this.turretBurst(e);   // 69.58 step 2: the gunship's roof turret at a missile
      else if (e.kind === 'blast') {
        // X33: who the burst killed, thrown from it (harder for those it hurt more)
        for (const victim of e.kills) {
          const dmg = e.damages.find(d => d.playerId === victim)?.damage ?? 90;
          this.deathCauses.set(victim, { push: { kind: 'blast', from: new THREE.Vector3(e.point.x, e.point.y, e.point.z), strength: Math.min(1, dmg / 90) }, cause: 'blast' });
        }
        this.burst(e.explosion, e.point, e.planetIndex);   // 67.54: its class's look (a vehicle's death too: 195)
        if (e.explosion === 'law' && COUNTERMEASURE_LOOK.flareBurst === 'scatter') this.flareBurst(e.point, e.planetIndex);   // 69.58 step 2: a missile on a flare
      }
      // 64.11: a seat's gun — a planet-scale muzzle flash; the landing is Game's to present (a turn shot's impact)
      else if (e.kind === 'shellFired') this.flash(e.shell.pos, 0xffd27a, 0.25, 0.25, 2);
      else if (e.kind === 'shellImpact') {
        for (const victim of e.resolution.kills) this.deathCauses.set(victim, { push: { kind: 'blast', from: new THREE.Vector3(e.point.x, e.point.y, e.point.z), strength: 1 }, cause: 'blast' });
        const cls = shellExplosion(e.shell.weaponKey);
        if (cls) this.burst(cls, e.point, e.planetIndex);   // 67.54: the shell's look (Game skips its old fireball and dust for these)
        this.o.onShellImpact?.(e);
      }
      else if (e.kind === 'hitCover') { this.afterimage(e.bullet, e.point); this.flash(e.point, 0xd0d0d0, 0.06 * k, 0.3, 2.2); }
      else if (e.kind === 'hitUnit') {
        this.afterimage(e.bullet, e.point);
        this.flash(e.point, 0xff4a3a, 0.09 * k, 0.2, 1.8);
        if (e.bullet.ownerId === me) this.hitMarkerUntil = this.time + 0.15;
        if (e.bullet.ownerId === me && e.location === 'head') this.aidNote = { text: 'headshot', until: this.time + 0.8 };   // 65.27: said once
        if (e.targetId === me) this.hurtUntil = this.time + 0.3;
        if (e.killed) {
          this.deathCauses.set(e.targetId, { push: { kind: 'bullet', dir: new THREE.Vector3(e.bullet.vel.x, e.bullet.vel.y, e.bullet.vel.z).normalize() }, cause: 'bullet' });   // X33: a slump along the round
        }
      }
    }
    for (const l of killLines(this.o.match.state, events)) this.feed(l);   // 69.83: every kill, its killer named and its kind
    if (gameOver && !this.over) {
      this.over = true;
      const state = this.o.match.state;
      const winner = isGameOver(state) ? (checkWinCondition(state)?.winningTeamIndex ?? null) : null;
      if (document.pointerLockElement) document.exitPointerLock();
      // 67.14: the end-of-game card — the winner and how, the match's shape, the table from the match record
      const m = this.o.match;
      const myTeam = this.human()?.teamIndex ?? null;
      showEndCard({
        headline: winner === null ? 'Draw' : me === null ? `${teamName(winner)} wins` : winner === myTeam ? 'Victory' : `Defeated — ${teamName(winner)} wins`,
        tone: winner === null || me === null ? 'neutral' : winner === myTeam ? 'won' : 'lost',
        how: `How: ${howWon(checkWinCondition(state)?.reason)}`,
        shape: matchShape(state, m.record, m.actionTick),
        table: scoreTable(state, m.record, this.human()?.id ?? null, m.actionTick),
        keys: [this.o.onRestart ? 'R to play again' : '', this.o.onExit ? 'M menu' : ''].filter(Boolean).join(' · '),
      });
      this.o.onGameOver(winner);
    }
  }

  // ---------------------------------------------------------------------------
  // Units
  // ---------------------------------------------------------------------------

  private updateUnits(dt: number): void {
    this.updateCallMarks();   // 67.70: the calls' marks — the one aimed, and each made until it comes down
    this.landers?.update(dt, id => this.o.match.state.players.find(q => q.id === id)?.landingLeft);   // 69.3c: the sim's fall
    this.updateBikes(dt);   // before the people: a rider sits on its bike's saddle as drawn this frame
    this.updateHmgs();      // 65.22: and a gunner behind its HMG as laid this frame
    this.updateDropships(dt); // 69.3b: the dropships in the air and the containers they left
    this.updateSupplyDrones(); // 69.32: the supply drones on their runs
    this.layPits();         // 63.1f's follow-up: each crewed gun pit's turret on its gunner's bearing (its gun-layer's anchors too)
    this.crowd?.begin();
    this.updateUnitViews(dt);
    this.crowd?.end();
  }

  /**
   * X31: each bike on the drawn ground, along its heading, leaning into its turn, the wheels turning and the front one
   * steering; a destroyed one lies on its side. Presentation only.
   */
  private updateBikes(dt: number): void {
    const state = this.o.match.state, ps = personSetOf(state);
    if (ps.k >= 1) return;
    const person = PERSON_BODY_HEIGHT * ps.k;
    const seen = new Set<number>();
    let rehinged = 0;   // (67.60: one hinge rebuild a frame, 1.5–2.5 ms each; the rest wait a frame)
    for (const s of state.structures) {
      // (63.1o: every body that drives and has a model — a new one was drawn nowhere)
      if (!s.drive || !(VEHICLE_KINDS.includes(s.type as VehicleKind) || s.type === 'tank')) continue;   // (69.29: the tank, the tracked D look)
      seen.add(s.id);
      let m = this.bikeViews.get(s.id);
      // 63.1d's follow-up: the transport drawn as it stands — whole, damaged once a shell has hurt it, the wreck when destroyed
      const hulled = s.type !== 'motorcycle';   // (a hull on wheels: the 6×6, the technical — its forms, its path below)
      const form = !hulled ? 'whole' : s.health <= 0 ? 'wreck' : s.health < structureHealth(s.type) ? 'damaged' : 'whole';
      // 67.60's switch-on (DECISIONS 197): its hinges live on the vehicle the player is in or beside, else baked at rest —
      // a crew's lid open while its seat is crewed, shut while empty (doors and the tailgate shut); a change rebuilds it
      const crewed = vehicleCrew(s), live = this.vehicleLive(s, person);
      const hinges = `${live ? 'live' : 'rest'}:${live ? '' : (['driver', 'gunner', 'spotter'] as const).map(r => crewed[r] ? 1 : 0).join('')}`;
      const rehinge = !!m && form !== 'wreck' && D_VEHICLES.includes(s.type as DVehicle) && this.vehicleHinges.get(s.id) !== hinges && rehinged++ < 1;
      // 69.29: a tank's mobility kill changes its build (the thrown track, the blown deck are built in): a change rebuilds it
      const tankDamage = s.type === 'tank' ? `${s.disabled?.running ?? ''}/${s.disabled?.engine ? 'e' : ''}` : '';
      const redamage = !!m && s.type === 'tank' && form !== 'wreck' && (this.tankDamage.get(s.id) ?? '/') !== tankDamage;
      if (s.type === 'tank') this.tankDamage.set(s.id, tankDamage);
      if (m && ((hulled && (this.vehicleForms.get(s.id) ?? 'whole') !== form) || rehinge || redamage)) {
        this.o.scene.remove(m.root); this.bikeViews.delete(s.id);
        m = undefined;
      }
      if (!m && form === 'wreck') {
        m = s.type === 'tank' ? buildVehicleWreck('tracked', SCORCHED, person, s.id + 1, { build: (_k, p, ph) => buildTank(p, ph, TANK_PICKS).m }) : buildVehicleWreck(s.type as 'wheeled' | 'technical' | 'tracked', SCORCHED, person, s.id + 1, D_VEHICLES.includes(s.type as DVehicle) ? { build: (k, p, ph) => (k === 'wheeled' ? buildVehicleShape('btr', p, ph) : k === 'tracked' ? buildVehicleShape('m113', p, ph) : buildVehicleLook(k, p, ph)) } : {});   // baked by its builder (67.60: the D look's wreck; the APC's, the BTR's)
        this.o.scene.add(m.root);
        this.bikeViews.set(s.id, m);
        this.vehicleForms.set(s.id, form);
      }
      if (!m) {
        const owner = state.players.find(p => p.id === s.ownerId);
        const pal = unitPaletteFor(TEAMS[(owner?.teamIndex ?? 0) % TEAMS.length]);
        // 67.60: the D look (the owner: "Let's run with vehicles d"); the APC as the BTR ("BTR can be a swap for APC": seats, exits and its hit shape as the 6×6's)
        const smoke = (state.config.vehicleSmoke ?? 0) > 0;   // (67.77: its smoke launchers drawn while vehicles carry smoke)
        m = s.type === 'wheeled' ? buildVehicleShape('btr', pal, person, { smoke }) : s.type === 'tank' ? this.tankModel(s, pal, person, smoke) : s.type === 'tracked' ?   // (69.29: the tank — lane E's look, the owner's picks, DECISIONS 247)
           buildVehicleShape('m113', pal, person, { smoke, ...(s.armament === 'mortar' ? { armament: 'mortar' as const } : {}) }) :   // (197 step 1: the tracked carrier as lane E's M113; 69.23: its mortar's rig posed each frame, poseMortarView)
           D_VEHICLES.includes(s.type as DVehicle) ? buildVehicleLook(s.type as DVehicle, pal, person, s.drive.variant, { smoke, ...(s.armament ? { armament: s.armament } : {}) }) : buildVehicleModel(s.type as VehicleKind, pal, person, s.drive.variant, s.armament ? { armament: s.armament } : {});   // (69.21: the mount as armed)
        if (form === 'damaged') damageVehicleModel(m, s.id + 1);
        this.vehicleForms.set(s.id, form);
        // 67.60: posed (doors shut, each lid by its seat) and baked — its hinged parts on their own when live, else into their parents
        updateVehicleModel(m, { speed: live ? 0 : 1, doors: 0, crewed: live ? this.lidShares.get(s.id) ?? crewed : crewed }, 0);   // (at rest the bike's stand folded, as today's)
        bakeVehicleModel(m, { hinges: live ? 'live' : 'rest' });
        this.vehicleHinges.set(s.id, hinges);
        this.o.scene.add(m.root);
        this.bikeViews.set(s.id, m);
      }
      this.vehicleHmg(s, m, form === 'wreck');
      if (m.cargo && form !== 'wreck') setTruckLoad(m, truckLoadShare(s.cargo));   // 166: the supply truck's load is its cargo (laden, part, empty; a set only when it changes; a wreck as built)
      if (m.cargo && form !== 'wreck') setTruckAmmo(m, s.ammo, () => unitPaletteFor(TEAMS[(state.players.find(p => p.id === s.ownerId)?.teamIndex ?? 0) % TEAMS.length]));   // 69.34: its bed's rounds at the tailgate
      if (hulled) { this.updateTransport(s, m, dt); continue; }
      const planet = state.planets[s.planetIndex];
      // 63.1d step 2: seated on the ground under its wheels (pitched), as its hit shape stands (core/vehicleFrame.ts)
      const f = vehicleFrame(planet, s, person, ps, d => this.groundAt(planet, d));
      _by.set(f.up.x, f.up.y, f.up.z); _bz.set(f.front.x, f.front.y, f.front.z); _bx.set(f.left.x, f.left.y, f.left.z);
      m.root.position.set(f.foot.x, f.foot.y, f.foot.z);
      m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      const wrecked = s.health <= 0;
      const v = wrecked ? 0 : (s.drive.speed / person) * PERSON_UNITS;          // person units a second
      const yawRate = wrecked ? 0 : s.drive.yawRate ?? 0;                       // + = toward the right
      // + rolls the hull toward its −x, the rider's right in this basis (+x = up × front, the left): a right turn leans right
      const lean = wrecked ? BIKE_LOOK.fallen : Math.max(-BIKE_LOOK.leanMax, Math.min(BIKE_LOOK.leanMax, Math.atan((Math.abs(s.drive.speed) / person) * yawRate / BIKE_LOOK.leanG)));
      const steer = Math.max(-BIKE_LOOK.steerMax, Math.min(BIKE_LOOK.steerMax, -yawRate * BIKE_LOOK.wheelbase / Math.max(1, Math.abs(s.drive.speed) / person)));
      updateVehicleModel(m, { speed: v, steer, lean }, dt);
      m.root.updateMatrixWorld(true);
    }
    for (const [id, m] of this.bikeViews) if (!seen.has(id)) { this.o.scene.remove(m.root); this.bikeViews.delete(id); this.vehicleHinges.delete(id); this.lidShares.delete(id); this.vehicleHmgs.delete(id); this.smokeSeen.delete(id); }
    this.smokePops(state, person);
    // (67.73's orbit census: a vehicle under VEHICLE_MIN_PX across is hidden — its baked parts are a draw each, a dozen from orbit for nothing seen)
    const cam = this.o.camera, focal = (typeof window !== 'undefined' ? window.innerHeight : 900) / 2 / Math.tan((cam.fov * Math.PI) / 360), across = VEHICLE_ACROSS_PEOPLE * person;
    for (const m of this.bikeViews.values()) m.root.visible = across * focal >= VEHICLE_MIN_PX * m.root.position.distanceTo(cam.position);
  }

  /**
   * 65.36: a vehicle's .50 for the player at its grips — its live mount on the cradle (the parts apart: the barrel's recoil on
   * each shot, the feed cover and the charging handle through the belt's reload, the butterfly trigger), made the first time
   * on the model it rides; the baked gun shows otherwise, still, on every vehicle (the player's only while they are at it;
   * 67.60's rest-bake untouched: the player at the grips is in the vehicle, so it is live).
   */
  private vehicleHmg(s: StructureState, m: VehicleModel, wreck: boolean): void {
    const state = this.o.match.state, seat = s.seats?.findIndex(q => q.guns?.includes('hmg')) ?? -1;
    let v = this.vehicleHmgs.get(s.id);
    if (v && v.m !== m) { v = { m, firedAt: v.firedAt }; this.vehicleHmgs.set(s.id, v); }   // (the old model went with its mount)
    if (seat < 0 || !m.cradle || wreck) return;   // (no .50, or the wreck: a model of its own, no mount on it)
    const me = this.controlled === null ? undefined : state.players.find(p => p.id === this.controlled);
    const own = !!me?.alive && me.mount?.bodyId === s.id && me.mount.seat === seat;
    if (!own && !v?.live) return;
    if (!v) { v = { m }; this.vehicleHmgs.set(s.id, v); }
    const cradle = { cradle: m.cradle };
    if (!v.live) { const owner = state.players.find(p => p.id === s.ownerId); v.live = hmgLiveMount(cradle, unitPaletteFor(TEAMS[(owner?.teamIndex ?? 0) % TEAMS.length]), { block: false, z: HMG_ON_CRADLE }); }
    showHmgLive(cradle, v.live, own);
    if (!own) return;
    const left = s.seats![seat].reloadLeft ?? 0, rk = left > 0 ? 1 - left / MOUNTED_GUNS.hmg.reload : -1;
    setGunParts(v.live.gun, rk >= 0 && rk < 1 ? gunReloadPose('hmg', rk) : gunShotPose(this.time - (v.firedAt ?? -Infinity), Math.min(0.16, 0.9 * MOUNTED_GUNS.hmg.interval)));
  }

  /**
   * 65.22: each emplaced HMG on the drawn ground, in the frame its hit shape stands in (math.ts uprightFrame), its gun
   * laid where its gunner aims — the bearing off the frame's front and the pitch — and held where it was left when
   * nobody is at it; a destroyed one is lane E's wreck. Presentation only.
   */
  private updateHmgs(): void {
    const state = this.o.match.state, ps = personSetOf(state);
    if (ps.k >= 1) return;
    const person = PERSON_BODY_HEIGHT * ps.k;
    const seen = new Set<number>();
    for (const s of state.structures) {
      if (s.type !== 'hmg') continue;
      seen.add(s.id);
      let v = this.hmgViews.get(s.id);
      const owner = state.players.find(p => p.id === s.ownerId), palette = unitPaletteFor(TEAMS[(owner?.teamIndex ?? 0) % TEAMS.length]);
      if (!v) {
        const m = buildHmgModel(palette, person, buildPlayHmg(palette));   // 65.36: the .50 as play draws it
        dressHmgD(m);                                                       // 67.62: the D pass (the ring as filled sandbags on the same places; seat and hit shape as built)
        bakeHmgModel(m);
        this.o.scene.add(m.root);
        v = { m, wreck: null };
        this.hmgViews.set(s.id, v);
      }
      const planet = state.planets[s.planetIndex];
      const up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);   // (65.22 (2): its ring turned to its gap's facing)
      // (67.52: a seated HMG at its sim base — the coarse ground plus its lift — or the drawn ground where that is higher: the
      // far lift away from the camera; near it the drawn ground under its disc is that base, rendering/fineFlatten.ts)
      const foot = vecAdd(planet.position, vecScale(up, s.baseLift !== undefined ? Math.max(getSurfaceRadius(planet, up) + s.baseLift, this.groundAt(planet, up)) : this.groundAt(planet, up)));
      _by.set(up.x, up.y, up.z); _bz.set(tz.x, tz.y, tz.z); _bx.set(tx.x, tx.y, tx.z);
      _bm.makeBasis(_bx, _by, _bz);
      if (s.health <= 0) {
        if (!v.wreck) {
          v.wreck = buildHmgWreck(palette, person, s.id + 1, buildPlayHmg(palette));
          dressHmgWreckD(v.wreck);
          this.o.scene.remove(v.m.root);
          this.o.scene.add(v.wreck);
        }
        v.wreck.position.set(foot.x, foot.y, foot.z);
        v.wreck.quaternion.setFromRotationMatrix(_bm);
        continue;
      }
      v.m.root.position.set(foot.x, foot.y, foot.z);
      v.m.root.quaternion.setFromRotationMatrix(_bm);
      const gunner = state.players.find(p => p.alive && p.mount?.bodyId === s.id);
      if (gunner?.heading) {
        const h = gunner.heading;
        aimHmg(v.m, Math.atan2(h.x * tx.x + h.y * tx.y + h.z * tx.z, h.x * tz.x + h.y * tz.y + h.z * tz.z), gunner.aimPitch ?? 0);
      }
      // 65.36: the player at its grips — the live mount (its parts apart: the barrel's recoil on each shot, the feed cover and
      // the charging handle through the belt's reload, the butterfly trigger); every other .50 stays baked and still
      const own = gunner !== undefined && gunner.id === this.controlled;
      if (own && !v.live) v.live = hmgLiveMount(v.m, palette);
      if (v.live) {
        showHmgLive(v.m, v.live, own);
        if (own) {
          const seat = s.seats?.find(q => q.guns?.length), left = seat?.reloadLeft ?? 0, rk = left > 0 ? 1 - left / MOUNTED_GUNS.hmg.reload : -1;
          setGunParts(v.live.gun, rk >= 0 && rk < 1 ? gunReloadPose('hmg', rk) : gunShotPose(this.time - (v.firedAt ?? -Infinity), Math.min(0.16, 0.9 * MOUNTED_GUNS.hmg.interval)));
        }
      }
      v.m.root.updateMatrixWorld(true);
    }
    for (const [id, v] of this.hmgViews) if (!seen.has(id)) { this.o.scene.remove(v.m.root); if (v.wreck) this.o.scene.remove(v.wreck); this.hmgViews.delete(id); }
  }

  /**
   * 69.3b: each dropship in the air at its pose this tick (core/dropship.ts dropshipPose: in along its track, down, out the way
   * it came), drawn as orbital-design-2's ship (rendering/dropshipLooks.ts) with its troop container in the slot: the nacelles
   * swung to the hover and the legs down near the ground, the ramp let down there, the container gone from the slot once it
   * is set down (it is a structure then); each container left standing on the drawn ground, turned to its yaw (the frame its
   * hit shape stands in), its ramp down, scorched once broken. Both baked. Presentation only.
   */
  /**
   * 69.32: each supply drone on a run at its pose this tick (core/supplyDrone.ts droneRunPose: up from its source, along its
   * route, down quick at the point, back), drawn as 67.75's utility drone (rendering/utilityDrone.ts) in its side's colours,
   * its crate in the cage till it is set down (the crate is a structure then), its height over the drawn ground. Presentation only.
   */
  private updateSupplyDrones(): void {
    const state = this.o.match.state, runs = state.supplyDrones?.runs ?? [];
    if (!runs.length && !this.droneViews.size) return;
    const tick = this.o.match.actionTick, scale = (PERSON_BODY_HEIGHT * personSetOf(state).k) / 1.8;   // (the model's units: a person 1.8)
    const seen = new Set<number>();
    for (const r of runs) {
      const pose = droneRunPose(state, r, tick);
      if (!pose) continue;
      seen.add(r.id);
      let v = this.droneViews.get(r.id);
      if (!v) {
        const team = TEAMS[r.team % TEAMS.length], palette = unitPaletteFor(team), livery = { name: hullName(team.name), mark: r.id };
        v = { carry: buildUtilityDrone(palette, livery).full, empty: buildUtilityDrone(palette, livery, { crate: false }).full };
        for (const m of [v.carry, v.empty]) { m.scale.setScalar(scale); this.o.scene.add(m); }
        this.droneViews.set(r.id, v);
      }
      const planet = state.planets[r.planetIndex], up = vecNormalize(pose.dir), h = pose.heading;
      const foot = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up) + pose.height));
      const left = { x: up.y * h.z - up.z * h.y, y: up.z * h.x - up.x * h.z, z: up.x * h.y - up.y * h.x };
      _by.set(up.x, up.y, up.z); _bz.set(h.x, h.y, h.z); _bx.set(left.x, left.y, left.z);
      for (const m of [v.carry, v.empty]) { m.position.set(foot.x, foot.y, foot.z); m.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz)); }
      v.carry.visible = pose.carrying;
      v.empty.visible = !pose.carrying;
    }
    for (const [id, v] of this.droneViews) {
      if (seen.has(id)) continue;
      for (const m of [v.carry, v.empty]) { this.o.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
      this.droneViews.delete(id);
    }
  }

  /**
   * 69.59 step 1: a helicopter's flight drawn — lane H's look C (rendering/helicopterLooks.ts `armed`, the owner's pick of look
   * to come: swapping it is presentation only), on its pose's ground point, banked as the pose has it (eased as a dropship's:
   * the pose's left wing down is the body's −z roll), its rotors spun (slower standing), its doors slid open on the ground.
   */
  private updateHeliView(f: DropshipFlight, pose: DropshipPose, person: number, dt: number): void {
    const state = this.o.match.state, planet = state.planets[f.planetIndex];
    if (!planet) return;
    let v = this.heliViews.get(f.id);
    if (!v) {
      const m = buildHelicopter('armed', unitPaletteFor(TEAMS[f.team % TEAMS.length]), person);
      bakeHelicopter(m);
      this.o.scene.add(m.root);
      v = { m };
      this.heliViews.set(f.id, v);
    }
    const up = vecNormalize(pose.dir), deck = f.padSpot !== undefined ? this.heliDeck(f.team, up, planet) : 0;   // (69.74 step 2b: off and onto its spot's deck)
    const foot = pose.height > deck ? pose.pos : vecAdd(planet.position, vecScale(up, this.groundAt(planet, up) + deck));
    const h = pose.heading, left = { x: up.y * h.z - up.z * h.y, y: up.z * h.x - up.x * h.z, z: up.x * h.y - up.y * h.x };
    _by.set(up.x, up.y, up.z); _bz.set(h.x, h.y, h.z); _bx.set(left.x, left.y, left.z);
    v.m.root.position.set(foot.x, foot.y, foot.z);
    v.m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
    const bankTo = -(pose.bank ?? 0), ease = Math.min(1, dt * 6), doorsTo = pose.phase === 'down' ? 1 : 0;
    v.bank = v.bank === undefined ? bankTo : v.bank + (bankTo - v.bank) * ease;
    v.doors = v.doors === undefined ? doorsTo : v.doors + (doorsTo - v.doors) * Math.min(1, dt * 2);
    updateHelicopter(v.m, { aim: 0, doors: v.doors, thrust: pose.phase === 'down' ? 0.35 : 1, bank: v.bank }, dt);
  }

  /** 69.59 step 1: a helicopter coming down — its model on the crash's arc (its frame, spinning as the crash has it), the rotors slowing, a smoke trail. */
  private heliFall(c: ShipCrash, tick: number, person: number, dt: number): void {
    const state = this.o.match.state;
    let v = this.heliViews.get(c.id);
    if (!v) {
      const m = buildHelicopter('armed', unitPaletteFor(TEAMS[c.team % TEAMS.length]), person);
      bakeHelicopter(m);
      this.o.scene.add(m.root);
      v = { m };
      this.heliViews.set(c.id, v);
    }
    const fr = crashPose(state, c, tick).frame;
    _by.set(fr.up.x, fr.up.y, fr.up.z); _bz.set(fr.front.x, fr.front.y, fr.front.z); _bx.set(fr.left.x, fr.left.y, fr.left.z);
    v.m.root.position.set(fr.origin.x, fr.origin.y, fr.origin.z);
    v.m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
    updateHelicopter(v.m, { aim: 0, doors: 0, thrust: 0.1, bank: v.bank ?? 0 }, dt);
    if (Math.random() < dt * 20) {
      this.explosions ??= new ExplosionField(this.o.scene, person);
      this.explosions.emit('smoke', { at: fr.origin, up: fr.up, vel: { x: fr.up.x * 0.5 * person, y: fr.up.y * 0.5 * person, z: fr.up.z * 0.5 * person }, life: 2.5, s0: 0.4 * person, s1: 1.6 * person, c0: '#3c3a38', c1: '#6b6866', drag: 1.0 });
    }
  }

  /** 67.73 step 2: the parked wave ships as drawn (the far field shows each full only near; a rider's is near). */
  parkedShipViews(): ParkedShipView[] {
    const out: ParkedShipView[] = [];
    for (const [key, v] of this.dropshipViews) if (key < 0) out.push({ key, team: -1 - key, root: v.m.root });
    return out;
  }

  private updateDropships(dt: number): void {
    const state = this.o.match.state, ps = personSetOf(state);
    if (ps.k >= 1) return;
    this.updateRide();
    const person = PERSON_BODY_HEIGHT * ps.k, tick = this.o.match.actionTick;
    const seen = new Set<number>();
    for (const f of state.dropships?.flights ?? []) {
      const now = flightNow(state, f, tick);   // 69.17 step 1: the ship as it is now
      if (!now) continue;
      const pose = now.flight!.pose;
      if (f.craft === 'heli') { this.updateHeliView(f, pose, person, dt); continue; }   // 69.59 step 1: its own look
      seen.add(f.id);
      let v = this.dropshipViews.get(f.id);
      if (!v) {
        // (63.13 step 2: a gunship, the sheet's built R2 with its side gun at the match's calibre — baked by its own; nothing slung)
        const palette = unitPaletteFor(TEAMS[f.team % TEAMS.length]), gs = f.payload === 'gunship' ? buildGunshipView(palette, person, f.gunship?.gun ?? 40, f.gunship?.chin ?? 30, defaultOn(this.o.match.state.config, 'shipTurrets')) : null, m = gs ? gs.m : buildDropship(palette, person);
        let payload: THREE.Object3D;
        if (gs) payload = new THREE.Group();
        else if (f.payload === 'vehicle' && f.vehicle) {
          // step 2: the vehicle in the slot, built in the ship's own units (a person 1.8) as play draws its kind (67.60: the 6×6 as the BTR)
          const k = f.vehicle, vm = k === 'wheeled' ? buildVehicleShape('btr', palette, 1.8) : k === 'tank' ? buildTank(palette, 1.8).m : D_VEHICLES.includes(k as DVehicle) ? buildVehicleLook(k as DVehicle, palette, 1.8, 'gas') : buildVehicleModel(k, palette, 1.8, 'gas');
          mountVehicle(m, vm);
          payload = vm.root;
        } else { const c = mountContainer(m, palette); bakeContainer(c); payload = c.root; }
        if (!gs) bakeDropship(m);
        this.o.scene.add(m.root);
        v = { m, payload, ...(gs ? { gun: gs.view } : {}) };
        this.dropshipViews.set(f.id, v);
      }
      const planet = state.planets[f.planetIndex], up = vecNormalize(pose.dir);
      // (on the ground: the drawn ground under it, as the container's; in the air its sim height over the fine ground)
      const foot0 = pose.height > 0 ? pose.pos : vecAdd(planet.position, vecScale(up, this.groundAt(planet, up)));
      // (208's follow-up: a wave ship low over its own pad stands on its deck, eased to the sim's height by 3 people up)
      const deck = f.fromPad ? this.padDeck(f.team, pose.dir, planet) * Math.max(0, 1 - pose.height / (3 * person)) : 0;
      const foot = deck > 0 ? vecAdd(foot0, vecScale(up, deck)) : foot0;
      const h = pose.heading, left = { x: up.y * h.z - up.z * h.y, y: up.z * h.x - up.x * h.z, z: up.x * h.y - up.y * h.x };
      _by.set(up.x, up.y, up.z); _bz.set(h.x, h.y, h.z); _bx.set(left.x, left.y, left.z);
      if (f.piloted) { const fr = now.frame; _by.set(fr.up.x, fr.up.y, fr.up.z); _bz.set(fr.front.x, fr.front.y, fr.front.z); _bx.set(fr.left.x, fr.left.y, fr.left.z); }   // 69.17 step 3b: flown, banked and pitched as its body
      v.m.root.position.set(foot.x, foot.y, foot.z);
      v.m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      const low = Math.max(0, Math.min(1, 1 - (pose.height / person - 4) / 10));   // (1 within 4 people of the ground, 0 from 14 up)
      v.payload.visible = pose.carrying;   // (set down, it is a structure: drawn as one)
      v.parts = f.parts;   // (69.3b, the ship's crash: the nacelles lost, should it fall)
      // 63.16: the ramp as the sim's container stands — let down at the landing as the pose has it, held down while it stands,
      // closed as it lifts (the pose's 0 then); a wave ship off its pad starts from its parked view's ramp, down for the boarding
      v.ramp = easeRamp(v.ramp ?? (f.fromPad ? this.dropshipViews.get(-1 - f.team)?.ramp ?? 0 : pose.ramp), pose.ramp, dt);
      if (this.ride?.key === f.id) this.rideCabinIn(f.team, v, v.ramp);   // 207 (7): the cabin in its container's place
      // 69.3b's follow-up (the owner: "tuning nacelle direction to animation … forward angle when slowing down on entry"): the
      // nacelles by the ship's motion (core/dropship.ts dropshipKinematics; dropshipLooks nacelleTiltFor) — aft at its cruise
      // speed, tipped forward of down while it brakes into the landing, upright in the hover and through the drop, turning aft
      // ahead of its speed as it leaves by either exit — eased toward that (easeTilt: no snaps); the hull pitched against its
      // acceleration (nose up as it slows into the landing)
      const kin = now.flight!.kin;
      const target = f.piloted ? now.frame.tilt : kin ? nacelleTiltFor(kin.speed, kin.accel, DROPSHIP.speedPeople) : low;   // (69.17 step 3b: flown, the allocation's)
      const tilt = v.tilt = v.tilt === undefined ? target : easeTilt(v.tilt, target, dt);
      const pitch = kin ? Math.max(-0.25, Math.min(0.25, -kin.accel / 40)) : 0;
      // (69.66: the hull banked as the pose has it — its bends' and its turn onto the landing, 63.13 a gunship's pylon turn; the
      // pose's left wing down is the hull's −z roll, eased a sixth of a second; flown, its body's frame banks it)
      const bankTo = -(pose.bank ?? 0), bank = v.bank = v.bank === undefined ? bankTo : v.bank + (bankTo - v.bank) * Math.min(1, dt * 6);
      const flownBody = f.piloted?.body;
      updateDropship(v.m, { tilt, pitch: flownBody ? 0 : pitch, bank: flownBody ? 0 : bank, gear: f.payload === 'troops' && pose.height / person < 6 ? 1 : 0, thrust: flownBody ? flownBody.thrust.reduce((sum, t) => sum + t, 0) / flownBody.thrust.length : pose.phase === 'down' ? (f.payload === 'vehicle' ? 0.55 : 0.25) : 0.4 + 0.5 * low, ramp: v.ramp,
        lower: pose.lower * 1.8, cables: pose.cables && pose.carrying, clamps: pose.clamps }, dt);   // (the model's units: a person 1.8; the bank eased above: 69.66's bends, 63.13 step 2's pylon turn)
      if (v.gun) updateGunshipView(v.gun, f, dt);   // 63.13 step 2: its guns laid, its heavy rockets as its load holds them
      // 69.17 step 3b: flown, each nacelle as the flight model's allocation set it (D1: the drawn one the physics' own), not the look's rule
      if (flownBody) v.m.nacelles.forEach((g, i) => { if (g) g.rotation.x = (flownBody.tilt[i] ?? 0) - Math.PI / 2; });
      v.m.root.updateMatrixWorld(true);
    }
    // DECISIONS 207 (1): each side's wave ship idling on its pad between runs (its key −1 − team), its engines low, its container in the slot
    for (const p of parkedShips(state)) {
      const key = -1 - p.team;
      seen.add(key);
      let v = this.dropshipViews.get(key);
      if (!v) {
        const m = buildDropship(unitPaletteFor(TEAMS[p.team % TEAMS.length]), person), c = mountContainer(m, unitPaletteFor(TEAMS[p.team % TEAMS.length]));
        bakeContainer(c);
        bakeDropship(m);
        this.o.scene.add(m.root);
        v = { m, payload: c.root };
        this.dropshipViews.set(key, v);
      }
      const planet = state.planets[p.planetIndex], up = vecNormalize(p.dir), foot = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up) + this.padDeck(p.team, p.dir, planet)));   // (208's follow-up: on its pad's deck)
      const h = p.facing, left = { x: up.y * h.z - up.z * h.y, y: up.z * h.x - up.x * h.z, z: up.x * h.y - up.y * h.x };
      _by.set(up.x, up.y, up.z); _bz.set(h.x, h.y, h.z); _bx.set(left.x, left.y, left.z);
      v.m.root.position.set(foot.x, foot.y, foot.z);
      v.m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      v.tilt = v.tilt === undefined ? 1 : easeTilt(v.tilt, 1, dt);
      // 63.16 (the owner: "should be left open for mount and while waiting for takeoff"): the ramp down on the pad, as the
      // sim's container stands (its open back: core/dropship.ts syncShipContainers); it closes as the ship lifts (the flight's view)
      v.ramp = easeRamp(v.ramp ?? 0, 1, dt);
      updateDropship(v.m, { tilt: v.tilt, pitch: 0, gear: 1, thrust: 0.1, ramp: v.ramp, lower: 0, cables: false, clamps: 0 }, dt);
      v.payload.visible = true;
      if (this.ride?.key === key) this.rideCabinIn(p.team, v, v.ramp, false);   // 208: boarded on the pad, the cabin in its place (the ramp down for the boarding, the light red till the drop)
      v.m.root.updateMatrixWorld(true);
    }
    // 69.74 step 2b: each side's helicopters parked on its helipad (keys −1000 − 64 team − spot), the rotors still; turning
    // round (refuelled, rearmed), idling; shown with their pad (the enemy's unseen under the fog)
    const parked = new Set<number>();
    for (const p of parkedHelis(state)) {
      const key = -1000 - 64 * p.team - p.spot;
      parked.add(key);
      let v = this.heliViews.get(key);
      if (!v) {
        const m = buildHelicopter('armed', unitPaletteFor(TEAMS[p.team % TEAMS.length]), person);
        bakeHelicopter(m);
        this.o.scene.add(m.root);
        v = { m };
        this.heliViews.set(key, v);
      }
      v.m.root.visible = this.o.structureShown?.(p.padId) ?? true;
      if (!v.m.root.visible) continue;
      const planet = state.planets[p.planetIndex], up = vecNormalize(p.dir), foot = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up) + this.heliDeck(p.team, up, planet)));
      const h = p.facing, left = { x: up.y * h.z - up.z * h.y, y: up.z * h.x - up.x * h.z, z: up.x * h.y - up.y * h.x };
      _by.set(up.x, up.y, up.z); _bz.set(h.x, h.y, h.z); _bx.set(left.x, left.y, left.z);
      v.m.root.position.set(foot.x, foot.y, foot.z);
      v.m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      updateHelicopter(v.m, { aim: 0, doors: 0, thrust: p.turning ? 0.15 : 0, bank: 0 }, dt);
    }
    // 69.3b, the ship's crash (DECISIONS 223): each lost flight falling along its crash's pose (core/shipCrash.ts crashPose) —
    // its own model kept from its flight, struck by the cause (rendering/shipCrashLook.ts FallingShip: the nacelles it lost
    // gone, fire where they were, a smoke trail, the rest hunting for lift; its pieces after a break-up in the air)
    for (const c of state.dropships?.crashes ?? []) {
      if (tick >= c.impactTick) continue;
      if (c.craft === 'heli') { this.heliFall(c, tick, person, dt); continue; }   // 69.59 step 1: its own model falling
      seen.add(c.id);
      let v = this.dropshipViews.get(c.id);
      if (!v) {   // (a view made after the loss: the ship bare)
        const m = buildDropship(unitPaletteFor(TEAMS[c.team % TEAMS.length]), person);
        bakeDropship(m);
        this.o.scene.add(m.root);
        v = { m, payload: new THREE.Group() };
        this.dropshipViews.set(c.id, v);
      }
      if (!v.fall) { v.fall = new FallingShip(v.m, c.cause, Math.sign(c.spinRate) || 1, lostNacelles(v.parts), SCORCHED, c.id + 1); this.o.scene.add(v.fall.pieces); }
      const pose = crashPose(state, c, tick), planet = state.planets[c.planetIndex], o = pose.frame.origin, cam = this.o.camera.position;
      const ground = vecAdd(planet.position, vecScale(pose.dir, this.groundAt(planet, pose.dir)));
      v.payload.visible = c.carrying;
      v.tilt = easeTilt(v.tilt ?? 0, c.cause === 'nacelle' ? 1 : 0, dt);   // (a nacelle loss: the rest swung down for lift; else they hang where the loss left them)
      updateDropship(v.m, { tilt: v.tilt, pitch: 0, gear: 0, thrust: c.cause === 'hull' ? 0 : 0.12 + 0.12 * Math.sin(this.time * 23 + c.id), ramp: 0, lower: 0, cables: false, clamps: 0 }, dt);
      const broke = c.brokeUp !== undefined && tick >= c.brokeUp ? { share: (tick - c.brokeUp) / Math.max(1, c.impactTick - c.brokeUp), pieces: c.pieces } : null;
      this.explosions ??= new ExplosionField(this.o.scene, person);
      v.fall.update(this.explosions, pose.frame, ground, broke, dt, person, burstDetail(Math.hypot(o.x - cam.x, o.y - cam.y, o.z - cam.z) / person));
      let vel = this.fallen.get(c.id);
      if (!vel) this.fallen.set(c.id, vel = new THREE.Vector3());
      vel.copy(v.fall.vel);
    }
    const helisUp = new Set([...(state.dropships?.flights ?? []).filter(f => f.craft === 'heli').map(f => f.id), ...(state.dropships?.crashes ?? []).filter(c => c.craft === 'heli' && tick < c.impactTick).map(c => c.id)]);
    for (const [id, v] of this.heliViews) if (!helisUp.has(id) && !parked.has(id)) { this.o.scene.remove(v.m.root); this.heliViews.delete(id); }
    for (const [id, v] of this.dropshipViews) if (!seen.has(id)) {
      this.o.scene.remove(v.m.root); this.dropshipViews.delete(id);
      if (v.fall) { this.o.scene.remove(v.fall.pieces); v.fall.dispose(); }
    }
    const standing = new Set<number>();
    for (const s of state.structures) {
      if (s.type !== 'dropContainer' || s.ship) continue;   // (69.3b's follow-up: a container a standing ship carries is the ship's look's)
      standing.add(s.id);
      const broken = s.health <= 0;
      let v = this.containerViews.get(s.id);
      if (v && v.wreck !== broken) { this.o.scene.remove(v.root); v = undefined; }
      if (!v) {
        const owner = state.players.find(p => p.id === s.ownerId), c = buildTroopContainer(broken ? SCORCHED : unitPaletteFor(TEAMS[(owner?.teamIndex ?? 0) % TEAMS.length]));
        setContainerRamp(c, broken ? 1 : 0);
        if (broken) { c.root.rotation.set(0.12, 0, 0.3); c.root.position.y = -0.3 * 1.8; }
        bakeContainer(c);   // (one draw a material: at most DROPSHIP.containerCap a side stand)
        const root = new THREE.Group();
        root.scale.setScalar(person / 1.8);
        root.add(c.root);
        v = { root, wreck: broken, c, ramp: broken ? 1 : 0 };
        this.o.scene.add(v.root);
        this.containerViews.set(s.id, v);
      }
      // 63.16: its ramp as the sim has it — down while its flight stands at it (the open back), shut once it lifts (one box:
      // core/buildingScale.ts structureHitbox); a wreck's as it fell
      if (!broken) { const want = s.rampOpen ? 1 : 0; if (v.ramp !== want) setContainerRamp(v.c, v.ramp = easeRamp(v.ramp, want, dt)); }
      const planet = state.planets[s.planetIndex], up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
      const foot = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up)));
      _by.set(up.x, up.y, up.z); _bz.set(tz.x, tz.y, tz.z); _bx.set(tx.x, tx.y, tx.z);
      v.root.position.set(foot.x, foot.y, foot.z);
      v.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      v.root.updateMatrixWorld(true);
    }
    for (const [id, v] of this.containerViews) if (!standing.has(id)) { this.o.scene.remove(v.root); this.containerViews.delete(id); }
    // 69.3b, the ship's crash: each wreck on the drawn ground at its frame (its hit shape's), burning and then smoking by its
    // age, settling in its last seconds (rendering/shipCrashLook.ts WreckView; one baked mesh each)
    const wrecks = new Set<number>(), cam = this.o.camera.position;
    for (const s of state.structures) {
      if (s.type !== 'shipWreck' || !s.wreck) continue;
      wrecks.add(s.id);
      let w = this.wreckViews.get(s.id);
      if (!w) { w = new WreckView(s.wreck.piece, SCORCHED, person, s.id + 1); this.o.scene.add(w.root); this.wreckViews.set(s.id, w); }
      const planet = state.planets[s.planetIndex], up = vecNormalize(s.surfaceDir), { tx, tz } = structureFrame(s, up);
      const foot = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up)));
      _by.set(up.x, up.y, up.z); _bz.set(tz.x, tz.y, tz.z); _bx.set(tx.x, tx.y, tx.z);
      w.root.position.set(foot.x, foot.y, foot.z);
      w.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
      w.root.updateMatrixWorld(true);
      this.explosions ??= new ExplosionField(this.o.scene, person);
      w.update(this.explosions, (tick - s.wreck.at) * PHYS_DT, dt, burstDetail(Math.hypot(foot.x - cam.x, foot.y - cam.y, foot.z - cam.z) / person));
    }
    for (const [id, w] of this.wreckViews) if (!wrecks.has(id)) { this.o.scene.remove(w.root); w.dispose(); this.wreckViews.delete(id); }
  }

  /**
   * 69.3b, the ship's crash: a ship come down (`shipCrashed`) — at each of its pieces a heavy shell's burst, the first a fuel
   * fire besides, and the slide's spray of dust and metal ahead along its fall (rendering/shipCrashLook.ts crashImpact).
   */
  private crashDown(e: Extract<ActionEvent, { kind: 'shipCrashed' }>): void {
    const state = this.o.match.state, planet = state.planets[e.planetIndex];
    if (!planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, onGround = (d: Vec3) => { const u = vecNormalize(d); return vecAdd(planet.position, vecScale(u, this.groundAt(planet, u))); };
    const pieces = e.wreckIds.map(id => state.structures.find(s => s.id === id)).filter((s): s is StructureState => !!s);
    const at = pieces.length ? pieces.map(s => onGround(s.surfaceDir)) : [onGround(e.at)];
    const up = vecNormalize(e.at), vel = this.fallen.get(e.id);
    this.fallen.delete(e.id);
    const along = vel && vel.lengthSq() > 0 ? vel : pieces[0] ? structureFrame(pieces[0], up).tz : up;
    const cam = this.o.camera.position, people = Math.hypot(at[0].x - cam.x, at[0].y - cam.y, at[0].z - cam.z) / person;
    this.explosions ??= new ExplosionField(this.o.scene, person);
    crashImpact(this.explosions, at, up, along, vel ? vel.length() : 40 * person, person, groundMaterial(state, e.planetIndex, up, this.o.match.actionTick * PHYS_DT), burstDetail(people), e.id * 13 + 1);
  }


  /**
   * 63.1d: a transport on the drawn ground along its heading, its front wheels steering, its ramp let down while it
   * stands (eased), its wheels turning; a wreck stands still. Presentation only.
   */
  private updateTransport(s: StructureState, m: VehicleModel, dt: number): void {
    const state = this.o.match.state, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const planet = state.planets[s.planetIndex];
    // 63.1d step 2: seated on the ground under its wheels — pitched and rolled — as its hit shape stands (core/vehicleFrame.ts)
    const f = vehicleFrame(planet, s, person, personSetOf(state), d => this.groundAt(planet, d));
    _by.set(f.up.x, f.up.y, f.up.z); _bz.set(f.front.x, f.front.y, f.front.z); _bx.set(f.left.x, f.left.y, f.left.z);
    m.root.position.set(f.foot.x, f.foot.y, f.foot.z);
    m.root.quaternion.setFromRotationMatrix(_bm.makeBasis(_bx, _by, _bz));
    // (63.1d's follow-up: the wreck is drawn as built — an update would set its gun and ramp back)
    if (this.vehicleForms.get(s.id) === 'wreck') { m.root.updateMatrixWorld(true); return; }
    const wrecked = s.health <= 0, speed = wrecked ? 0 : s.drive!.speed;
    const want = Math.abs(speed) < 0.05 * person ? 1 : 0;
    const ramp = this.rampDown.get(s.id) ?? want;
    const next = ramp + Math.max(-dt / TRANSPORT_LOOK.rampSeconds, Math.min(dt / TRANSPORT_LOOK.rampSeconds, want - ramp));
    this.rampDown.set(s.id, next);
    const yawRate = wrecked ? 0 : s.drive!.yawRate ?? 0;
    const steer = Math.max(-TRANSPORT_LOOK.steerMax, Math.min(TRANSPORT_LOOK.steerMax, -yawRate * TRANSPORT_LOOK.wheelbase / Math.max(1, Math.abs(speed) / person)));
    // step 3: the cupola laid where its gunner aims — its bearing off the hull's front, about the hull's up, and its pitch
    // (69.21 step 5: a launcher's too — its seat has no `guns`, and its tubes stood still while the gunner laid them)
    const gunner = state.players.find(p => p.alive && p.mount?.bodyId === s.id && (s.seats?.[p.mount.seat]?.guns?.length || s.seats?.[p.mount.seat]?.launcher));
    let aimYaw = 0, aimPitch = 0;
    if (s.type === 'tank') {
      // 69.29: the turret as the sim lays it (its yaw off the hull, at the turret's rates), the gun at its gunner's pitch
      const turretGunner = state.players.find(p => p.alive && p.mount?.bodyId === s.id && p.mount.seat === TANK.turretSeat);
      aimYaw = s.turretYaw ?? 0; aimPitch = turretGunner?.aimPitch ?? 0;
    } else if (gunner?.heading) {
      const h = gunner.heading, x = h.x * f.left.x + h.y * f.left.y + h.z * f.left.z, z = h.x * f.front.x + h.y * f.front.y + h.z * f.front.z;
      aimYaw = Math.atan2(x, z); aimPitch = gunner.aimPitch ?? 0;
    }
    updateVehicleModel(m, { speed: (speed / person) * PERSON_UNITS, steer, ramp: next, aimYaw, aimPitch, doors: 0, crewed: this.easeLids(s, dt) }, dt);
    m.root.updateMatrixWorld(true);
    if (m.mortar && s.mortar) { this.poseMortarView(s, m, f, dt); m.root.updateMatrixWorld(true); }
    if (m.tank) {
      // 69.29: the gun at its gunner's lay within its elevation (the turret at the sim's turretYaw, updateVehicleModel), the main gun's recoil
      m.cradle!.rotation.x = -Math.max(TANK_GUN_LOOK.elevation[0], Math.min(TANK_GUN_LOOK.elevation[1], aimPitch));
      const at = this.tankFiredAt.get(s.id); recoilBarrel(m.tank.barrel, at === undefined ? undefined : this.time - at);
      // 249: the cupola's .50 laid to its commander's aim (heading and pitch, as the sim fires it), in the turret's frame; at rest unmanned
      const ck = (s.seats ?? []).findIndex((q, k) => q.role === 'weapon' && k !== TANK.turretSeat), cu = ck >= 0 ? s.seats![ck].occupant : null;
      const cp = cu && 'unitId' in cu ? this.o.match.state.players.find(p => p.id === cu.unitId) : undefined;
      m.root.updateMatrixWorld(true);
      if (cp && m.turret) {
        const d = aimDirection(normalize(cp.surfaceDir), headingOf(cp), cp.aimPitch ?? 0);
        m.turret.getWorldQuaternion(_cupolaQ);
        poseCupola(m.tank.commander, _cupolaDir.set(d.x, d.y, d.z).applyQuaternion(_cupolaQ.invert()));
      } else poseCupola(m.tank.commander);
      m.root.updateMatrixWorld(true);
      // a blown engine smokes (a puff a fifth of a second from each of its deck's places)
      if (m.tank.smoke.length && this.time - (this.tankSmokeAt.get(s.id) ?? -Infinity) > 0.2) {
        this.tankSmokeAt.set(s.id, this.time);
        this.explosions ??= new ExplosionField(this.o.scene, person);
        const up = vecNormalize(s.surfaceDir);
        for (const a of m.tank.smoke) { const p = a.getWorldPosition(new THREE.Vector3()); this.explosions.emit('smoke', { at: p, up, vel: { x: up.x * 1.4 * person, y: up.y * 1.4 * person, z: up.z * 1.4 * person }, life: 3, s0: 0.25 * person, s1: 1.4 * person, c0: '#3a3632', c1: '#55504a', drag: 0.8 }); }
      }
    }
    // 69.21 step 5: a launcher technical's mount from its seat — the rack's spares (the store), the reload's swap (the spent tube
    // out and over the side, a fresh one from the rack to the clamp, the gunner's near hand on it)
    const launcher = m.mount ? s.seats?.find(q => q.launcher) : undefined;
    if (m.mount && launcher?.launcher) {
      const L = MOUNT_LAUNCHERS[launcher.launcher];
      poseMount(m.mount, { loaded: launcher.belt ?? L.magazine, store: launcher.spare ?? state.config.mountStore ?? L.store, ...(launcher.reloadLeft !== undefined ? { reload: 1 - launcher.reloadLeft / L.reload } : {}), seconds: L.reload });
      m.cradle?.updateMatrixWorld(true);
    }
  }

  /** 69.23: each mortar carrier's rig as drawn: its set share, its lay, the crew's hands (stable objects the rider reads) and the ticks seen. */
  private mortarViews = new Map<number, { set: number; lay: { traverse: number; tilt: number }; hands: { right: THREE.Object3D; left: THREE.Object3D }; shown: boolean; tick: number; pending: number[]; lastFire?: number }>();
  /**
   * 69.23 (the owner's picks: "Let's take the lean for mortar design (tube a, turntable, hatch b, crew of 1)"): a mortar
   * carrier's rig from its state — set over its mortarSetupSeconds from `stillSince` (packed again as it moves), the turntable and
   * the tube laid to the mission's lay (its rounds' flight, read off the hull's frame, eased), the loader's take, raise and drop
   * before each round's fire, the recoil and the duck after it, the shot's flash and smoke at the muzzle; the store in the
   * racks, the mission's rounds in the ready rack. Its crew is drawn at the rig while it is up (`rideOf`). Presentation only.
   */
  private poseMortarView(s: StructureState, m: VehicleModel, f: { left: Vec3; up: Vec3; front: Vec3 }, dt: number): void {
    const state = this.o.match.state, tick = this.o.match.actionTick, rig = m.mortar!, mo = s.mortar!;
    let v = this.mortarViews.get(s.id);
    if (!v) { v = { set: 0, lay: { traverse: 0, tilt: MORTAR_RIG.setTilt }, hands: { right: rig.sightKnob, left: rig.handwheel }, shown: false, tick, pending: [] }; this.mortarViews.set(s.id, v); }
    // set: the sim's clock since it stood still; moving, it packs (drawn back down over a couple of seconds)
    v.set = mo.stillSince !== undefined ? Math.min(1, (tick - mo.stillSince) * PHYS_DT / mortarSetupSeconds(state, s)) : Math.max(0, v.set - dt / 2);
    // the mission: its rounds from this carrier still to fire (the sim drops a round from its strike as it fires: the fire ticks
    // seen are kept, and one gone past its tick has fired), the next to fire and the last fired
    const rounds = (state.airstrikes?.inbound ?? []).filter(x => x.kind === 'mortar').flatMap(x => x.rounds.filter(r => r.gun === s.id && r.fireTick > tick)).sort((a, b) => a.fireTick - b.fireTick);
    const next = rounds[0], shots = v.pending.filter(t => t <= tick);
    if (shots.length) v.lastFire = Math.max(v.lastFire ?? -Infinity, ...shots);
    v.pending = rounds.map(r => r.fireTick);
    if (rounds.length) {
      const d = rounds[0].vel, want = mortarLayOf({ x: vecDot(d, f.left), y: vecDot(d, f.up), z: vecDot(d, f.front) });
      const turn = Math.atan2(Math.sin(want.traverse - v.lay.traverse), Math.cos(want.traverse - v.lay.traverse)), k = Math.min(1, dt * 2.5);
      v.lay = { traverse: v.lay.traverse + turn * k, tilt: v.lay.tilt + (want.tilt - v.lay.tilt) * k };
    }
    const ready = v.set >= 0.99 ? Math.min(MORTAR_RIG.ready, rounds.length ? rounds.length : mo.rounds) : 0;
    const last = v.lastFire !== undefined ? (tick - v.lastFire) * PHYS_DT : undefined;
    const pose = poseMortarCrew(rig, { set: v.set, traverse: v.lay.traverse, tilt: v.lay.tilt, rounds: mo.rounds + ready, ready, ...(next ? { next: (next.fireTick - tick) * PHYS_DT } : {}), ...(last !== undefined && last < 5 ? { last } : {}) });
    if (pose.hands) { v.hands.right = pose.hands.right; v.hands.left = pose.hands.left; }
    v.shown = v.set > 0.3;
    // the shot: a round whose fire came since the last frame — the flash and the smoke at the muzzle, out along the tube
    if (shots.length) {
      const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
      this.explosions ??= new ExplosionField(this.o.scene, person);
      rig.muzzle.updateWorldMatrix(true, false);
      const at = rig.muzzle.getWorldPosition(new THREE.Vector3()), out = new THREE.Vector3(0, 1, 0).applyQuaternion(rig.muzzle.getWorldQuaternion(new THREE.Quaternion())), up = vecNormalize(s.surfaceDir);
      this.explosions.emit('fireball', { at, up, life: 0.1, s0: 0.12 * person, s1: 0.4 * person, c0: '#ffd27a' });
      this.explosions.emit('smoke', { at, up, vel: { x: out.x * 1.6 * person, y: out.y * 1.6 * person, z: out.z * 1.6 * person }, life: 2.0, s0: 0.2 * person, s1: 1.1 * person, c0: '#d9d6cf', c1: '#c8c4bc', drag: 1.5 });
    }
    v.tick = tick;
  }

  /**
   * 69.29: a tank as the owner picked it (DECISIONS 247), its mobility kills drawn (`StructureState.disabled`, lane F's step 4):
   * the running gear's — the track thrown on the side the hit went through (`running`: +1 the hull's left, the model's +x) and
   * two road wheels off; the engine's — its deck blown, smoking (`m.tank.smoke`, the host's puffs).
   */
  private tankModel(s: StructureState, pal: UnitPalette, person: number, smoke: boolean): VehicleModel {
    const t = buildTank(pal, person, { ...TANK_PICKS, smoke, ...(this.armsDrawn() ? { ammoDoor: true } : {}) }), d = s.disabled;   // (69.31: its ammunition door with the dumps' arms)
    if (d) {
      damageTank(t, { ...(d.running ? { track: d.running } : {}), ...(d.engine ? { engine: true } : {}) }, pal);
    }
    return t.m;
  }
  /** 69.29: when each tank's main gun last fired (the host's clock): its barrel's recoil; when each disabled engine last puffed. */
  private tankSmokeAt = new Map<number, number>();
  /** 69.29: each tank's mobility kill as last built (`running`/`engine`). */
  private tankDamage = new Map<number, string>();
  private tankFiredAt = new Map<number, number>();
  /**
   * 69.29: the muzzle a tank's round leaves, by the firer's seat and the round — the main gun's (the explosive or the solid
   * round: its barrel's recoil starts), the coax beside it (the turret seat's bullet gun), the commander's gun at the cupola;
   * null when the firer is at no tank.
   */
  private tankMuzzle(unitId: number, bullet: { solid?: true; explosion?: string }): THREE.Object3D | null {
    const state = this.o.match.state, mount = state.players.find(p => p.id === unitId)?.mount;
    const s = mount ? state.structures.find(x => x.id === mount.bodyId) : undefined, m = s?.type === 'tank' ? this.bikeViews.get(s.id) : undefined;
    if (!mount || !m?.tank) return null;
    if (mount.seat !== TANK.turretSeat) return m.tank.commander.muzzle;
    if (bullet.solid || bullet.explosion === 'tankHe') { this.tankFiredAt.set(s!.id, this.time); return m.tank.muzzle; }
    return m.tank.coaxMuzzle;
  }

  /** 69.21 step 5: the tube a mounted rocket just left (its gunner's technical's, as drawn), or null when the firer is at none. */
  private launcherMuzzle(unitId: number): THREE.Object3D | null {
    const state = this.o.match.state, mount = state.players.find(p => p.id === unitId)?.mount;
    const seat = mount ? state.structures.find(x => x.id === mount.bodyId)?.seats?.[mount.seat] : undefined;
    const m = mount && seat?.launcher ? this.bikeViews.get(mount.bodyId) : undefined;
    return m?.mount && seat?.launcher ? firedMuzzle(m.mount, seat.belt ?? MOUNT_LAUNCHERS[seat.launcher].magazine) : null;
  }

  /**
   * 67.60's switch-on: a vehicle's hinges are live when the player's unit is in it or within `VEHICLE_LIVE_PEOPLE` of it
   * (its lids then swing open and shut with its crew); every other vehicle's are baked at rest, so the D look draws as
   * many as today's.
   */
  private vehicleLive(s: StructureState, person: number): boolean {
    const me = this.human();
    if (!me?.alive) return false;
    if (me.mount?.bodyId === s.id) return true;
    if (me.planetIndex !== s.planetIndex || !s.surfaceDir) return false;
    const planet = this.o.match.state.planets[me.planetIndex];
    return dist(normalize(me.surfaceDir), normalize(s.surfaceDir)) * planet.radius < VEHICLE_LIVE_PEOPLE * person;
  }

  /** 67.60: a live vehicle's crew lids eased toward their seats' crewing (about half a second open or shut). */
  private easeLids(s: StructureState, dt: number): Partial<Record<CrewRole, number>> {
    const want = vehicleCrew(s), had = this.lidShares.get(s.id) ?? want, next: Partial<Record<CrewRole, number>> = {};
    for (const r of ['driver', 'gunner', 'spotter'] as const) { const a = had[r] ?? 0, b = want[r] ?? 0; next[r] = a + Math.max(-dt * 2, Math.min(dt * 2, b - a)); }
    this.lidShares.set(s.id, next);
    return next;
  }

  /** 63.1f: the vehicle (one that drives) whose exposed gun `u` works from its hatch, its frame as drawn, and the gunner's feet there (the sim's `seatPos`). */
  private hatchGun(u: PlayerState): { s: StructureState; frame: VehicleFrame; feet: Vec3 } | null {
    if (!u.mount || !u.seatPos) return null;
    const state = this.o.match.state, s = state.structures.find(x => x.id === u.mount!.bodyId);
    if (!s || !drives(s) || !s.seats?.[u.mount.seat]?.guns?.length) return null;
    const planet = state.planets[s.planetIndex], ps = personSetOf(state);
    return { s, frame: vehicleFrame(planet, s, PERSON_BODY_HEIGHT * ps.k, ps, d => this.groundAt(planet, d)), feet: u.seatPos };
  }

  /**
   * 63.1f: a seated bullet gun's crosshair converges where the gun's line first meets something — a person, the drawn
   * ground, a prop, another hull; its own hull and what is within arm's reach (67.32) looked past — as on foot (67.10). The
   * camera rides off the gun, so a stale range (the last one on foot) put the rounds beside the crosshair at every other
   * range. A seat without a bullet gun keeps the range it had, and draws no dot.
   *
   * 63.1j (the owner: "you don't get the actual bullet drop indicator on hmg"): and the dot where its round lands — the gun's
   * round (its speed, its life, the bullets' fall, the wind's share) flown from the muzzle along the gun's line as the sim
   * flies it, through the same hits — as on foot; none when the round meets nothing in its life.
   */
  private seatAim(u: PlayerState): void {
    const state = this.o.match.state, s = state.structures.find(x => x.id === u.mount!.bodyId);
    const guns = s?.seats?.[u.mount!.seat]?.guns;
    this.launcherAim = false;   // (a seat's gun: bullets — 65.45's impact marker is a launcher's)
    if (s && s.seats?.[u.mount!.seat]?.launcher === 'rocket') { this.rocketSeatAim(u, s); return; }   // 69.56: the rocket technical's tubes
    if (!s || !guns?.length) { this.dot = null; this.assist = null; this.gunAimPitch = null; this.aimYaw = 0; return; }
    const planet = state.planets[u.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const own = drives(s) ? vehicleFrame(planet, s, person, ps, d => this.groundAt(planet, d)) : null;
    // 69.54: a tank's turret — the search from the barrel's muzzle (69.55: where its round leaves) along the player's aim, the
    // shot's dot from it along the gun as it lies now: where its round lands while the turret slews to the aim
    const main0 = s.seats?.[u.mount!.seat]?.main, aimH = main0 ? this.turretAimOf(u) : null;
    const muzzle = main0 ? tankMuzzle(s, planet, ps, person, u.aimPitch ?? 0, 'main') : muzzlePoint(u, state.planets, ps.eyeHeight, ps);
    const ray = aimDirection(normalize(u.surfaceDir), aimH ?? headingOf(u), this.pitch);   // the sim's own line (core/bullets.ts fireBullet)
    const hit = (p: Vec3): AimHit => {
      for (const o of state.players) {
        if (aimRayStopsOn(u, o, state.config) && pointInPerson(p, o, state.planets, ps, state.config.measuredBodies === true)) return o.teamIndex === u.teamIndex ? 'friend' : 'person';
      }
      const rel = sub(p, planet.position);
      if (len(rel) - this.groundAt(planet, normalize(rel)) < 0) return 'ground';
      if (own && inHull(p, s.type, own, person)) return null;
      return pointBlocked(p, u.planetIndex, state, own ? undefined : { kind: 'structure', id: s.id }) ? 'prop' : null;   // (65.22 (1): a fixed gun fires over its own ring)
    };
    // (69.29 step 3b: a tank's turret — the main gun's round, the one in it or the one asked for: the coax is its second trigger)
    const main = s.seats?.[u.mount!.seat]?.main, gunSpec = main ? tankRoundSpec(ps, main.loaded ?? this.tankRound) : mountedGunSpec(ps, guns[0]);
    const reach = Math.max(AIM_FINE_PERSONS * person, roundReach(gunSpec));   // 65.45 (a): out to the gun's reach
    const found = this.aimSearch(u, muzzle, ray, reach, aimNearOn() ? AIM_NEAR_PERSONS * person : 0, hit);
    this.converge = Math.max(AIM_CONVERGE_MIN_PERSONS * person, Math.min(reach, found.range));
    this.seatRange = found.target ? found.range : null;
    const spec = gunSpec;
    const wind = this.windAlong(u.planetIndex, ACTION_WIND.bullet);   // 65.25e: the wind along the round's flight
    // 65.25a: a seen enemy within the assist cone of the lay gets the gun's elevation (its round's drop); the lay is the view
    this.assist = this.assistFor(u, muzzle, ray, roundReach(spec));   // (65.30: as far as it flies under its drag)
    let lay = ray, want = { pitch: 0, yaw: 0 };
    const n = normalize(u.surfaceDir);
    if (this.assist) {
      const lead = this.leadFor(u, muzzle, this.assist, spec.speed, ACTION_WIND.bullet, spec.life, shotFall(spec, ps.bulletGravityScale), spec.drag ?? 0);   // (cut 2: across, the lead and the wind)
      const g = scale(gravityAccel(scale(add(muzzle, lead.at), 0.5), state.planets), shotFall(spec, ps.bulletGravityScale) * ps.gravityScale);
      const p = solveAimPitchDrag(muzzle, lead.at, n, spec.speed, spec.drag ?? 0, g, { life: spec.life, fall: shotFall(spec, ps.bulletGravityScale), gravityScale: ps.gravityScale, planets: state.planets, stepLength: 0.25 * ps.k });   // 65.30: under its round's drag
      if (p !== null) want = { pitch: Math.max(-MAX_PITCH, Math.min(MAX_PITCH, p)) - this.pitch, yaw: lead.yaw };
    }
    // 65.25f: the assist's correction over the lay eased, as on foot (the lay itself, the mouse's, passes straight through)
    const ease = this.easeAssistBy(want, Math.min(0.1, Math.max(0, this.time - this.assistEase.t)));
    const eased = Math.abs(ease.pitch) > 1e-6 || Math.abs(ease.yaw) > 1e-6;
    this.gunAimPitch = eased ? Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.pitch + ease.pitch)) : null;
    this.aimYaw = eased ? ease.yaw : 0;
    if (eased) lay = aimDirection(n, this.aimYaw ? turnHeading(headingOf(u), n, this.aimYaw) : headingOf(u), this.gunAimPitch!);
    if (main0) lay = aimDirection(n, headingOf(u), u.aimPitch ?? 0);   // (69.54: the main gun's round goes where the gun lies; its lay is no assist's)
    const shot = predictShot(muzzle, scale(lay, spec.speed), { drag: spec.drag, life: spec.life, fall: shotFall(spec, ps.bulletGravityScale), gravityScale: ps.gravityScale, planets: state.planets, blocked: p => hit(p) !== null, stepLength: 0.25 * ps.k, wind, windStep: windAimStep(spec.speed, person) });
    if (shot.hit) this.placeDot(shot.at); else this.dot = null;
  }

  /**
   * 69.56 (DECISIONS 264 (3), the owner: "make aiming better for rocket truck"): the rocket technical's gunner aims as the LAW
   * on foot does (65.45 (1)) — the rocket's pitch solved so it lands where the crosshair rests (its fall over the flight: the
   * tubes sent the look's pitch, so every rocket dropped short of what the crosshair was on), and its landing drawn as the
   * impact marker. No assist (a launcher's never pulls onto a body); `direct` aim keeps the look's pitch, the marker showing
   * the drop. The search and the flight from where the sim fires it (the gunner's muzzle point, match.ts fireBullet).
   */
  private rocketSeatAim(u: PlayerState, s: StructureState): void {
    const state = this.o.match.state, planet = state.planets[u.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const n = normalize(u.surfaceDir), heading = headingOf(u);
    const own = drives(s) ? vehicleFrame(planet, s, person, ps, d => this.groundAt(planet, d)) : null;
    const muzzle = muzzlePoint(u, state.planets, ps.eyeHeight, ps), ray = aimDirection(n, heading, this.pitch);
    const hit = (p: Vec3): AimHit => {
      for (const o of state.players) {
        if (aimRayStopsOn(u, o, state.config) && pointInPerson(p, o, state.planets, ps, state.config.measuredBodies === true)) return o.teamIndex === u.teamIndex ? 'friend' : 'person';
      }
      const rel = sub(p, planet.position);
      if (len(rel) - this.groundAt(planet, normalize(rel)) < 0) return 'ground';
      if (own && inHull(p, s.type, own, person)) return null;
      return pointBlocked(p, u.planetIndex, state, own ? undefined : { kind: 'structure', id: s.id }) ? 'prop' : null;
    };
    const spec = weaponSpec(ps, 'rocket', state.config), fall = shotFall(spec, ps.bulletGravityScale);
    const reach = Math.max(AIM_FINE_PERSONS * person, roundReach(spec));
    const found = this.aimSearch(u, muzzle, ray, reach, aimNearOn() ? AIM_NEAR_PERSONS * person : 0, hit);
    this.converge = Math.max(AIM_CONVERGE_MIN_PERSONS * person, Math.min(reach, found.range));
    this.seatRange = found.target ? found.range : null;
    this.xh.friend = found.friend;
    this.assist = null; this.aimYaw = 0; this.launcherAim = true;
    let pitch: number | null = null;
    if (this.aim === 'solved' && found.target && dist(found.target, muzzle) <= roundReach(spec)) {
      const g = scale(gravityAccel(scale(add(muzzle, found.target), 0.5), state.planets), fall * ps.gravityScale);
      const p = solveAimPitchDrag(muzzle, found.target, n, spec.speed, spec.drag ?? 0, g, { life: spec.life, fall, gravityScale: ps.gravityScale, planets: state.planets, stepLength: 0.25 * ps.k, lob: fall > 0 });
      if (p !== null) pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, p));
    }
    this.gunAimPitch = pitch;
    this.inReach = !found.target || this.aim !== 'solved' || pitch !== null;
    const wind = this.windAlong(u.planetIndex, windShare(spec, state.config));
    const shot = predictShot(muzzle, scale(aimDirection(n, heading, pitch ?? this.pitch), spec.speed), { drag: spec.drag, life: spec.life, fall, gravityScale: ps.gravityScale, planets: state.planets, blocked: p => hit(p) !== null, stepLength: 0.25 * ps.k, wind, windStep: windAimStep(spec.speed, person) });
    if (shot.hit) this.placeDot(shot.at); else this.dot = null;
  }

  /**
   * 65.25a: the enemy body the shot's solution is for (game/aimTarget.ts assistTarget): within the Lab's cone of the line
   * from `muzzle` along `ray`, in the round's reach, and one the player sees — the bots' look's tests from the player's eye
   * (the weather's sight, the ground, cover, crowns), so the assist never finds a body the player cannot see. Its middle, or
   * its head where a rise hides the middle (on the range's rolling ground the middles at 9 and 12 people were hidden and the
   * heads in plain view): the solution is for the point seen.
   */
  private assistFor(u: PlayerState, muzzle: Vec3, ray: Vec3, reach: number): { id: number; at: Vec3 } | null {
    const deg = aimAssistDeg();
    if (!(deg > 0)) return null;
    const state = this.o.match.state, ps = personSetOf(state), seconds = this.o.match.actionTick * PHYS_DT;
    const eye = assistEye(u, personEyePoint(u, state.planets, ps, ps.losEyeHeight), muzzle);   // 65.25b: seated, from the gun's muzzle
    const sight = weatherEffectHere(state, u.planetIndex, u.surfaceDir, seconds).sight * ps.k;
    const bodies = assistBodies(state, u, ps);   // 65.39: the seated gunners where they show too
    const seen = (at: Vec3, p: PlayerState) => assistSees(state, u.planetIndex, eye, at, p, seconds, sight, ps.coverSampleStep);
    // (its middle or its head, the visible one nearest the crosshair's line — 65.25c: a head over a crest wins, not the ridge)
    // 65.25f: the pick held through a ridge's flicker (aimTarget.ts steadyAssistTarget)
    const { pick: found, hold } = steadyAssistTarget(this.assistHold, muzzle, ray, bodies, (deg * Math.PI) / 180, reach, (b, point) => seen(point, b.p), this.time);
    this.assistHold = hold;
    return found ? { id: found.id, at: found.at } : null;
  }

  /**
   * 65.25a cut 2: the aim point and the yaw for the assisted body — where it will be when a round at `speed` arrives (its
   * motion as watched from frame to frame, smoothed), less the wind's drift over the flight (`windShare` of the wind: the
   * bullet's or the rocket's) — the yaw across the ground from the heading to it, clamped to AIM_YAW_MAX (the Match
   * clamps it again). 65.25e: the drift is solved on the round's real flight (aimTarget.ts windAimPoint: its `life` and
   * `fall`, the wind where it flies), the first-order lead only where that is out of reach; and the yaw is no longer held to
   * the cone (2° by default), which chooses the body, not how far the wind may carry the round.
   */
  private leadFor(u: PlayerState, muzzle: Vec3, assist: { id: number; at: Vec3 }, speed: number, windShare: number, life: number, fall: number, drag = 0): { at: Vec3; yaw: number } {
    const state = this.o.match.state, was = this.assistMotion.get(assist.id);
    let v = { x: 0, y: 0, z: 0 };
    if (was && this.time - was.t > 1e-3 && this.time - was.t < 0.5) {
      const dt = this.time - was.t, k = 0.35;
      const now = { x: (assist.at.x - was.at.x) / dt, y: (assist.at.y - was.at.y) / dt, z: (assist.at.z - was.at.z) / dt };
      v = { x: was.v.x + (now.x - was.v.x) * k, y: was.v.y + (now.y - was.v.y) * k, z: was.v.z + (now.z - was.v.z) * k };
    }
    if (!was || this.time - was.t > 1e-3) this.assistMotion.set(assist.id, { at: assist.at, t: this.time, v });
    if (this.assistMotion.size > 16) for (const id of this.assistMotion.keys()) if (id !== assist.id) this.assistMotion.delete(id);
    const n = normalize(u.surfaceDir), moving = leadPoint(muzzle, assist.at, v, speed, undefined, drag);
    const field = this.windAlong(u.planetIndex, windShare), ps = personSetOf(state);
    let at = moving;
    if (field) {
      // (the solve at WIND_AIM_EVERY_S for this body and this round; between, its wind offset on the body's motion)
      const c = this.windAim;
      if (c && c.id === assist.id && c.share === windShare && this.time - c.t >= 0 && this.time - c.t < WIND_AIM_EVERY_S) at = add(moving, c.offset);
      else {
        const flight: WindFlight = { speed, drag, life, fall, gravityScale: ps.gravityScale, planets: state.planets, stepLength: 0.25 * ps.k, wind: field, windStep: windAimStep(speed, PERSON_BODY_HEIGHT * ps.k) };
        at = windAimPoint(muzzle, moving, n, flight) ?? leadPoint(muzzle, assist.at, v, speed, actionWindAccel(state, u.planetIndex, muzzle, windShare) ?? undefined, drag);
        this.windAim = { id: assist.id, share: windShare, t: this.time, offset: sub(at, moving) };
      }
    }
    const yaw = Math.max(-AIM_YAW_MAX, Math.min(AIM_YAW_MAX, yawToward(n, headingOf(u), sub(at, muzzle))));
    return { at, yaw };
  }

  /**
   * 65.25e: the wind's pull along a round's flight — at the point it has reached, that many seconds on (`windShare`: the
   * weapon's, ACTION_WIND) — as core/bullets.ts flies it; null with the wind switch off.
   */
  private windAlong(planetIndex: number, windShare: number): ((p: Vec3, t: number) => Vec3 | null) | null {
    const state = this.o.match.state;
    if (!state.config.wind) return null;
    const t0 = windSeconds(state);
    return (p, t) => actionWindAccel(state, planetIndex, p, windShare, t0 + t);
  }

  /** 65.25f: the assist's correction one frame on toward `want` (aimTarget.ts easeAssist), kept with the time. */
  private easeAssistBy(want: { pitch: number; yaw: number }, dt: number, capped = true): { pitch: number; yaw: number } {
    const e = easeAssist(this.assistEase, want, dt, capped);
    this.assistEase = { ...e, t: this.time };
    return e;
  }

  /** 65.22: does the human's seat fire bullets (a mounted gun: the HMG's, the cupola's)? */
  /** 69.21: the player at a mounted launcher (the rocket or missile technical's) — its kind, the rounds loaded, the store's, a reload under way; else null. */
  private seatLauncher(): { kind: 'rocket' | 'missile'; loaded: number; store: number; reloading: boolean } | null {
    const me = this.human();
    if (!me?.mount) return null;
    const state = this.o.match.state, seat = state.structures.find(s => s.id === me.mount!.bodyId)?.seats?.[me.mount.seat];
    if (!seat?.launcher) return null;
    const L = MOUNT_LAUNCHERS[seat.launcher];
    return { kind: seat.launcher, loaded: seat.belt ?? L.magazine, store: seat.spare ?? state.config.mountStore ?? L.store, reloading: seat.reloadLeft !== undefined };
  }
  /** 69.23: the player at a mortar carrier's tube — the line's words: set, setting up (seconds left) or packed (moving); firing a mission; the rounds left; else null. */
  private seatMortar(): string | null {
    const me = this.human();
    if (!me?.mount) return null;
    const state = this.o.match.state, tick = this.o.match.actionTick, s = state.structures.find(x => x.id === me.mount!.bodyId);
    if (!s?.mortar || !s.seats?.[me.mount.seat]?.mortar) return null;
    const firing = (state.airstrikes?.inbound ?? []).some(x => x.kind === 'mortar' && x.rounds.some(r => r.gun === s.id && r.fireTick > tick));
    const since = s.mortar.stillSince, left = since === undefined ? null : Math.max(0, mortarSetupSeconds(state, s) - (tick - since) * PHYS_DT);
    const status = s.mortar.rounds <= 0 ? 'out of rounds' : firing ? 'firing a mission' : left === null ? 'packed (moving)' : left > 0 ? `setting up ${left.toFixed(0)} s` : 'set — ready for a call';
    return `At the mortar: ${status} · ${s.mortar.rounds} rounds · it fires on a squad leader's call (the calls: Mortar here)`;
  }
  private seatGuns(): boolean {
    const me = this.human();
    if (!me?.mount) return false;
    return !!this.o.match.state.structures.find(s => s.id === me.mount!.bodyId)?.seats?.[me.mount.seat]?.guns?.length;
  }

  /** X31: a rider's anchors on the bike it is mounted on (drawn this frame), or undefined — 63.1d: a transport's driver and cupola gunner at their hatches (its bay riders are inside, unseen). */
  private rideOf(p: PlayerState): PersonRide | undefined {
    if (!p.mount) return undefined;
    const hmg = this.hmgViews.get(p.mount.bodyId);   // 65.22: sitting behind the gun, hands on its grips
    if (hmg && !hmg.wreck) return { seat: hmg.m.seat, right: hmg.m.gripR, left: hmg.m.gripL, pose: 'ground' };
    const pit = this.pitRide(p.mount.bodyId);   // 63.3's follow-up: in the gun pit's chair, hands on the handwheel
    if (pit) return pit;
    const m = this.bikeViews.get(p.mount.bodyId);
    if (m && m.kind !== 'motorcycle') {
      // 67.63: only the gunner is up — standing in its hatch (or the technical's bed), the hands on the .50's grips; the rest
      // sit inside, not drawn (`insideHull`)
      const seat = this.o.match.state.structures.find(s => s.id === p.mount!.bodyId)?.seats?.[p.mount.seat], role = seat?.role;
      // 69.29: the tank's commander at the cupola's gun; 249: its turret gunner inside (`insideHull`)
      if (role === 'weapon' && m.tank) return p.mount.seat !== TANK.turretSeat ? { seat: m.tank.commander.seat, right: m.tank.commander.hands.right, left: m.tank.commander.hands.left, pose: 'hatch' } : undefined;
      if (role === 'weapon' && m.crew.gunner && m.hands.gunner) return { seat: m.crew.gunner, right: m.hands.gunner.right, left: m.hands.gunner.left, pose: 'hatch' };
      // 69.23: the mortar's crew at its tube while it is up (the sight and the handwheel, the round in hand): the rig's anchors
      const mv = seat?.mortar && m.mortar ? this.mortarViews.get(p.mount.bodyId) : undefined;
      if (mv?.shown) return { seat: m.mortar!.crew, right: mv.hands.right, left: mv.hands.left, pose: 'hatch' };
      return undefined;
    }
    const seat = m?.crew.driver, bars = m?.bars;
    return seat && bars ? { seat, right: bars.right, left: bars.left } : undefined;
  }

  /**
   * 63.1f's follow-up (the owner in a gun pit: "I can't figure out how to turn the cannon left / right"): each standing gun
   * pit's turret drawn as the sim lays it (`turretYaw`: its gunner's bearing, the shield's shape turned with it) — the
   * pit's own (each form under its drawn group) and its gun-layer's anchors, which ride the turret (the sim swings the
   * gun-layer with it: SEAT_POINTS' pivot). Each turret laid by layTurret in its parent's frame. Reads state only.
   */
  private layPits(): void {
    const state = this.o.match.state;
    for (const s of state.structures) {
      if (s.type !== 'emplacement' || s.health <= 0) continue;
      const group = this.o.structureGroup?.(s.id);
      if (!group) continue;
      // the turret as the sim has it (its `turretYaw`: the shield's shape stands so) — its gunner's bearing while crewed,
      // where it was left after
      if (state.players.some(p => p.alive && p.mount?.bodyId === s.id)) this.pitRide(s.id);   // (its anchors under the group first, so they turn with it)
      const yaw = s.turretYaw ?? 0, { tx, tz } = uprightFrame(vecNormalize(s.surfaceDir)), c = Math.cos(yaw), sn = Math.sin(yaw);
      const heading = { x: tx.x * sn + tz.x * c, y: tx.y * sn + tz.y * c, z: tx.z * sn + tz.z * c };
      group.traverse(o => { if (o.name === 'turret') layTurret(o, heading); });
      group.updateMatrixWorld(true);
    }
  }

  /**
   * 63.3's follow-up: a gun pit's gun-layer as drawn — its chair beside the breech (the sim seats it there, core/seats.ts
   * satSeatPos), hands on the handwheel: anchors from lane E's model with its meshes stripped, hung under the pit's drawn
   * group (renderer.ts places and seats it; 63.1f's follow-up: its turret turns with the gunner's bearing, layPits), so they
   * ride wherever it is drawn.
   */
  private pitRide(id: number): PersonRide | undefined {
    const s = this.o.match.state.structures.find(x => x.id === id);
    if (!s || s.type !== 'emplacement' || s.health <= 0) return undefined;
    const group = this.o.structureGroup?.(id);
    if (!group) return undefined;
    let rig = this.pitSeats.get(id);
    if (!rig || rig.group !== group) {
      const em = buildEmplacementModel(unitPaletteFor(TEAMS[0]), PERSON_BODY_HEIGHT * personSetOf(this.o.match.state).k);
      const meshes: THREE.Object3D[] = [];
      em.root.traverse(o => { if ((o as THREE.Mesh).isMesh) meshes.push(o); });
      for (const m of meshes) { m.parent?.remove(m); (m as THREE.Mesh).geometry.dispose(); }
      em.silhouette.parent?.remove(em.silhouette);
      const feet = new THREE.Object3D(); feet.position.set(0, -0.55, 0); em.seat.add(feet);   // a seated pose's hips 0.55 above its root (design_sheets/crew.ts)
      if (rig) rig.group.remove(rig.root);   // a rebuilt drawn group: the anchors move to it
      group.add(em.root);
      rig = { group, root: em.root, ride: { seat: feet, right: em.wheelGrips[0], left: em.wheelGrips[1], pose: 'chair' } };
      this.pitSeats.set(id, rig);
    }
    return rig.ride;
  }

  /** 63.1f's follow-up: seated in a gun pit — its gun's bearing against the pit's front (` (40° right)`), else nothing. */
  private pitBearing(): string {
    const me = this.human(), t = me ? pitTraverse(this.o.match.state, me) : null;
    return t ? ` (${t === 'ahead' ? 'laid ahead' : t})` : '';
  }

  /** 63.1d: inside a transport's bay — not drawn (its people ride unseen). 63.1o: a bay by its seat's entry, not the 6×6 by name. */
  private inBay(p: PlayerState): boolean {
    if (!p.mount) return false;
    const s = this.o.match.state.structures.find(x => x.id === p.mount!.bodyId);
    return !!s && !!s.seats?.[p.mount.seat] && BODIES[s.type as BodyKey]?.seats[p.mount.seat]?.entry === 'bay';
  }
  /** 67.63: inside a hull (a driver, a spotter: every seat of a vehicle but the gunner's, the bike's apart) — its lid shut, not drawn. */
  private insideHull(p: PlayerState): boolean {
    if (!p.mount) return false;
    const m = this.bikeViews.get(p.mount.bodyId);
    if (!m || m.kind === 'motorcycle') return false;
    const seat = this.o.match.state.structures.find(x => x.id === p.mount!.bodyId)?.seats?.[p.mount.seat];
    if (seat?.mortar && this.mortarViews.get(p.mount.bodyId)?.shown) return false;   // (69.23: up at the tube while the mortar is set)
    if (m.tank && p.mount.seat === TANK.turretSeat) return true;   // (249: the tank's turret gunner rides inside)
    return seat?.role !== 'weapon';
  }
  /** 63.1o: seated in a closed cab (the technical's driver and passenger: the model's hidden crew) — not drawn. */
  private inCab(p: PlayerState): boolean {
    if (!p.mount) return false;
    const m = this.bikeViews.get(p.mount.bodyId), role = this.o.match.state.structures.find(x => x.id === p.mount!.bodyId)?.seats?.[p.mount.seat]?.role;
    const crew = role === 'driver' ? 'driver' : role === 'passenger' ? 'passenger' : role === 'weapon' ? 'gunner' : null;
    return !!m && !!crew && !!m.hiddenCrew?.includes(crew);
  }

  /** X33: a unit's body falls where it died — a ragdoll or a canned topple — and becomes a corpse. */
  private presentDeath(p: PlayerState, pv: PersonView): void {
    const state = this.o.match.state, planet = state.planets[p.planetIndex];
    if (!planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const c = new THREE.Vector3(planet.position.x, planet.position.y, planet.position.z);
    const feet = pv.holder.position.clone();
    const world = {
      up: feet.clone().sub(c).normalize(),
      heightAbove: (q: THREE.Vector3) => { const rel = q.clone().sub(c), r = rel.length(); rel.divideScalar(r); return r - this.groundAt(planet, { x: rel.x, y: rel.y, z: rel.z }); },
      personHeight: person,
    };
    const why = this.deathCauses.get(p.id) ?? { push: { kind: 'collapse' as const }, cause: 'other' as const };
    this.deathCauses.delete(p.id);
    if (p.id === this.controlled) this.deathSpot = { id: p.id, feet: { x: feet.x, y: feet.y, z: feet.z }, heading: headingOf(p) };   // 69.81: where the camera stays
    (this.deaths ??= new DeathPresenter(this.o.scene, corpseConfig(), LEAN_RENDER_EVERY > 1)).onDeath(p, pv, why.push, why.cause, world, this.o.match.actionTick * PHYS_DT);
  }

  private updateUnitViews(dt: number): void {
    const { match, playerMeshes } = this.o;
    const planets = match.state.planets;
    for (const p of match.state.players) {
      const pm = playerMeshes.get(p.id);
      // human scale: the E2 person stands in for the classic model (which stays the giant class, ruling 24)
      const pv = this.personView(p);
      if (pv && pm) { pm.group.visible = false; skipWhenHidden(pm.group); }   // (62.64 (b): hidden, the draw's matrix walk leaves it)
      // X33: a death — the body falls from the pose the view last drew (before the view hides it); the unit respawns as before
      if (pv && this.wasAlive.get(p.id) && !p.alive) this.presentDeath(p, pv);
      this.wasAlive.set(p.id, p.alive);
      if (!p.alive) { if (pv) pv.holder.visible = false; else if (pm) pm.group.visible = false; continue; }
      const n = normalize(p.airborne && p.airbornePos ? sub(p.airbornePos, planets[p.planetIndex].position) : p.surfaceDir);
      const pos = p.airborne && p.airbornePos ? p.airbornePos : this.feetDrawn(p, planets);
      const heading = headingOf({ surfaceDir: n, heading: p.heading });
      if (pv) {
        // 65.8: a medic poses the bandage from the patient's timer
        const patient = p.treating !== undefined ? match.state.players.find(q => q.id === p.treating) : undefined;
        const treat = patient?.healLeft !== undefined ? { left: patient.healLeft, total: patient.healTime ?? BANDAGE.seconds } : undefined;
        const inPod = (p.landingLeft ?? 0) > 0 || !!this.landers?.hidden(p.id);   // 125: still in the pod (69.3c: the sim's fall)
        pv.crowd = inPod ? null : (this.crowd ??= new SilhouetteCrowd(this.o.scene));
        const ride = this.rideOf(p);
        pv.coverFace = this.coverFaceOf(p);   // 66.7 (a)'s rework: the face the pressed poses lean into
        pv.ground = this.groundProbe(planets[p.planetIndex]);   // 67.60: the drawn ground the feet stand on
        pv.rockets = carriesRocket(p, match.state.config);   // S7: who carries a LAW (and its spare) — its side's tube
        pv.tube = carriesMissile(p) ? 'missile' : carriesPanzerfaust(p) ? 'panzerfaust' : 'rocket';   // 65.43: the Panzerfaust in the LAW's place, drawn as itself (65.49: the anti-dropship missile's launcher too)
        pv.tool = match.state.config.entrench === true;     // 66.8: who carries the entrenching tool (everyone, with entrench on)
        pv.matchSeconds = match.actionTick * PHYS_DT;        // 66.8: the dig's strokes on the match's clock, with its sounds
        pv.spentSeconds = this.spentDials.seconds; pv.spentMost = this.spentDials.most;   // S7's dials: how long a spent tube lies, how many
        pv.setOwn(p.id === this.controlled);                 // 65.36: the player's own gun moves (its slide or bolt, the reload); every other is baked still
        pv.update(p, pos, n, heading, dt, this.o.camera, window.innerHeight, p.airborne || ride ? undefined : this.slopeAt(planets[p.planetIndex], n, heading), treat, ride);
        if (inPod || p.boarded || this.inBay(p) || this.inCab(p) || this.insideHull(p)) pv.holder.visible = false;   // (67.63: what is enclosed is not drawn; 208: nor one in the wave ship)
        continue;
      }
      if (!pm) continue;
      pm.group.visible = true;
      pm.group.position.set(pos.x, pos.y, pos.z);
      // Basis: up = surface normal, the model's front (−Z after its built-in flip) along the heading
      const y = new THREE.Vector3(n.x, n.y, n.z);
      const z = new THREE.Vector3(-heading.x, -heading.y, -heading.z);
      const x = new THREE.Vector3().crossVectors(y, z);
      pm.group.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(x, y, z));
      pm.model.group.rotation.y = Math.PI;

      // Animation: walk while moving on the ground, jump / airborne / landing around hops
      const ctx = pm.animCtx;
      const last = this.lastPos.get(p.id);
      const moving = !!last && dist(last, pos) > 0.2 * dt;
      this.lastPos.set(p.id, pos);
      const wasAir = this.wasAirborne.get(p.id) ?? false;
      if (p.airborne && !wasAir) ctx.state = 'airborne_jump';
      else if (!p.airborne && wasAir) ctx.state = 'landing';
      else if (!p.airborne && ctx.state !== 'landing') ctx.state = moving ? 'waddle' : (ctx.state === 'waddle' ? 'idle' : ctx.state);
      this.wasAirborne.set(p.id, p.airborne);
      ctx.aimTracking = true;
      ctx.aimElevation = p.aimPitch ?? 0;
      if (p.airborne && p.airborneVel) ctx.airborneSpeed = len(p.airborneVel);
      updateAnimation(pm.model, ctx, dt);
    }
  }

  private personViews = new Map<number, PersonView>();
  /** 67.18: gunfire heard from the camera (shots, impacts, near misses); silent while fast-forwarding */
  private readonly sound: ActionSound;
  /** X33: deaths fall (ragdolls, a budget of them) and lie as corpses; made on first use at a person scale */
  private deaths: DeathPresenter | null = null;
  /** 69.81: where the player's own unit fell (its feet as drawn, world) and the way the camera looks there (the mouse turns it) */
  private deathSpot: { id: number; feet: Vec3; heading: Vec3 } | null = null;
  /** X33: what killed whom this frame, from the tick's events (read when the unit's death shows in the units pass) */
  private readonly deathCauses = new Map<number, { push: DeathPush; cause: DeathCause }>();
  /** X33: who was alive last frame (a death is the switch) */
  private readonly wasAlive = new Map<number, boolean>();
  /** 67.17: the far tier's instanced batches (one draw per team and pose), made on first use */
  private crowd: SilhouetteCrowd | null = null;
  /** 125: arrivals come down in pods (rendering/lander.ts), made on first use */
  private landers: Landers | null = null;
  /**
   * 67.70 (DECISIONS 209 (2); before, 68.39's strike column and 69.3b's landing ring): the side's calls on the ground — the one
   * being aimed (its rounds' circle, its landing box and room, the pods' ring) and each made until it comes down, with its
   * countdown and a column to find it from afar (the caller's side only: fog). ui/callMarks.ts gives the shapes.
   */
  private callMarks: CallMarksView | null = null;
  /** The aimed call's shape, asked again only when the call or its point changes (or every `CALL_AIM_EVERY` s: a call spent, a picture newer). */
  private callAim: { key: string; at: number; shape: CallShape | null } | null = null;
  /** per bullet: the drawn muzzle's offset from the sim's start, blended away as it flies (muzzleBlend) */
  private bulletStarts = new Map<number, Vec3>();
  /** The E2 person for a unit at human scale (k_human < 1), made on first use; none at classic. */
  private personView(p: PlayerState): PersonView | null {
    const k = personSetOf(this.o.match.state).k;
    if (k >= 1) return null;
    let v = this.personViews.get(p.id);
    // 68.3b: the squad leader wears lane E's leader kit (68.8) — a role, so a view whose role changed is built again
    const lead = this.leaders.has(p.id);
    // 185 / 67.56: the vest it was issued drawn (the plate carrier, light / heavy; never for wear), and the helmet (drawnHelmet:
    // the light one in a match without armour, else the one it wears or a balaclava) — a kind that changed builds it again
    const armor = p.armour?.vest?.kind === 'light' || p.armour?.vest?.kind === 'heavy' ? p.armour.vest.kind : 'none';
    const helmet = drawnHelmet(this.o.match.state.config.armour === true, p.armour);
    const primary = primaryOf(p), sidearm = sidearmOf(p);   // 65.43: a classic gun drawn as itself (a changed loadout builds the view again)
    const role = p.role ?? null;   // 201 (8): the medic's or the engineer's kit
    if (v && (v.leader !== lead || v.armor !== armor || v.helmet !== helmet || v.primary !== primary || v.sidearm !== sidearm || v.role !== role)) { v.dispose(); this.personViews.delete(p.id); v = undefined; }
    if (!v) { v = new PersonView(this.o.scene, unitPaletteFor(TEAMS[p.teamIndex % TEAMS.length]), PERSON_BODY_HEIGHT * k, primary, lead, armor, helmet, sidearm, role); this.personViews.set(p.id, v); }   // 65.13 / 65.14: drawn with their own long gun (65.43: a classic one as its kin)
    v.setHand(this.handFor(p.id));
    return v;
  }

  /** 67.21: a unit's strong hand — the player's for the unit they control, the match seed's for every other. Presentation only. */
  /**
   * 66.7 (a): the cover the unit is in, and a cover within its reach (ui/coverCue.ts) — the sim's answer, from where lane
   * C's split puts it; none until that lands, so neither the camera nor the HUD moves today.
   */
  /** 66.7 (a): the cover `u` holds (the Match's PlayerState.cover) as the HUD's bracket reads it — the side, and out or down. */
  private coverOf(u: PlayerState): CoverHeld | null {
    const c = u.cover;
    if (!c) return null;
    const mine = this.cover?.unitId === u.id ? this.cover : null;
    return { side: mine?.side ?? (c.pose === 'low' || c.pose === 'crest' ? 'over' : c.side === -1 ? 'left' : 'right'), peeking: c.out };
  }

  /** 66.9: the slot Shift would take for `u` now (lane F's slots round a wide rock; game/takeCover.ts pickSlot), or null. */
  private slotNear(u: PlayerState): CoverSlot | null {
    const state = this.o.match.state, planet = state.planets[u.planetIndex];
    if (!planet) return null;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, reach = TAKE_COVER.reachPeople * person;
    const toward = aheadOf(u.surfaceDir, headingOf(u), TAKE_COVER.threatPeople * person, planet.radius);
    return pickSlot(coverSlotsNear(state, u.planetIndex, u.surfaceDir, reach), u.surfaceDir, toward, reach, planet.radius, SLOT_LEAN_BONUS_PEOPLE * person);
  }

  /**
   * 66.8: why the dig key digs nothing here — the ground refuses it (rock, ice: core/dig.ts), or the dig under you is as deep
   * as it goes — said in the note slot while the key is held.
   */
  private digRefusal(me: PlayerState): void {
    const state = this.o.match.state, seconds = this.o.match.actionTick * PHYS_DT, planet = state.planets[me.planetIndex];
    if (!planet) return;
    const material = groundMaterial(state, me.planetIndex, me.surfaceDir, seconds);
    const near = 0.5 * PERSON_BODY_HEIGHT * personSetOf(state).k;
    const deep = (planet.digs ?? []).some(d => d.kind === 'foxhole' && d.done >= 1 && dist(normalize(d.dir), normalize(me.surfaceDir)) * planet.radius < near);
    const why = deep ? 'dug in as deep as it goes' : !DIG_GROUND[material] || !(ENTRENCH.rate[material] > 0) ? `can't dig: ${material}` : null;
    if (why) this.aidNote = { text: why, until: this.time + 1.5 };
  }

  /** 66.7 (a): the cover Shift would take for `u` now (game/takeCover.ts pickCover: in reach, hiding from where it looks). */
  private coverNear(u: PlayerState): CoverPoint | null {
    const state = this.o.match.state, planet = state.planets[u.planetIndex];
    if (!planet) return null;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, reach = TAKE_COVER.reachPeople * person;
    const toward = aheadOf(u.surfaceDir, headingOf(u), TAKE_COVER.threatPeople * person, planet.radius);
    return pickCover(coverPointsNear(state, u.planetIndex, u.surfaceDir, reach), u.surfaceDir, toward, reach, planet.radius);
  }

  /** 66.7 (a): where the body stands at `p` — pressed to its prop (the walk's obstacle by the point's ref, the nearest such), else the point (the ground's cover). */
  private coverSpot(p: CoverPoint, planetIndex: number): Vec3 {
    if (!p.prop) return p.dir;
    const state = this.o.match.state, planet = state.planets[planetIndex], ps = personSetOf(state), at = normalize(p.dir);
    let prop: { dir: Vec3; radius: number } | null = null, best = Infinity;
    for (const o of planetObstacles(state, planetIndex).list) {
      if (o.ref?.kind !== p.prop.kind || o.ref.id !== p.prop.id) continue;
      const d = dist(normalize(o.dir), at);
      if (d < best) { best = d; prop = o; }
    }
    return prop ? pressedSpot(p, prop, ps.unitRadius, TAKE_COVER.pressBodies * ps.unitRadius, planet.radius) : p.dir;
  }

  /**
   * 66.7 (a)'s rework: the face of the cover `p` holds (game/takeCover.ts coverFace), in the pose's person units — the prop
   * whose contact it stands within half a person of; worked out when its hold or its spot changes (a held unit stands still).
   */
  private coverFaceOf(p: PlayerState): CoverFace | null {
    const c = p.cover;
    if (!c || p.mount || p.airborne) { this.coverFaces.delete(p.id); return null; }
    const state = this.o.match.state, planet = state.planets[p.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    // 66.8: in a dig, the dig's own face (digCover's two numbers, the pose's person units) — re-read as it deepens
    const dug = digFace(planet.digs, p.surfaceDir, 0.5 * person, planet.radius);
    const was = this.coverFaces.get(p.id);
    if (was && was.cover === c && dist(was.dir, p.surfaceDir) < 1e-9 && was.dug === (dug ? dug.dig.id * 8 + dug.dig.done * 4 : undefined)) return was.face;
    if (dug) { const face = { gap: dug.gap, height: dug.height }; this.coverFaces.set(p.id, { cover: c, dir: p.surfaceDir, face, dug: dug.dig.id * 8 + dug.dig.done * 4 }); return face; }
    // 66.9: at a slot (not in a dig: the dig's face wins), its face's own distances (lane F's: to the drawn face at the chest, the drawn top)
    const sl = this.cover?.unitId === p.id && this.cover.phase === 'held' ? this.cover.slot : undefined;
    if (sl) return { gap: sl.gap * PERSON_UNITS, height: sl.top * PERSON_UNITS, toward: sl.facing };
    const toWorld = person / PERSON_UNITS, chest = (COVER_POSE_SIZE[c.pose].down.eye - 0.3) * toWorld;
    const po = planetObstacles(state, p.planetIndex);
    const f = coverFace(p.surfaceDir, obstaclesNear(po, normalize(p.surfaceDir), (po.maxRadius + 2 * ps.unitRadius + 0.5 * person) / planet.radius, false), chest, ps.unitRadius, 0.5 * person, planet.radius);
    const face = f ? { gap: f.gap / toWorld, height: f.height / toWorld, toward: f.toward } : null;
    this.coverFaces.set(p.id, { cover: c, dir: p.surfaceDir, face });
    return face;
  }

  /**
   * 66.7 (a): where `me` leans out round its cover on `side` (game/takeCover.ts leanSpot): the sim's own leaned muzzle
   * (muzzlePoint reads core/lean.ts) at each candidate, clear to what the crosshair rests on (or a point twelve people
   * along the look) through the ground and every prop; a spot the walk can stand on (outside every prop's contact).
   */
  private leanSpotFor(me: PlayerState, c: NonNullable<ActionHost['cover']>, hold: CoverHold, side: 'left' | 'right'): Vec3 | null {
    const state = this.o.match.state, planet = state.planets[me.planetIndex], ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    const aim = this.aimHold?.target ?? add(muzzlePoint(me, state.planets, ps.eyeHeight, ps), scale(aimDirection(normalize(me.surfaceDir), headingOf(me), this.pitch), TAKE_COVER.threatPeople * person));
    const po = planetObstacles(state, me.planetIndex);
    const clears = (q: Vec3): boolean => {
      const m = muzzlePoint({ ...me, surfaceDir: q, cover: hold }, state.planets, ps.eyeHeight, ps);
      const t = add(m, scale(sub(aim, m), 0.97));   // (short of the aim point itself: on the ground or a prop's face)
      return personLineOfSight(m, t, state) && !segmentCovered(m, t, state);
    };
    const free = (q: Vec3): boolean => obstaclesNear(po, q, (po.maxRadius + 2 * ps.unitRadius) / planet.radius, false)
      .every(o => dist(normalize(o.dir), normalize(q)) * planet.radius >= o.radius + ps.unitRadius);
    return leanSpot(c.at, c.point.facing, side, person, planet.radius, clears, free);
  }

  /**
   * 66.7 (a), X32: this tick's take-cover for the player's unit — the walk to the cover or to its peek spot (no cover sent
   * while walking: the Match drops it on a step and takes it again when still), or the hold and stance at it. `stepped`: a
   * movement key or a jump this tick — the player's own step leaves the cover. Null: not in cover.
   */
  private coverStep(me: PlayerState | null | undefined, stepped: boolean): { walk?: { forward: number; right: number }; hold?: CoverHold; stance?: Stance } | null {
    const queued = this.coverQueued;
    this.coverQueued = false;
    if (!me || !me.alive || me.mount || this.commanding) { this.cover = null; this.leanKey = null; return null; }
    if (this.cover && (this.cover.unitId !== me.id || stepped || me.airborne)) this.cover = null;
    if (queued) {
      if (this.cover) { this.cover = null; return null; }   // Shift again: out of it
      // 66.9: a wide rock's slot first (lane F's: the distances sorted out), else the cover points (a trunk, a narrow rock)
      const slot = this.slotNear(me);
      if (slot) this.cover = { unitId: me.id, point: slotPoint(slot), at: slot.dir, slot, phase: 'going', since: this.time };
      else {
        const point = this.coverNear(me);
        if (!point) { this.aidNote = { text: 'no cover in reach', until: this.time + 2 }; return null; }
        this.cover = { unitId: me.id, point, at: this.coverSpot(point, me.planetIndex), phase: 'going', since: this.time };
      }
    }
    const c = this.cover;
    if (!c) return null;
    const state = this.o.match.state, planet = state.planets[me.planetIndex];
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, heading = headingOf(me);
    const toward = aheadOf(me.surfaceDir, heading, TAKE_COVER.threatPeople * person, planet.radius);
    const lean = c.phase === 'held' ? this.leanKey ?? undefined : undefined;
    const kept = c.hides && c.side ? { hides: c.hides, side: c.side } : undefined;   // (chosen as it arrived)
    if (c.slot && c.phase === 'held' && lean) {
      // 66.9: a lean key at a slot — its own end's side leans out here; another side, a step to the face's end that way
      const to = slotFor(coverSlotsNear(state, me.planetIndex, c.slot.dir, (c.slot.faceWidth + 2) * person), c.slot, lean === 'right' ? 1 : -1);
      if (to) Object.assign(c, { slot: to, point: slotPoint(to), at: to.dir, phase: 'going', since: this.time, reached: undefined, walk: undefined, side: undefined, hides: undefined });
    }
    const r = c.slot
      ? (s => ({ ...slotHoldFor(s, c.phase === 'held' && (this.peekHeld || (lean !== undefined && s.kind === 'end' && s.lean === (lean === 'right' ? 1 : -1)))), hides: s.shelter[0] ?? 'crouch' as const }))(c.slot)
      : coverHoldFor(c.point, toward, c.phase === 'held' && (this.peekHeld || lean !== undefined), kept, c.at, lean);
    const { hold, side, hides, stance } = r;
    let spot = r.spot;
    // out round an edge: the lean's own spot — the smallest shift along the cover whose leaned gun clears to the aim (lane C's
    // lean moves the eye and the muzzle; pressed to a wide rock the lean alone may not clear it) — found once as it begins
    // (66.9: a slot stands where its lean clears — no search)
    if (!c.slot && hold.out && (side === 'left' || side === 'right')) {
      if (c.lean?.side !== side) c.lean = { side, spot: this.leanSpotFor(me, c, hold, side) ?? spot };
      spot = c.lean.spot;
    } else c.lean = undefined;
    const target = c.phase === 'going' ? c.at : spot;
    if (target !== c.reached) {
      // the walk there: arrived within a step, or stalled near it (pressed to the prop: the walk stops a body at its contact)
      const gap = gapTo(me.surfaceDir, target, planet.radius);
      const w = c.walk?.target === target ? c.walk : (c.walk = { target, gap, at: this.time });
      let stalled = false;
      if (this.time - w.at >= TAKE_COVER.stallSeconds) { stalled = gap < TAKE_COVER.nearPeople * person && gap > w.gap - TAKE_COVER.stallPeople * person; w.gap = gap; w.at = this.time; }
      const walk = stalled ? null : walkTo(me.surfaceDir, heading, target, TAKE_COVER.arrivePeople * person, planet.radius, TAKE_COVER.slowPeople * person);
      if (walk) {
        if (this.time - c.since > TAKE_COVER.walkSeconds) { this.cover = null; this.aidNote = { text: 'can\'t reach that cover', until: this.time + 2 }; return null; }
        return { walk };
      }
      c.reached = target; c.walk = undefined;
    }
    if (c.phase === 'going') c.phase = 'held';   // there: down behind it this tick (a peek held already steps out on the next)
    c.since = this.time;   // (a peek's step is timed from the last hold)
    c.side = side; c.hides = hides;
    return { hold, stance };
  }

  private handFor(unitId: number): Hand {
    if (unitId === this.controlled) return this.playerHand;
    let h = this.hands.get(unitId);
    if (!h) { h = handOf(this.o.match.state.config.seed, unitId); this.hands.set(unitId, h); }
    return h;
  }

  /** 67.21: the Settings toggle — the controlled unit redraws with this hand and the camera moves to that shoulder. */
  setPlayerHand(h: Hand): void { this.playerHand = h; }
  /** 68.14: the wind's level where the camera is (main's windAtViewer), for the far fight's rumble from above. */
  setWind(level: number): void { this.sound.setWind(level); }

  /** 67.15: how long a round's streak is — what it covers in TRAIL_SECONDS, never shorter than STREAK, never longer than it has flown. */
  private trailOf(speed: number, flown: number): number {
    return Math.min(flown, Math.max(STREAK * personSetOf(this.o.match.state).k, speed * TRAIL_SECONDS));
  }

  /** 69.29: the tank's rounds in flight (by bullet id): the explosive round or the dart, the dart's sabot falling away. */
  private readonly tankRounds = new Map<number, { g: THREE.Group; sabot: THREE.Group | null }>();
  /**
   * 69.29: each tank round in flight drawn as itself (rendering/tankLooks buildTankRound: the explosive round, or the dart
   * with its sabot's petals falling away over its first quarter second) at its head, along its flight, over its tracer;
   * gone with its bullet. Presentation only.
   */
  private updateTankRounds(bullets: readonly Bullet[], person: number): void {
    const live = new Set<number>();
    for (const b of bullets) {
      if (!b.solid && b.explosion !== 'tankHe') continue;
      live.add(b.id);
      let r = this.tankRounds.get(b.id);
      if (!r) {
        const g = buildTankRound(b.solid ? 'solid' : 'he', TANK_PICKS.era, 'flight'), sabot = b.solid && TANK_PICKS.era === 'A' ? buildSabot() : null;
        if (sabot) g.add(sabot);
        g.scale.setScalar(person / TANK_GUN_LOOK.person);
        this.o.scene.add(g);
        r = { g, sabot }; this.tankRounds.set(b.id, r);
      }
      const at = muzzleBlend(b.pos, this.bulletStarts.get(b.id), b.age), v = normalize(b.vel);
      r.g.position.set(at.x, at.y, at.z);
      r.g.quaternion.setFromUnitVectors(_tankZ, _tankV.set(v.x, v.y, v.z));
      if (r.sabot) poseSabot(r.sabot, b.age / 0.25);
    }
    for (const [id, r] of this.tankRounds) {
      if (live.has(id)) continue;
      this.o.scene.remove(r.g); this.tankRounds.delete(id);
      r.g.traverse(o => { const mesh = o as THREE.Mesh; if (!mesh.isMesh) return; mesh.geometry.dispose(); if (mesh.material instanceof THREE.MeshBasicMaterial) mesh.material.dispose(); });   // (its own geometry; the round's vertex-colour material is shared)
    }
  }

  /** 63.13 step 2: a gunship fired — the gun that fired laid on its aim and recoiled (gunshipLook), its flash at its drawn muzzle (the rockets' at the sim's). */
  private gunshipFire(e: Extract<ActionEvent, { kind: 'gunshipFire' }>): void {
    const state = this.o.match.state, f = state.dropships?.flights.find(x => x.id === e.flight), planet = f && state.planets[f.planetIndex];
    if (!f || !planet) return;
    const v = this.dropshipViews.get(e.flight), person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    const r = v?.gun ? gunshipFired(v.gun, e.weapon, e.from, e.to) : { at: e.from, size: 0.6 };
    const dir = normalize(sub(e.to, r.at)), up = normalize(sub(r.at, planet.position));
    for (const part of muzzleFlashParts(r.at, dir, up, person * r.size, this.smokeDriftAt(r.at, f.planetIndex))) {
      this.o.scene.add(part.mesh);
      this.flashes.push({ mesh: part.mesh, life: part.life, age: 0, grow: part.grow, peak: part.peak, rise: part.rise, shared: true, base: part.mesh.scale.x });
    }
  }

  /** 68.39b: the strike rounds' balls in flight (pooled). */
  private readonly strikeBalls: THREE.Mesh[] = [];

  private updateBullets(dt: number): void {
    const bullets = this.o.match.state.bullets ?? [];
    const canvas = this.o.canvas as HTMLCanvasElement;
    const bw = canvas.width || window.innerWidth, bh = canvas.height || window.innerHeight;
    this.tracers.begin(bw, bh, bw / Math.max(1, canvas.clientWidth || window.innerWidth));
    const count = Math.min(bullets.length, MAX_BULLETS);
    const person = PERSON_BODY_HEIGHT * personSetOf(this.o.match.state).k;
    for (let i = 0; i < count; i++) {
      const b = bullets[i];
      const speed = len(b.vel), v = normalize(b.vel);
      if (b.coast && b.age >= (b.life ?? 0)) { this.coastingRocket(b, v, person); continue; }   // 65.46: past its motor's burn
      const trail = this.trailOf(speed, speed * b.age);
      const a = muzzleBlend(b.pos, this.bulletStarts.get(b.id), b.age);
      this.tracers.add(a, { x: a.x - v.x * trail, y: a.y - v.y * trail, z: a.z - v.z * trail });
    }
    if (this.puffAt.size) { const live = new Set(bullets.map(b => b.id)); for (const id of this.puffAt.keys()) if (!live.has(id)) this.puffAt.delete(id); }
    if (this.bulletStarts.size) { const live = new Set(bullets.map(b => b.id)); for (const id of this.bulletStarts.keys()) if (!live.has(id)) this.bulletStarts.delete(id); }
    this.updateTankRounds(bullets, person);
    if (this.guidedRockets || bullets.some(b => b.guide)) (this.guidedRockets ??= new GuidedRockets(this.o.scene)).draw(bullets, person);   // 63.13 step 2
    // 68.39b: a strike's rounds drawn as the classic game's shells — a ball at the head (its tracer the trail), a person's third across
    const strike = bullets.filter(b => b.explosion === 'bomb');
    while (this.strikeBalls.length < strike.length) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(1, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffb347 }));
      this.o.scene.add(m);
      this.strikeBalls.push(m);
    }
    this.strikeBalls.forEach((m, i) => {
      const b = strike[i];
      m.visible = !!b;
      if (b) { m.position.set(b.pos.x, b.pos.y, b.pos.z); m.scale.setScalar(0.35 * person); }
    });
    // the afterimages: the head stays where the round stopped, the tail closes in on it as it fades
    this.afterimages = this.afterimages.filter(g => (g.age += dt) < AFTERIMAGE_SECONDS);
    for (const g of this.afterimages) {
      const f = g.age / AFTERIMAGE_SECONDS;
      this.tracers.add(g.head, { x: g.tail.x + (g.head.x - g.tail.x) * f, y: g.tail.y + (g.head.y - g.tail.y) * f, z: g.tail.z + (g.head.z - g.tail.z) * f }, 1 - f);
    }
    // 69.9: the point-defence bursts' tracers, each round streaking out from the muzzle along its line, gone at its end
    if (this.pdStreams.length) {
      const T = PD_TRACERS;
      this.pdStreams = this.pdStreams.filter(r => {
        r.age += dt;
        // 67.78: a round's fuse runs out — checked after the step (a frame carries a round past its end and its streak at once)
        if (r.pop && !r.pop.done && r.age * T.speed * person >= r.len) { r.pop.done = true; this.pdAirburst(r, person); }
        return r.age * T.speed * person - T.streak * person < r.len;
      });
      for (const r of this.pdStreams) {
        if (r.age <= 0) continue;
        const head = Math.min(r.len, r.age * T.speed * person), tail = Math.max(0, head - T.streak * person);
        this.tracers.add({ x: r.from.x + r.dir.x * head, y: r.from.y + r.dir.y * head, z: r.from.z + r.dir.z * head }, { x: r.from.x + r.dir.x * tail, y: r.from.y + r.dir.y * tail, z: r.from.z + r.dir.z * tail });
      }
    }
    this.tracers.end();
  }

  /**
   * 69.9: a point-defence burst (`pdBurst`): its tracer rounds from the mount's drawn muzzle (rendering/pointDefenceLooks.ts
   * pdMuzzle: its height, and its reach out along the line to the round) to the round — on past it when it missed; at a ship
   * it hit, the flak's sparks on the hull. The mount's own turret slews to it (renderer.ts aimStructures).
   */
  private pdBurst(e: Extract<ActionEvent, { kind: 'pdBurst' }>): void {
    const state = this.o.match.state, m = state.structures.find(s => s.id === e.mountId), planet = state.planets[e.planetIndex];
    if (!m || !planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, up = vecNormalize(m.surfaceDir), mz = pdMuzzle(e.variant);
    const base = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up) + mz.height * person));
    const toward = vecSub(e.at, base), dist = len(toward);
    if (!(dist > 0)) return;
    const dir = vecScale(toward, 1 / dist), from = vecAdd(base, vecScale(dir, mz.reach * person));
    const reach = Math.max(0, dist - mz.reach * person) * (e.hit ? 1 : 1 + PD_TRACERS.past);
    // 67.78: of a miss, the rounds that self-destruct fly on to their fuse (the presentation's randomness: not the sim's)
    const key = e.variant as CannonKey, n = PD_TRACERS.tracers[e.variant], fuses = CANNON_VOICES[key] ? cannonFuses(CANNON_VOICES[key], e.hit, n, Math.random) : [];
    for (let k = 0; k < n; k++) {
      const f = fuses.find(x => x.tracer === k);
      if (!f) { this.pdStreams.push({ from, dir, len: reach, age: -k * PD_TRACERS.spacing }); continue; }
      const j = PD_AIRBURST.scatter, d = vecNormalize({ x: dir.x + (Math.random() - 0.5) * j, y: dir.y + (Math.random() - 0.5) * j, z: dir.z + (Math.random() - 0.5) * j });
      this.pdStreams.push({ from, dir: d, len: Math.max(reach, f.fuse * PD_TRACERS.speed * person), age: -k * PD_TRACERS.spacing, pop: { key, planetIndex: e.planetIndex } });
    }
    if (e.target.kind === 'ship' && e.hit) {
      this.explosions ??= new ExplosionField(this.o.scene, person);
      for (let k = 0; k < 3; k++) {
        const j = { x: (Math.random() - 0.5) * 1.6 * person, y: (Math.random() - 0.5) * 1.0 * person, z: (Math.random() - 0.5) * 1.6 * person };
        const at = vecAdd(e.at, j);
        this.explosions.emit('fireball', { at, up: vecNormalize(vecSub(at, planet.position)), life: 0.12, s0: 0.15 * person, s1: 0.3 * person, c0: '#ffd27a', delay: 0.05 * k + 0.15 });
      }
    }
  }

  /** 67.78: a self-destructing round bursting at the end of its stream: the flash, the dark puff (both at least PD_AIRBURST.minAngle across from the camera), its pop. */
  private pdAirburst(r: { from: Vec3; dir: Vec3; len: number; pop?: { key: CannonKey; planetIndex: number } }, person: number): void {
    const planet = this.o.match.state.planets[r.pop!.planetIndex];
    if (!planet) return;
    const at = vecAdd(r.from, vecScale(r.dir, r.len)), up = vecNormalize(vecSub(at, planet.position));
    this.explosions ??= new ExplosionField(this.o.scene, person);
    drawAirburst(this.explosions, at, up, this.o.camera.position, person);   // (69.58: the roof turret's misses burst the same)
    this.sound.cannonPop(r.pop!.key, at);
  }

  /**
   * 69.58 step 2 (the sheet design_sheets/countermeasures.html; rendering/countermeasureLooks.ts): the flares burning, drawn
   * where the state has them (core/decoys.ts `flareAt`: the sim's closed-form fall) for their burn, their smoke lingering
   * after; nothing until a flare is popped.
   */
  private flares: FlareField | null = null;
  private updateFlares(dt: number): void {
    const state = this.o.match.state, live = state.decoys?.live ?? [];
    if (!live.length && !this.flares) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, seen: FlareSeen[] = [];
    for (const d of live) {
      const age = (this.o.match.actionTick - d.tick) * PHYS_DT, at = flareAt(state, d, age), planet = state.planets[d.planetIndex];
      if (at && planet) seen.push({ id: d.id, pos: at.pos, age, up: vecNormalize(vecSub(at.pos, planet.position)) });
    }
    (this.flares ??= new FlareField(this.o.scene, person)).update(seen, this.o.camera, dt);
  }

  /** 69.58 step 2: a flare popped (`decoyPop`): the dispenser's flash and puff where it left the ship, toward its throw. */
  private flarePop(e: Extract<ActionEvent, { kind: 'decoyPop' }>): void {
    const state = this.o.match.state, planet = state.planets[e.planetIndex], d = state.decoys?.live.find(x => x.id === e.decoy);
    if (!planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, up = vecNormalize(vecSub(e.at, planet.position));
    const dir = d && len(d.vel) > 0 ? vecNormalize(d.vel) : vecScale(up, -1);
    (this.flares ??= new FlareField(this.o.scene, person)).pop(e.at, up, dir);
  }

  /** 69.58 step 2: a missile burst (the LAW's class) within 2.5 people of a burning flare: the flare took it — its magnesium thrown out. */
  private flareBurst(point: Vec3, planetIndex: number): void {
    const state = this.o.match.state, live = state.decoys?.live ?? [], planet = state.planets[planetIndex];
    if (!live.length || !planet || !this.flares) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    // (a flare a moment old is still at the ship: a hit on the ship beside it is not a burst on it)
    const near = live.some(d => { const age = (this.o.match.actionTick - d.tick) * PHYS_DT, at = age > 0.25 ? flareAt(state, d, age) : null; return !!at && len(vecSub(at.pos, point)) < 2.5 * person; });
    if (near) this.flares.scatter(point, vecNormalize(vecSub(point, planet.position)));
  }

  /**
   * 69.58 step 2: the gunship's roof turret fired at a missile (`shipIntercept`; GameConfig.shipTurrets): the turret laid on
   * it, its tracers from the drawn muzzle as point defence's (a miss's rounds flying on to their self-destruct, popping
   * there), a kill the LAW's burst in the air where the missile was and its pieces falling. No turret drawn: nothing.
   */
  private turretBurst(e: Extract<ActionEvent, { kind: 'shipIntercept' }>): void {
    const state = this.o.match.state, planet = state.planets[e.planetIndex], v = this.dropshipViews.get(e.flight);
    const from = v?.gun ? turretFired(v.gun, e.at) : null;
    if (!planet || !from) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, toward = vecSub(e.at, from), dist = len(toward);
    if (!(dist > 0)) return;
    // (the lean's count and spacing; its speed and streak are point defence's: the streams draw at PD_TRACERS')
    const T = TURRET_TRACERS[COUNTERMEASURE_LOOK.tracer], dir = vecScale(toward, 1 / dist), reach = dist * (e.hit ? 1 : 1 + T.past);
    const fuses = cannonFuses(CANNON_VOICES.ciws, e.hit, T.tracers, Math.random);
    for (let k = 0; k < T.tracers; k++) {
      const f = fuses.find(x => x.tracer === k);
      if (!f) { this.pdStreams.push({ from, dir, len: reach, age: -k * T.spacing }); continue; }
      const j = PD_AIRBURST.scatter, d = vecNormalize({ x: dir.x + (Math.random() - 0.5) * j, y: dir.y + (Math.random() - 0.5) * j, z: dir.z + (Math.random() - 0.5) * j });
      this.pdStreams.push({ from, dir: d, len: Math.max(reach, f.fuse * PD_TRACERS.speed * person), age: -k * T.spacing, pop: { key: 'ciws', planetIndex: e.planetIndex } });
    }
    if (!e.hit) return;
    this.explosions ??= new ExplosionField(this.o.scene, person);
    const up = vecNormalize(vecSub(e.at, planet.position)), ground = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up)));
    drawTurretKill(this.explosions, COUNTERMEASURE_LOOK.kill, e.at, up, ground, person, this.burstSeed++);
  }

  /** 69.9: a round killed in the air (`intercepted`): a small airburst where it died (no ground under it: the flash, the fire, a dark puff) and its pieces falling. */
  private pdIntercept(e: Extract<ActionEvent, { kind: 'intercepted' }>): void {
    const state = this.o.match.state, planet = state.planets[e.planetIndex];
    if (!planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k, up = vecNormalize(vecSub(e.at, planet.position));
    this.explosions ??= new ExplosionField(this.o.scene, person);
    this.explosions.spawn(e.target.kind === 'grenade' ? 'gl40' : 'law', e.at, up, 'rock', 'far', this.burstSeed++);
    const ground = vecAdd(planet.position, vecScale(up, this.groundAt(planet, up)));
    for (let k = 0; k < 8; k++) {
      const a = k * 2.4;
      this.explosions.emit('debris', { at: e.at, up, ground, vel: vecAdd(vecScale(up, (1 + Math.random() * 3) * person), { x: Math.cos(a) * 3 * person * Math.random(), y: 0, z: Math.sin(a) * 3 * person * Math.random() }), gravity: 9 * person, drag: 0.2, life: 4, s0: 0.08 * person, s1: 0.08 * person, c0: '#2b2d30', spin: 6 });
    }
  }

  /**
   * 65.46 (the owner: "once the motor runs out ... stayed the course with a low speed"): a rocket coasting past its burn — its
   * motor's flame out (a short dim streak, its body, not the burning trail), and its smoke left behind it every
   * ROCKET_PUFF.everySeconds of the frame's clock: grey puffs that swell and fade (one shared sphere, no allocation a puff).
   */
  private puffAt = new Map<number, number>();
  private puffGeo: THREE.SphereGeometry | null = null;
  private coastingRocket(b: { id: number; pos: Vec3 }, v: Vec3, person: number): void {
    const body = 0.6 * person;
    this.tracers.add(b.pos, { x: b.pos.x - v.x * body, y: b.pos.y - v.y * body, z: b.pos.z - v.z * body }, 0.35);
    const last = this.puffAt.get(b.id);
    if (last !== undefined && this.time - last < ROCKET_PUFF.everySeconds) return;
    this.puffAt.set(b.id, this.time);
    this.puffGeo ??= new THREE.SphereGeometry(1, 8, 6);
    const mesh = new THREE.Mesh(this.puffGeo, flashMaterial(ROCKET_PUFF.color, ROCKET_PUFF.peak));
    mesh.position.set(b.pos.x, b.pos.y, b.pos.z);
    mesh.scale.setScalar(ROCKET_PUFF.sizePeople * person);
    this.o.scene.add(mesh);
    this.flashes.push({ mesh, life: ROCKET_PUFF.seconds, age: 0, grow: ROCKET_PUFF.grow, peak: ROCKET_PUFF.peak, shared: true, base: ROCKET_PUFF.sizePeople * person });
  }

  /** 67.15: a round stopped at `point` — leave its streak there for AFTERIMAGE_SECONDS. */
  private afterimage(b: { vel: Vec3; age: number }, point: Vec3): void {
    const speed = len(b.vel), v = normalize(b.vel);
    const trail = this.trailOf(speed, speed * Math.max(b.age, PHYS_DT));
    if (this.afterimages.length < MAX_BULLETS) this.afterimages.push({ head: point, tail: { x: point.x - v.x * trail, y: point.y - v.y * trail, z: point.z - v.z * trail }, age: 0 });
  }

  /** 65.49 step 2: the anti-dropship missiles in flight (rendering/missileFlights.ts), read from the state every frame; nothing until one is fired. */
  private missileFlights: MissileFlights | null = null;
  private updateMissiles(): void {
    const state = this.o.match.state, live = state.missiles?.live ?? [];
    if (!live.length && !this.missileFlights) return;
    (this.missileFlights ??= new MissileFlights(this.o.scene)).update(live, this.o.camera, this.time, PERSON_BODY_HEIGHT * personSetOf(state).k, team => unitPaletteFor(TEAMS[team % TEAMS.length]));
  }

  /**
   * 65.49 step 2: HUD view B while the player's anti-dropship missile is up (its lock on the unit: ui/antiDropshipMissileHud.ts) —
   * the ship it tracks or holds put on screen from the sim's hull, the seeker's cone as a radius from the camera's field of view.
   */
  private missileHud: AntiDropshipMissileOverlay | null = null;
  private updateMissileHud(me: PlayerState | undefined, shown: boolean): void {
    const lock = shown ? me?.missileLock : undefined;
    if (!lock) { this.missileHud?.update(null); return; }
    const state = this.o.match.state, ship = lock.target !== null ? shipMotion(state, lock.target, me!.planetIndex, this.o.match.actionTick) : null;
    const coneRadius = (Math.tan(ANTI_DROPSHIP_MISSILE_DIALS.lock.cone) / Math.tan((this.o.camera.fov * Math.PI) / 360)) * (window.innerHeight / 2);
    (this.missileHud ??= new AntiDropshipMissileOverlay()).update({ state: lock.state, target: ship ? this.onScreen(ship.pos) : null, coneRadius, t: this.time });
  }

  /** 64.11: the shells in flight, read from the state every frame. */
  private updateShells(): void {
    const shells = this.o.match.state.shells ?? [];
    while (this.shellMeshes.length < shells.length) {
      const m = new THREE.Mesh(new THREE.SphereGeometry(0.12, 10, 8), new THREE.MeshBasicMaterial({ color: 0xffb347 }));
      this.o.scene.add(m);
      this.shellMeshes.push(m);
    }
    this.shellMeshes.forEach((m, i) => {
      const s = shells[i];
      m.visible = !!s;
      if (s) m.position.set(s.pos.x, s.pos.y, s.pos.z);
    });
  }

  /** 67.54: every burst's pieces (six instanced meshes, made on first use at the person's scale). */
  private explosions: ExplosionField | null = null;
  /** 67.77: each vehicle's smoke charges popped, as last drawn (a rise is a pop). */
  private smokeSeen = new Map<number, number>();
  /**
   * 67.77: a vehicle's pop (its `smokeUsed` rose) drawn at its launchers: a flash and a puff at each muzzle, out along the
   * barrel — the grenades fly from those muzzles in the sim (core/vehicleSmoke.ts). Per frame a map read a vehicle; a pop,
   * two pieces a barrel in the field's pools (no draw of its own).
   */
  private smokePops(state: Match["state"], person: number): void {
    for (const s of state.structures) {
      if (!s.drive) continue;
      const used = s.smokeUsed ?? 0, was = this.smokeSeen.get(s.id);
      if (was === used) continue;
      this.smokeSeen.set(s.id, used);
      const m = this.bikeViews.get(s.id);
      if (was === undefined || used < was || !m?.smokeMuzzles) continue;
      this.explosions ??= new ExplosionField(this.o.scene, person);
      const up = vecNormalize(s.surfaceDir);
      for (const mz of m.smokeMuzzles) {
        const at = mz.muzzle.getWorldPosition(new THREE.Vector3()), out = at.clone().sub(mz.breech.getWorldPosition(new THREE.Vector3())).normalize();
        this.explosions.emit('fireball', { at, up, life: 0.08, s0: 0.08 * person, s1: 0.22 * person, c0: '#ffd27a' });
        this.explosions.emit('smoke', { at, up, vel: { x: out.x * 1.5 * person, y: out.y * 1.5 * person, z: out.z * 1.5 * person }, life: 1.4, s0: 0.12 * person, s1: 0.7 * person, c0: '#d9d6cf', c1: '#c8c4bc', drag: 1.5 });
      }
    }
  }
  private burstSeed = 1;
  /**
   * 67.54: a burst of `cls` drawn at `point` — the ground's up and material there, thinned by its distance from the camera
   * (rendering/explosionLook.ts burstDetail: no debris past 60 people, the diorama's flash, fire and smoke past 200); the
   * drawn ground under it given, so its scorch lies draped on the terrain and its debris lands there (67.54's bug: the flat
   * disc at the burst's height, floating over a slope or beside a hit truck).
   */
  private burst(cls: ExplosionClass, point: Vec3, planetIndex: number): void {
    const state = this.o.match.state, planet = state.planets[planetIndex];
    if (!planet) return;
    const person = PERSON_BODY_HEIGHT * personSetOf(state).k;
    this.explosions ??= new ExplosionField(this.o.scene, person);
    const up = normalize({ x: point.x - planet.position.x, y: point.y - planet.position.y, z: point.z - planet.position.z });
    const material = groundMaterial(state, planetIndex, up, this.o.match.actionTick * PHYS_DT);
    const cam = this.o.camera.position, people = Math.hypot(point.x - cam.x, point.y - cam.y, point.z - cam.z) / person;
    const pc = planet.position;
    const under = (q: THREE.Vector3, out: THREE.Vector3): boolean => {
      const dx = q.x - pc.x, dy = q.y - pc.y, dz = q.z - pc.z, l = Math.hypot(dx, dy, dz);
      if (!(l > 0)) return false;
      const dir = { x: dx / l, y: dy / l, z: dz / l }, r = this.groundAt(planet, dir);
      out.set(pc.x + dir.x * r, pc.y + dir.y * r, pc.z + dir.z * r);
      return true;
    };
    this.explosions.spawn(cls, point, up, material, burstDetail(people), this.burstSeed++, under);
  }

  /**
   * 65.47: a shot's spent case, out of the drawn gun's port — the firer's gun in hand (PersonView.ejection), or a mounted .50's
   * (its port behind its drawn muzzle, the case down out of it). None past BRASS_LOOK.near people from the camera (asked
   * before any matrix is read), nor from a figure not drawn in full.
   */
  private throwBrass(unitId: number, seed: number, from: Vec3, pv: PersonView | undefined, mounted: THREE.Object3D | null, k: number): void {
    const field = this.brass!, person = PERSON_BODY_HEIGHT * k, cam = this.o.camera.position;
    if ((from.x - cam.x) ** 2 + (from.y - cam.y) ** 2 + (from.z - cam.z) ** 2 > (BRASS_LOOK.near * person) ** 2) { field.skipped++; return; }   // (the shot's start: nothing looked up for a far one)
    const state = this.o.match.state, p = state.players.find(q => q.id === unitId);
    if (!p) return;
    if (!field.group.parent) this.o.scene.add(field.group);
    const planets = state.planets, feet = this.feetDrawn(p, planets);
    const unit = person / PERSON_UNITS, n = normalize(p.surfaceDir);
    _bu.set(n.x, n.y, n.z); _bf.set(feet.x, feet.y, feet.z);
    const probe = this.groundProbe(planets[p.planetIndex]);
    if (mounted) {
      // the .50: its port about 0.6 of a person behind the muzzle, a little under it; the case falls out under the gun
      mounted.getWorldPosition(_bp); mounted.getWorldDirection(_ba);
      const e = EJECT.hmg!;
      _bp.addScaledVector(_ba, -0.6 * PERSON_UNITS * unit).addScaledVector(_bu, -0.04 * PERSON_UNITS * unit);
      _bo.crossVectors(_ba, _bu).multiplyScalar(0.15).addScaledVector(_bu, -1);
      field.eject(e.round, _bp, _bo, _ba, _bu, unit, seed, probe, _bf, cam);
      return;
    }
    const ej = pv?.ejection(_bp, _bo, _ba);
    if (ej) field.eject(ej.round, _bp, _bo, _ba, _bu, unit, seed, probe, _bf, cam, ej.delay ?? 0);
  }

  private flash(at: Vec3, color: number, size: number, life: number, grow: number): void {
    // (69.98: the rocket puffs' unit sphere, scaled to the flash's size; its material kept — rendering/flashMaterials.ts)
    this.puffGeo ??= new THREE.SphereGeometry(1, 8, 6);
    const mesh = new THREE.Mesh(this.puffGeo, flashMaterial(color, 0.9));
    mesh.position.set(at.x, at.y, at.z);
    mesh.scale.setScalar(size);
    this.o.scene.add(mesh);
    this.flashes.push({ mesh, life, age: 0, grow, shared: true, base: size });
  }

  private updateFlashes(dt: number): void {
    // (67.73's orbit census: a flash or a puff of muzzle smoke under a pixel across is drawn nothing worth its call — 64 of
    // the budget scene's 142 draws from orbit; each is its own mesh, so far off it is hidden, not drawn)
    const cam = this.o.camera, focal = (typeof window !== 'undefined' ? window.innerHeight : 900) / 2 / Math.tan((cam.fov * Math.PI) / 360);
    for (let i = this.flashes.length - 1; i >= 0; i--) {
      const f = this.flashes[i];
      f.age += dt;
      const k = f.age / f.life;
      if (k >= 1) {
        this.o.scene.remove(f.mesh);
        if (!f.shared) f.mesh.geometry.dispose();
        releaseFlashMaterial(f.mesh.material as THREE.Material);   // 69.98 (1): kept for the next flash
        this.flashes.splice(i, 1);
        continue;
      }
      // a shared geometry is unit-sized: its scale carries the size (a flame's three axes keep their proportions)
      if (f.base !== undefined) f.mesh.scale.multiplyScalar((1 + (f.grow - 1) * k) * f.base / f.mesh.scale.x);
      else f.mesh.scale.setScalar(1 + (f.grow - 1) * k);
      if (f.rise) f.mesh.position.addScaledVector(f.rise, dt);
      (f.mesh.material as THREE.MeshBasicMaterial).opacity = (f.peak ?? 0.9) * (1 - k);
      const g = f.mesh.geometry;
      if (!g.boundingSphere) g.computeBoundingSphere();
      const sc = f.mesh.scale, r = g.boundingSphere!.radius * Math.max(sc.x, sc.y, sc.z);
      f.mesh.visible = 2 * r * focal >= FLASH_MIN_PX * f.mesh.position.distanceTo(cam.position);
    }
  }

  // ---------------------------------------------------------------------------
  // Camera: over the shoulder, up = the ground's normal
  // ---------------------------------------------------------------------------

  private updateCamera(dt: number): void {
    if (this.rideView()) return;   // 207 (7): riding in, the seat's view
    if (this.pilotView()) return;   // 69.17 step 3b: flying, the cockpit (V)
    if (this.gunsightView()) return;   // 69.29 step 3b: in a tank's turret, the gunner's sight (V)
    const down = this.deathCam();   // 69.81: the player down, the camera by the body
    const u = down ? this.human() : this.viewUnit();
    if (!u) return;
    const { match, camera } = this.o;
    const planet = match.state.planets[u.planetIndex];
    // 63.1f: a vehicle's hatch gunner — the camera pivots at the gunner in its hatch (its eye over the turret), not at the
    // hull's middle, where the unit's own spot is (the hull filled the view: the owner, "mostly blocked by the vehicle itself")
    const hatch = down ? null : this.hatchGun(u);
    const ship = down ? null : this.shipCarrying(u);   // 63.16: aboard a ship, the ship's foot (RIDE_CHASE)
    const feet = down ? down.feet : ship ?? (u.airborne && u.airbornePos ? u.airbornePos : hatch ? hatch.feet : this.feetDrawn(u, match.state.planets));
    const n = normalize(sub(feet, planet.position));
    if (down) { down.heading = turnHeading(down.heading, n, this.pendingYaw); this.pendingYaw = 0; }   // 69.81: the mouse turns the look round the body (none of it kept for the respawn)
    // 69.17 step 3b: flying, the chase sits behind the ship's nose (the mouse looking round it, easing back to it)
    const flown = u.id === this.controlled ? this.piloting() : null, flownNow = flown ? flightNow(match.state, flown, match.actionTick) : null;
    if (flown) this.pilotLook *= Math.max(0, 1 - 0.8 * dt);
    const heading = down ? headingOf({ surfaceDir: n, heading: down.heading }) : flownNow ? turnHeading(headingOf({ surfaceDir: n, heading: flownNow.frame.front }), n, this.pilotLook) : headingOf({ surfaceDir: n, heading: this.turretAimOf(u) ?? u.heading });   // (69.54: a turret's gunner looks along the aim)
    // Your own aim is exact; watching a bot, its aim is smoothed and kept moderate (no sky / ground stares)
    let pitch = this.pitch;
    if (u.id !== this.controlled) {
      this.watchPitch += (Math.max(-0.35, Math.min(0.45, u.aimPitch ?? 0)) - this.watchPitch) * frameLerp(0.08, dt);
      pitch = this.watchPitch;
    }
    const aim = aimDirection(n, heading, pitch);
    const right = rightOf(heading, n);
    const ps = personSetOf(match.state);
    const eye = add(feet, scale(n, ship ? RIDE_CHASE.pivot * PERSON_BODY_HEIGHT * ps.k : ps.eyeHeight));
    // zoomed: the boom stretches back and up; the sideways offset grows less, so the soldier stays off centre, not far off
    // 67.21: over the strong shoulder — the right for a right-hander (the usual third-person view), the left for a left-hander
    // (66.7 (a): in cover the camera keeps this shoulder — it used to swap to the cover's open side and lean out with a peek,
    // moving the view under the mouse; the owner: "messing up aim and sensitivity". The body's own peek step moves it, and a
    // lean (Q / E, right mouse) carries the view with the leaned eye — the sim's own lean at the eyes (core/lean.ts), eased in)
    const lo = leanOffset(u, ps.k, ps.eyeHeight), person0 = PERSON_BODY_HEIGHT * ps.k;
    const shoulder = { side: handSide(this.handFor(u.id)), lean: lo ? (lo.x * right.x + lo.y * right.y + lo.z * right.z) / person0 : 0, rise: 0 };
    const sd = this.shoulderDrawn?.unitId === u.id ? this.shoulderDrawn : { unitId: u.id, side: shoulder.side, lean: shoulder.lean, rise: shoulder.rise };
    const glide = frameLerp(0.18, dt);
    sd.side += (shoulder.side - sd.side) * glide; sd.lean += (shoulder.lean - sd.lean) * glide; sd.rise += (shoulder.rise - sd.rise) * glide;
    this.shoulderDrawn = sd;
    const side = sd.side, person = PERSON_BODY_HEIGHT * ps.k;
    let want = ship ? add(add(eye, scale(n, RIDE_CHASE.up * person * this.zoom)), scale(aim, -RIDE_CHASE.back * person * this.zoom))
      : add(add(add(eye, scale(n, (ps.cameraUp * this.zoom) + sd.rise * person)), scale(aim, -ps.cameraBack * this.zoom)), scale(right, side * ps.cameraRight * Math.min(this.zoom, 1.5) + sd.lean * person));
    // Never inside a prop: pull in to just before the first rock / trunk / structure on the boom — E5.2: their shapes as
    // shots and sight meet them (a leaning trunk, a limb, a fallen log, a building's boxes), kept a quarter person clear
    const shapes = planetShapes(match.state, u.planetIndex);   // memoised on the state's props: a lookup, and current when they change
    const boom = sub(want, eye), clear = 0.25 * ps.k;
    const near = shapes.list.length && !ship ? shapesNear([eye, add(eye, scale(boom, 0.5)), want], planet, shapes, this.nearIds) : [];
    for (let i = 1, prev = 0; i <= 16 && near.length; i++) {
      const t = i / 16;
      const pt = add(eye, scale(boom, t));
      let hit = false;
      for (let j = 0; j < near.length && !hit; j++) {
        const sh = shapes.list[near[j]];
        if (sh.bound && primDistance(pt, sh.bound) >= clear) continue;   // a tree's bound: nearer than any of its parts
        for (const p of sh.prims) if (primDistance(pt, p) < clear) { hit = true; break; }
      }
      if (hit) { want = add(eye, scale(boom, Math.max(0.4, prev))); break; }   // never closer than 40 % of the boom: inside that is the soldier's own head
      prev = t;
    }
    // 63.1f: never inside the gunner's own hull (a short boom, the gun laid high): over its top where it stands over it
    if (hatch) want = liftOverHull(want, hatch.s.type, hatch.frame, person);
    // Never under the ground: lift the camera to clear the terrain beneath it
    const rel = sub(want, planet.position);
    const r = len(rel);
    const ground = this.groundAt(planet, normalize(rel)) + (ship ? RIDE_CHASE.clear * person : 0.5 * ps.k);   // 67.11: half a person — at 0.35 the boom skimmed the grass walking downhill
    if (r < ground) want = add(planet.position, scale(normalize(rel), ground));
    const target = new THREE.Vector3(want.x, want.y, want.z);
    // L1: the commander's view eases the camera up into the overview (and back down), one move
    this.commandBlend += ((this.commanding ? 1 : 0) - this.commandBlend) * frameLerp(0.12, dt);
    if (Math.abs(this.commandBlend - (this.commanding ? 1 : 0)) < 0.002) this.commandBlend = this.commanding ? 1 : 0;
    const blend = this.commandBlend * this.commandBlend * (3 - 2 * this.commandBlend);
    this.commandStop += (this.commandStopTarget - this.commandStop) * frameLerp(0.15, dt);
    if (Math.abs(this.commandStop - this.commandStopTarget) < 0.002) this.commandStop = this.commandStopTarget;
    if (blend > 0) this.updateCommandFrame(u, dt);
    const ov = blend > 0 ? this.commandPose(u) : null;
    this.ears = ov ? { focus: ov.look, blend, stop: this.commandStop } : null;   // 67.37: the commander hears the fight from the stop's focus
    // 68.13: the stop's field of view and the diorama's tilt-shift, eased with the camera
    if (!this.baseFov) this.baseFov = camera.fov;
    const fov = ov ? this.baseFov + (ov.fov - this.baseFov) * blend : this.baseFov;
    if (Math.abs(camera.fov - fov) > 0.01) { camera.fov = fov; camera.updateProjectionMatrix(); }
    if (ov && ov.tilt * blend > 0.01) { this.tilt ??= new TiltShift(this.o.canvas as HTMLCanvasElement); this.tilt.set(ov.tilt * blend); } else this.tilt?.set(0);
    if (ov) target.lerp(new THREE.Vector3(ov.pos.x, ov.pos.y, ov.pos.z), blend);
    if (!this.camInit) { camera.position.copy(target); this.camInit = true; }
    else camera.position.lerp(target, frameLerp(0.5, dt));
    // 68.14: the commander's camera never under the ground (the owner: "sometimes … the diorama level doesn't really show
    // anything"); if a stop's pose ever lands there, lift it and say once, with the pose, so the case can be found
    if (ov) {
      const rel = sub({ x: camera.position.x, y: camera.position.y, z: camera.position.z }, planet.position);
      const nrm = normalize(rel), floor = this.groundAt(planet, nrm) + ps.k;
      if (len(rel) < floor) {
        const lifted = add(planet.position, scale(nrm, floor));
        camera.position.set(lifted.x, lifted.y, lifted.z);
        if (this.time - this.underWarnedAt > 5) {
          this.underWarnedAt = this.time;
          console.warn('[68.14] the commander camera was under the ground; lifted', { stop: +this.commandStop.toFixed(2), target: this.commandStopTarget, blend: +this.commandBlend.toFixed(2), camOverR: +(len(rel) / planet.radius).toFixed(4), groundOverR: +((floor - ps.k) / planet.radius).toFixed(4), centre: this.cmdCentre, facing: this.cmdFacing, planet: u.planetIndex });
        }
      }
    }
    if (ov) { const upB = normalize(add(scale(n, 1 - blend), scale(ov.up, blend))); camera.up.set(upB.x, upB.y, upB.z); }
    else camera.up.set(n.x, n.y, n.z);
    // Driving: converge where the line of sight lands (placeCrosshair), glided so a range jump swings the view, not snaps it; watching: a fixed distance
    // 65.45 (b): a jump over a crest held a moment (a flicker never swings the view), and the glide on the view's angle
    const driving = u.id === this.controlled && this.converge !== null;
    this.convergeHold = driving ? holdConverge(this.convergeHold, this.converge!, dt) : null;
    const wantConverge = driving ? this.convergeHold!.range : CONVERGE * ps.k;
    this.convergeDrawn = this.convergeDrawn === null ? wantConverge : glideConverge(this.convergeDrawn, wantConverge, frameLerp(0.2, dt));
    const lookShoulder = add(eye, scale(aim, this.convergeDrawn));
    const look = ov ? add(scale(lookShoulder, 1 - blend), scale(ov.look, blend)) : lookShoulder;
    camera.lookAt(look.x, look.y, look.z);
    camera.updateMatrixWorld();
    if (u.id === this.controlled && !this.commanding) this.placeCrosshair(u, dt);
  }

  /**
   * DECISIONS 207 (7) (the owner: *"when your player is on the dropship it would be cool to offer a camera view where you're
   * just inside the container seated looking at the rows of soldiers, maybe with some basic red light on then a green light to
   * exit once the doors open"*): while the player, down, rides a flight (rideOf: a rider the sim has aboard), the ride follows
   * it; it ends — the cabin gone, the camera back on the player's own view at once — when its exit passes (or the flight goes).
   * DECISIONS 208 (the owner: *"leaning yes so you could wait the n seconds in the can before it takes off"*): from boarding the
   * parked ship on its pad, alive; the ride is the rider's, not the ship's, so at take-off the cabin moves from the parked
   * ship's slot to the flight's (`key`), seats and all.
   */
  private updateRide(): void {
    const h = this.human(), state = this.o.match.state;
    const on = this.rideNow = h && (!h.alive || h.boarded) ? rideOf(state, h.id) : null;   // (208: aboard alive)
    if (this.ride && (!on || this.ride.unitId !== h!.id)) {
      if (this.ride.cabin) disposeRideCabin(this.ride.cabin);
      this.ride = null;
      this.camInit = false;   // (the usual view placed at once, not swept out of the ship)
      delete this.o.camera.userData.nearMost; delete this.o.camera.userData.shelter;
    }
    if (on) (this.ride ??= { key: on.key, unitId: h!.id, cabin: null, yaw: 0, pitch: 0, off: false, kits: new Map() }).key = on.key;
  }

  /** A rider's figure as the world draws it: its long gun and sidearm, its vest and helmet, a squad's leader in the leader's kit (personView's rules). */
  private riderKit(unitId: number, leader: boolean): PersonKit {
    const state = this.o.match.state, p = state.players.find(u => u.id === unitId)!;
    const armor = p.armour?.vest?.kind === 'light' || p.armour?.vest?.kind === 'heavy' ? p.armour.vest.kind : undefined, helmet = drawnHelmet(state.config.armour === true, p.armour);
    return { primary: primaryOf(p), ...(sidearmOf(p) !== 'pistol' ? { sidearm: sidearmOf(p) } : {}), ...(leader ? { leader: true } : {}), ...(armor ? { armor } : {}), ...(helmet !== 'light' ? { helmet } : {}), ...(p.role ? { role: p.role } : {}) };   // 201 (8): the role's kit
  }

  /**
   * 207 (7): the ridden ship's cabin hung in its slot in place of its container — the riders the sim has aboard (rideOf's
   * reading), each seated in its kit as it comes aboard, the ramp and its light. 208: the same cabin moves to whichever view
   * holds the ride (the parked ship's, then the flight's).
   */
  private rideCabinIn(team: number, v: { m: DropshipModel; payload: THREE.Object3D }, ramp: number, go = true): void {
    const r = this.ride!, now = this.rideNow;
    if (r.off || !now) { if (r.cabin) { disposeRideCabin(r.cabin); r.cabin = null; } return; }
    const riders = now.aboard.map(x => { let kit = r.kits.get(x.unitId); if (!kit) r.kits.set(x.unitId, kit = this.riderKit(x.unitId, x.leader)); return { unitId: x.unitId, seat: x.seat, kit }; });
    r.cabin ??= buildRideCabin(unitPaletteFor(TEAMS[team % TEAMS.length]), riders, r.unitId);
    if (r.cabin.root.parent !== v.m.mount) { v.m.mount.add(r.cabin.root); r.cabin.root.position.copy(v.m.payload!.at); }
    v.payload.visible = false;
    updateRideCabin(r.cabin, riders, ramp, go);
  }

  /**
   * 69.17 step 3b: the ship the human pilots (core/piloted.ts: `piloting`, its flight `piloted`), or null. P takes a seat on
   * foot and leaves it flying (`pilotQueued`); V flies from the cockpit (`pilotCockpit`); the mouse looks round the chase
   * (`pilotLook`, radians off the ship's nose, easing back).
   */
  private pilotQueued = false;
  private pilotCockpit = false;
  private pilotLook = 0;
  private piloting(): DropshipFlight | null {
    const me = this.human();
    if (me?.piloting === undefined || !me.alive) return null;
    return this.o.match.state.dropships?.flights.find(f => f.id === me.piloting && f.piloted) ?? null;
  }
  /** The pilot's intent: the keys fly the ship (2b's map), the unit itself still (aboard); P leaves the seat. */
  private pilotIntent(): UnitIntent {
    const k = this.keys, ax = (a: string, b: string) => (k.has(a) ? 1 : 0) - (k.has(b) ? 1 : 0);
    this.pilotLook = Math.max(-2.5, Math.min(2.5, this.pilotLook + this.pendingYaw));
    this.pendingYaw = 0; this.jumpQueued = false; this.reloadQueued = false; this.healQueued = false; this.caseQueued = false; this.fireQueued = false; this.seatQueued = false; this.upQueued = false; this.leaveQueued = false; this.switchQueued = false;
    const leave = this.pilotQueued;
    this.pilotQueued = false;
    const fly = { forward: ax('w', 's') || ax('arrowup', 'arrowdown'), turn: ax('d', 'a') || ax('arrowright', 'arrowleft'), strafe: ax('e', 'q'), lift: ax(' ', 'c') };
    return { forward: 0, right: 0, turn: 0, aimPitch: 0, fire: false, jump: false, fly, ...(leave ? { pilot: 'leave' as const } : {}) };
  }
  /** V while flying: the camera at the pilot's seat in the cab, looking out along the hull (banked and pitched with it); false for the chase. */
  /** 69.29 step 3b: the human's seat when it is a tank's turret (its main gun's), else null. */
  private turretSeat(): { unit: PlayerState; structure: StructureState; seat: SeatState } | null {
    const u = this.human();
    if (!u?.mount) return null;
    const s = this.o.match.state.structures.find(x => x.id === u.mount!.bodyId), seat = s?.seats?.[u.mount.seat];
    return s && seat?.main ? { unit: u, structure: s, seat } : null;
  }

  /** 69.54: the turret's aim turned by `turn` (the frame's mouse and keys) — begun at the gun's bearing, kept on the ground's plane where the gunner is. */
  private layTurretAim(u: PlayerState, bodyId: number, turn: number): Vec3 {
    const n = normalize(u.surfaceDir), was = this.turretAim?.bodyId === bodyId ? this.turretAim.heading : headingOf(u);
    const flat = normalize(sub(was, scale(n, vecDot(was, n))));
    const heading = turn ? turnHeading(flat, n, turn) : flat;
    this.turretAim = { bodyId, heading };
    return heading;
  }

  /** 69.54: the aim's bearing when `u` is the player's gunner in a tank's turret, else null. */
  private turretAimOf(u: PlayerState): Vec3 | null {
    return u.id === this.controlled && this.turretAim && u.mount?.bodyId === this.turretAim.bodyId && u.mount.seat === TANK.turretSeat ? this.turretAim.heading : null;
  }

  /** 69.29 step 3b: the gunner's sight — the camera at the gun, along its line, at a quarter of the usual field; the overlay its reticle, drop marks and state. */
  private gunsightView(): boolean {
    const t = this.gunsight && !this.commanding ? this.turretSeat() : null;
    if (!t) { if (this.gunsight && !this.turretSeat()) this.gunsight = false; this.gunsightEl = drawGunsight(this.gunsightEl, null); return false; }
    const { match, camera } = this.o, state = match.state, u = t.unit, planet = state.planets[u.planetIndex];
    if (!planet) return false;
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, n = vecNormalize(u.surfaceDir);
    // 69.54: from the barrel's muzzle (69.55: where the round leaves) along the gun as it lies — the reticle on the round's
    // line, not a person above it at the gunner's eye
    const dir = aimDirection(n, headingOf(u), u.aimPitch ?? 0), eye = tankMuzzle(t.structure, planet, ps, person, u.aimPitch ?? 0, 'main');
    const at = vecAdd(eye, vecScale(dir, 0.1 * person));   // (just past the muzzle: nothing of the gun in view)
    if (!this.baseFov) this.baseFov = camera.fov;
    camera.fov = this.baseFov * GUNSIGHT_FOV_SHARE;
    camera.updateProjectionMatrix();
    camera.position.set(at.x, at.y, at.z);
    camera.up.set(n.x, n.y, n.z);
    camera.lookAt(at.x + dir.x, at.y + dir.y, at.z + dir.z);
    camera.updateMatrixWorld();
    this.seatAim(u);   // 69.54: the aim's range and the round's dot (updateCamera's crosshair pass is skipped while the sight is up)
    // 69.54: the drop marks the round's in the breech (else the one loading next): the solid flies flatter than the explosive
    const main = t.seat.main!, h = typeof window !== 'undefined' ? window.innerHeight : 900, kind = main.loaded ?? this.tankRound;
    const he = tankRoundSpec(ps, kind), ga = gravityAccel(eye, state.planets), g = Math.sqrt(ga.x * ga.x + ga.y * ga.y + ga.z * ga.z) * ps.gravityScale * (he.gravity ?? 0);
    // the range to what the aim rests on (the crosshair's search, seatAim: from the muzzle along the aim), none past the gun's reach
    const range = this.seatRange !== null ? Math.round(this.seatRange / person) : null;
    this.gunsightEl = drawGunsight(this.gunsightEl, { round: main.loaded, next: this.tankRound, he: main.he, solid: main.solid, reloading: main.reloadLeft !== undefined, reloadLeft: main.reloadLeft, range, marks: dropMarks(he.speed, g, person, camera.fov, h) });
    return true;
  }

  private pilotView(): boolean {
    const f = this.piloting(), camera = this.o.camera;
    if (!f || !this.pilotCockpit || this.commanding) return false;
    const v = this.dropshipViews.get(f.id), hv = v ? undefined : this.heliViews.get(f.id), now = flightNow(this.o.match.state, f, this.o.match.actionTick);
    const seat = v ? v.m.pilots[0] : hv?.m.seats.find(x => x.role === 'pilot')?.at;   // (69.74 step 2c: a helicopter's, its pilot's seat)
    if (!seat || !now) return false;
    (v ?? hv)!.m.root.updateMatrixWorld(true);
    const at = seat.getWorldPosition(_pilotAt), up = now.frame.up, front = now.frame.front, person = PERSON_BODY_HEIGHT * personSetOf(this.o.match.state).k;
    camera.position.set(at.x + up.x * 0.55 * person + front.x * 0.4 * person, at.y + up.y * 0.55 * person + front.y * 0.4 * person, at.z + up.z * 0.55 * person + front.z * 0.4 * person);
    camera.up.set(up.x, up.y, up.z);
    const look = turnHeading(front, up, this.pilotLook);
    camera.lookAt(camera.position.x + look.x, camera.position.y + look.y + 0, camera.position.z + look.z);
    return true;
  }

  /** 63.16: the drawn foot of the ship `u` rides (rideOf: a flight's rider, or boarded on its pad), or null when it rides none. */
  private shipCarrying(u: PlayerState): Vec3 | null {
    if (!this.dropshipViews.size && !this.heliViews.size) return null;
    if (u.piloting !== undefined) { const p = this.dropshipViews.get(u.piloting) ?? this.heliViews.get(u.piloting); if (p) return { x: p.m.root.position.x, y: p.m.root.position.y, z: p.m.root.position.z }; }   // 69.17 step 3b: the ship it flies (69.74 step 2c: or the helicopter)
    const on = u.id === this.human()?.id ? this.rideNow : rideOf(this.o.match.state, u.id), v = on ? this.dropshipViews.get(on.key) : undefined;
    return v ? { x: v.m.root.position.x, y: v.m.root.position.y, z: v.m.root.position.z } : null;
  }

  /** 207 (7): the camera in the rider's seat, looking across at the rows (the mouse turns the head); false when the usual view is drawn. */
  private rideView(): boolean {
    const r = this.ride, camera = this.o.camera;
    if (!r?.cabin || this.commanding || this.commandBlend > 0) { delete camera.userData.nearMost; delete camera.userData.shelter; return false; }
    rideEye(r.cabin, r.yaw, r.pitch, _rideAt, _rideLook, _rideUp);
    camera.position.copy(_rideAt);
    camera.up.copy(_rideUp);
    camera.lookAt(_rideAt.x + _rideLook.x, _rideAt.y + _rideLook.y, _rideAt.z + _rideLook.z);
    camera.userData.nearMost = NEAR_MIN;   // (the cabin is a few hundredths across: cameraDepth's near plane held under it)
    camera.userData.shelter = rideShelter(r.cabin, this.shelterBox);   // (207 (7)'s follow-up: no rain in the can — the weather's fall kept out of its box)
    if (!this.baseFov) this.baseFov = camera.fov;   // (the usual view's, which updateCamera sets back)
    if (Math.abs(camera.fov - RIDE_LOOK.fov) > 0.01) { camera.fov = RIDE_LOOK.fov; camera.updateProjectionMatrix(); }
    this.tilt?.set(0);
    this.ears = null;
    camera.updateMatrixWorld();
    return true;
  }

  /**
   * The crosshair stays at the screen's centre, as in any shooter, and the gun answers it (67.10).
   * What it rests on is the first thing along the shooter's line of sight — a unit, the drawn
   * ground, a prop — and the camera converges at that range, so the centre is on the fire line
   * there. In the `solved` mode the gun elevates to land the shot there under the gravity the
   * shot feels (core/aimSolve.ts; the bots aim the same way), so the crosshair tells the truth at
   * every range in reach; out of reach, it dims and the shot goes along the line of sight. In
   * `direct` mode the shot always goes along the line of sight. Either way a small dot marks where
   * the shot would actually land when that differs from the crosshair — the drop in `direct`, a
   * ridge or a rock the arc clips in `solved`.
   *
   * It used to draw the crosshair at the predicted impact itself: at human scale a round drops nine
   * body heights in a tenth of a second, so that point slid along the ground with every mouse move
   * and the crosshair "stuck to the terrain" (the owner, Oct 10).
   */
  private placeCrosshair(u: PlayerState, dt: number): void {
    const { match, camera } = this.o;
    const state = match.state;
    // 64.11: seated, the mouse lays the gun itself (bearing and elevation) — the crosshair is the lay, no solve, no dot
    if (u.mount) { this.inReach = true; this.aimHold = null; this.xh = { friend: false, far: false, blocked: false }; this.here = null; this.seatAim(u); return; }
    const planet = state.planets[u.planetIndex];
    const n = normalize(u.surfaceDir);
    const ps = personSetOf(state);
    // the gun in hand: its speed, its life and the fall its round feels (a rocket's none) — the sim's own numbers
    const gun = weaponSpec(ps, state.config.groundWeapons !== false ? weaponOf(u) : 'rifle', state.config);
    const speed = gun.speed, fall = shotFall(gun, ps.bulletGravityScale);
    // the first thing a path meets: a unit's hitbox, the drawn ground, a prop — a teammate only when the shot would hit
    // them (friendly fire on; aimRayStopsOn), and then the crosshair warns rather than aims
    const hitAt = (p: Vec3, past?: ObstacleRef): AimHit => {
      for (const o of state.players) {
        if (!aimRayStopsOn(u, o, state.config)) continue;
        if (pointInPerson(p, o, state.planets, ps, state.config.measuredBodies === true)) return o.teamIndex === u.teamIndex ? 'friend' : 'person';   // decision 132: the shot's own volume
      }
      const rel = sub(p, planet.position);
      const r = len(rel), dir = normalize(rel);
      if (r - this.groundAt(planet, dir) < 0) return 'ground';
      // the shot's own cover (63.3 step 3, core/hits.ts): the same shapes a bullet meets — a rock by its drawn profile,
      // a trunk, a structure by its measured shape; foliage is in none of them (67.32 b: concealment never stops the aim)
      return pointBlocked(p, u.planetIndex, state, past) ? 'prop' : null;
    };
    // 66.7 (a): in cover, the crosshair converges past the cover you hold (the owner: "the auto-aim system … catches on the
    // object you're behind cover on"), as a seated gun's past its own hull — its prop is looked past for the aim and the
    // solve; the round's path (the dot) still meets it, and a round that would stop in it says so (blocked, no dot)
    const held = this.cover?.unitId === u.id && this.cover.phase === 'held' ? this.cover.point.prop : undefined;
    const aimHit = (p: Vec3): AimHit => hitAt(p, held);
    const blocked = (p: Vec3): boolean => hitAt(p) !== null;
    // the real muzzle: the person's eye height at this scale, on the fine ground
    const muzzle = muzzlePoint(u, state.planets, ps.eyeHeight, ps);
    // What the crosshair rests on: the first thing along the shooter's own line of sight (the muzzle, along the heading at
    // the look pitch), a quarter of a person a step, out to 80 people. The camera converges there (updateCamera), so the
    // screen's centre is on the fire line at that very range — the gun's bearing is the body's, and the camera rides
    // off the strong shoulder, so any fixed convergence put the shot beside what the crosshair rested on at every other range.
    // 67.32: the ground and props within AIM_NEAR_PERSONS of the muzzle are looked past (a ridge lip, a rock or a trunk a
    // step away is not *here*), and a sudden jump much nearer waits a moment before the converge follows
    const ray = aimDirection(n, headingOf(u), this.pitch);
    const step = 0.25 * ps.k, person = PERSON_BODY_HEIGHT * ps.k;
    const nearOn = aimNearOn(), near = nearOn ? AIM_NEAR_PERSONS * person : 0;   // the Lab's row: off = the first touch, as before
    const reach = Math.max(AIM_FINE_PERSONS * person, roundReach(gun));   // 65.45 (a): out to the round's reach, not 80 people
    const found = this.aimSearch(u, muzzle, ray, reach, near, aimHit);
    this.aimHold = nearOn ? holdAimTarget(this.aimHold, found, dt) : { target: found.target, range: found.range, waited: 0 };
    const target = this.aimHold.target;
    // 68.2b: the ground's *here* starts from what the crosshair rests on (hereNow goes on past it when an order is given)
    this.here = target ? { planetIndex: u.planetIndex, surfaceDir: normalize(sub(target, planet.position)) } : null;
    this.xh.friend = found.friend;   // a teammate in the line (friendly fire on): a warning, not an aim
    const range = this.aimHold.range;
    this.converge = Math.max(AIM_CONVERGE_MIN_PERSONS * person, Math.min(reach, range));   // (65.45 (a), (c): to the search's reach; at arm's length)
    this.inReach = true;
    // 65.25a: a seen enemy within the assist cone of the crosshair gets the solution (its range and drop) — the view and the
    // converge stay the crosshair's; only the gun's elevation answers the body
    // 65.45 (1): a launcher gets none — no circle, no pull onto a body: it goes where the crosshair rests, its landing marked
    this.launcherAim = !assistAppliesTo(gun);
    this.assist = this.aim === 'solved' && !this.launcherAim ? this.assistFor(u, muzzle, ray, roundReach(gun)) : null;
    // 65.25a cut 2: and across — where the body will be when the round arrives, less the wind's drift, as the yaw it leaves at
    const lead = this.assist ? this.leadFor(u, muzzle, this.assist, speed, windShare(gun, state.config), gun.life, fall, gun.drag ?? 0) : null;
    const solveAt = lead?.at ?? this.assist?.at ?? target;
    if (this.aim === 'solved') {
      // out of the round's reach (it is spent before it gets there) the crosshair dims and the look pitch stands
      const solve = (at: Vec3 | null): number | null => {
        if (!at || dist(at, muzzle) > roundReach(gun)) return null;
        const g = scale(gravityAccel(scale(add(muzzle, at), 0.5), state.planets), fall * ps.gravityScale);
        return solveAimPitchDrag(muzzle, at, n, speed, gun.drag ?? 0, g, { life: gun.life, fall, gravityScale: ps.gravityScale, planets: state.planets, stepLength: 0.25 * ps.k, lob: !!gun.blast && fall > 0 });   // 65.30: under its round's drag (65.31: a lob refined)
      };
      const clampPitch = (p: number) => Math.max(-MAX_PITCH, Math.min(MAX_PITCH, p));
      // 65.25f: the crosshair's own solution, and the assist's correction over it (its pitch and its yaw) eased — the view
      // passes straight through; a pick that changes moves the aim at most ASSIST_EASE_RATE a second
      // 65.39: one correction over the look, eased as a whole (aimTarget.ts solvedAimWant): a held pick's aim no longer
      // follows the crosshair's own solution flipping between a crest and the ground behind it
      const plain = solve(target), assisted = this.assist ? solve(solveAt) : null;
      if (this.assist) this.assistLastAt = this.time;
      const ease = this.easeAssistBy(solvedAimWant(this.pitch, plain, assisted, lead?.yaw ?? 0, clampPitch), dt, this.time - this.assistLastAt <= ASSIST_RETURN_S);
      this.inReach = (this.assist ? assisted : plain) !== null;
      this.firePitch = clampPitch(this.pitch + ease.pitch);
      this.aimYaw = ease.yaw;
    } else { this.firePitch = this.pitch; this.aimYaw = 0; this.assistEase = { pitch: 0, yaw: 0, t: this.time }; }
    // (65.31 step 4: a lob — the 40 mm — looked up past the crosshair's 80 people finds nothing on its straight line; its
    // dot marks the landing, so the crosshair stays lit: out of reach only when the round lands nowhere)
    const lob = !!gun.blast && fall > 0;
    this.xh.far = !this.inReach && !(lob && !target);
    // Where the shot would land: the round's real path from the muzzle, under the fall it feels, for its whole life (a
    // rocket's two seconds; it bursts where it stops)
    const wind = this.windAlong(u.planetIndex, windShare(gun, state.config));   // (65.31: the round's own share — windShare)   // 62.52: the dot drifts as the round will (65.25e: by the wind along its flight)
    const flown = predictShot(muzzle, scale(aimDirection(n, this.aimYaw ? turnHeading(headingOf(u), n, this.aimYaw) : headingOf(u), this.firePitch), speed), { drag: gun.drag, life: gun.life, fall, gravityScale: ps.gravityScale, planets: state.planets, blocked, stepLength: 0.25 * ps.k, wind, windStep: windAimStep(speed, person) }), at = flown.at;
    if (lob && !target && !flown.hit) this.xh.far = true;   // (65.31: a lob that lands nowhere in its flight)
    // 67.32 (c): the shot would stop a step from the muzzle, short of what the crosshair rests on (prone behind a lip the
    // arc cannot clear): the crosshair says so, and no dot is drawn at the lip under your nose
    const muzzleStopped = nearOn && muzzleBlocked(muzzle, at, solveAt, AIM_NEAR_PERSONS * person);
    // 66.7 (a): or in the cover you hold (down behind it, or a lean that does not clear it)
    const coverStopped = held !== undefined && hitAt(at) === 'prop' && aimHit(at) === null;
    this.xh.blocked = muzzleStopped || coverStopped;
    if (muzzleStopped || coverStopped) { this.dot = null; return; }
    this.placeDot(at);
  }

  /**
   * 65.45 (a): what the crosshair rests on along `ray` out to `reach` — the march (a quarter person a step to 80 people, then
   * growing), and past 80 people the bodies on the ray itself (a coarse step could pass through one), whichever is nearer.
   */
  private aimSearch(u: PlayerState, muzzle: Vec3, ray: Vec3, reach: number, near: number, hit: (p: Vec3) => AimHit): AimTarget {
    const state = this.o.match.state, ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, fine = AIM_FINE_PERSONS * person;
    const found = marchAimTarget(muzzle, ray, 0.25 * ps.k, fine, reach, near, hit);
    const end = found.target ? found.range : reach;
    if (end <= fine) return found;
    const bodies = state.players.filter(o => aimRayStopsOn(u, o, state.config)).map(o => ({ centre: hitboxCenter(o, state.planets, ps.hitboxCenter, ps), radius: 0.5 * person, friend: o.teamIndex === u.teamIndex }));
    const body = firstBodyAlong(muzzle, ray, fine, end, bodies);
    return body ? { target: { x: muzzle.x + ray.x * body.range, y: muzzle.y + ray.y * body.range, z: muzzle.z + ray.z * body.range }, range: body.range, friend: body.friend } : found;
  }

  /** 65.25a: a world point on screen (px), or null behind the camera. */
  private onScreen(at: Vec3): { x: number; y: number } | null {
    const v = new THREE.Vector3(at.x, at.y, at.z).project(this.o.camera);
    return v.z >= 1 ? null : { x: (v.x * 0.5 + 0.5) * window.innerWidth, y: (-v.y * 0.5 + 0.5) * window.innerHeight };
  }

  /** The dot where the round lands (world), on screen: only when it differs from the crosshair; smoothed, so the camera's glide doesn't make it shiver. */
  /** 65.45 (1): the weapon in hand bursts (a launcher): no assist, and its landing drawn as the impact marker. */
  private launcherAim = false;

  private placeDot(at: Vec3): void {
    const v = new THREE.Vector3(at.x, at.y, at.z).project(this.o.camera);
    const x = (v.x * 0.5 + 0.5) * window.innerWidth, y = (-v.y * 0.5 + 0.5) * window.innerHeight;
    const cx = window.innerWidth / 2, cy = window.innerHeight / 2;
    if (v.z >= 1 || !showLanding(Math.hypot(x - cx, y - cy), this.launcherAim)) this.dot = null;   // (65.45: a launcher's always)
    else if (!this.dot || Math.hypot(x - this.dot.x, y - this.dot.y) > 160) this.dot = { x, y };
    else this.dot = { x: this.dot.x + (x - this.dot.x) * 0.4, y: this.dot.y + (y - this.dot.y) * 0.4 };
  }

  // ---------------------------------------------------------------------------
  // HUD
  // ---------------------------------------------------------------------------

  private buildHud(): HTMLDivElement {
    const hud = document.createElement('div');
    hud.id = 'action-hud';
    hud.innerHTML = `
      <style>
        body.action-mode #hud { display: none; }
        #action-hud { position: fixed; inset: 0; pointer-events: none; z-index: 40; font: 600 14px/1.3 system-ui, sans-serif; color: #e9eef5; }
        #action-hud .xh { position: absolute; left: 50%; top: 50%; width: 22px; height: 22px; transform: translate(-50%, -50%); }
        #action-hud .xh::before, #action-hud .xh::after { content: ''; position: absolute; background: rgba(255,255,255,0.85); }
        #action-hud .xh::before { left: 10px; top: 0; width: 2px; height: 22px; clip-path: polygon(0 0,100% 0,100% 35%,0 35%,0 65%,100% 65%,100% 100%,0 100%); }
        #action-hud .xh::after { top: 10px; left: 0; height: 2px; width: 22px; clip-path: polygon(0 0,35% 0,35% 100%,0 100%,0 0,65% 0,100% 0,100% 100%,65% 100%); }
        #action-hud .xh.far { opacity: 0.4; }
        #action-hud .xh.friend::before, #action-hud .xh.friend::after { background: rgba(120,200,255,0.95); }
        #action-hud .xh.blocked::before, #action-hud .xh.blocked::after { background: rgba(255,150,90,0.95); }
        #action-hud .xh.blocked { transform: translate(-50%, -50%) rotate(45deg); }
        #action-hud .dot { position: absolute; left: 50%; top: 50%; width: 7px; height: 7px; margin: -3.5px 0 0 -3.5px; border-radius: 50%; background: rgba(255,214,120,0.95); box-shadow: 0 0 0 1px rgba(0,0,0,0.5); display: none; }
        #action-hud .hit { position: absolute; left: 50%; top: 50%; width: 30px; height: 30px; transform: translate(-50%, -50%) rotate(45deg); border: 2px solid #ff5a4a; opacity: 0; transition: opacity 80ms; }
        #action-hud .hurt { position: absolute; inset: 0; box-shadow: inset 0 0 120px rgba(255,40,30,0.55); opacity: 0; transition: opacity 120ms; }
        #action-hud .bar { position: absolute; left: 24px; bottom: 24px; width: 220px; }
        #action-hud .hp { height: 10px; background: rgba(255,255,255,0.15); border-radius: 5px; overflow: hidden; margin-top: 4px; }
        #action-hud .hp > div { height: 100%; background: #7fe08a; width: 100%; }
        #action-hud .aid { height: 4px; background: rgba(255,255,255,0.12); border-radius: 2px; overflow: hidden; margin-top: 3px; display: none; }
        #action-hud .aid > div { height: 100%; background: #e8f0f8; width: 0; }
        #action-hud .count { position: absolute; left: 50%; top: 16px; transform: translateX(-50%); text-shadow: 0 1px 4px #000; }
        #action-hud .feed { position: absolute; right: 24px; top: 20px; text-align: right; font-weight: 500; }
        #action-hud .points { position: absolute; left: 50%; top: 42px; transform: translateX(-50%); display: flex; gap: 6px; }
        #action-hud .points > div { width: 30px; height: 30px; border-radius: 6px; display: grid; place-items: center; font-weight: 700; color: #10131a; position: relative; overflow: hidden; box-shadow: 0 1px 4px rgba(0,0,0,0.5); }
        #action-hud .points > div i { position: absolute; left: 0; bottom: 0; height: 4px; }
        #action-hud .msg { position: absolute; left: 50%; top: 38%; transform: translate(-50%, -50%); text-align: center; text-shadow: 0 2px 8px #000; }
        #action-hud .msg b { display: block; font-size: 36px; letter-spacing: 0.04em; }
        #action-hud .help { position: absolute; left: 50%; bottom: 20px; transform: translateX(-50%); opacity: 0.7; font-weight: 500; }
        #action-hud .ordernote { position: absolute; left: 50%; top: 50%; margin-top: 46px; transform: translateX(-50%); font-size: 13px; text-shadow: 0 1px 4px #000; white-space: nowrap; display: none; }
        #action-hud .airnote { position: absolute; left: 50%; top: 96px; transform: translateX(-50%); font-size: 13px; text-shadow: 0 1px 4px #000; white-space: pre; text-align: center; opacity: 0.9; display: none; }
      </style>
      <div class="help"></div><div class="ordernote"></div><div class="airnote"></div>`;
    document.body.appendChild(hud);
    document.body.classList.add('action-mode');
    for (const k of ['help', 'ordernote', 'airnote']) this.el[k] = hud.querySelector(`.${k}`) as HTMLElement;
    // 67.35: the ground HUD (lane E's mock) — the match, you, what you hold, the crosshair, the slot, the feed, the corner map
    this.ground = new GroundHud(hud);
    this.updateHelp();
    return hud;
  }

  private updateHelp(): void {
    const crew = this.crewSwitch();   // DECISIONS 250: seated as a driver or a gunner, the other seat's key
    const menu = `${crew ? ` · ${l0Keys('seatSwitch').join(' / ')} the ${crew.name}'s seat${crew.bot ? ' (its crew changes places)' : ''}` : ''}${this.o.onExit ? ' · M menu' : ''}`;
    // 68.2b: the keys from the one key map (ui/l0Keys.ts) — the carried items' and the order menu's only while you carry
    // them or can use them (the owner couldn't find how to throw)
    const keyOf = (id: string) => l0Keys(id).join(' / ');
    const word = (id: string) => (L0_KEYS.find(b => b.id === id)?.what ?? id).split(/ \(| · | — /)[0];
    // (68.3: Tab takes the next unit, held it picks one; the number keys select the squads the menu orders)
    const swap = this.team !== null && this.teammates().length > 1 ? ` · ${keyOf('take')} next unit (hold: pick) · ${keyOf('squads')} squads · ${keyOf('letGo')} let go` : '';
    const me = this.human();
    const kit = [
      (me?.grenades?.he ?? 0) > 0 ? 'he' : '', (me?.grenades?.smoke ?? 0) > 0 ? 'smoke' : '',
      (me?.drones ?? 0) > 0 && this.o.onDroneTablet ? 'tablet' : '', me && squadOf(this.o.match.state, me.id) ? 'orders' : '',
    ].filter(Boolean).map(id => ` ${keyOf(id)} ${word(id)} ·`).join('');
    // 69.33: the ride's line for its driver, parked or moving (riding() is the speed: 0 parked read as no ride, so a parked
    // tank's driver was shown the gun pit's line), and only for the driver (a moving 6×6's gunner was shown the throttle's);
    // a passenger at a hatch rides and looks about
    const ride = this.riding(), seatDef = me?.mount ? BODIES[this.o.match.state.structures.find(x => x.id === me.mount!.bodyId)?.type as BodyKey]?.seats?.[me.mount.seat] : undefined;
    // 69.36: a tank's fighting seats name their own weapon and keys (the turret read the bullet gun's line: no X, no V)
    const turret = this.turretSeat(), onTank = !!me?.mount && this.o.match.state.structures.find(x => x.id === me.mount!.bodyId)?.type === 'tank';
    const help = this.team === null ? `Watching — bots only${menu}`
      : this.controlled === null ? `Commanding — your units fight on their own${swap}${menu}`
      : ride !== null && seatDef?.role === 'driver' ? `Riding: W throttle · S brake / back up · A / D and the mouse steer · E get off · ${ride!.toFixed(0)} people/s${swap}${menu}`
      : this.human()?.mount && this.inBay(this.human()!) ? `In the bay: look about · ${this.moveUp() ? `tap E: the ${this.moveUp()!.name}'s seat · hold E: get out` : 'E get out'}${swap}${menu}`   // 63.1d's follow-up
      : turret?.seat.main ? `${tankGunnerHelp(turret.seat.main, this.tankRound, this.gunsight)}${swap}${menu}`   // 69.36: the turret's main gun, its coax, X / V / J
      : this.human()?.mount && this.seatGuns() && onTank ? `${tankCommanderHelp()}${swap}${menu}`   // 69.36: the cupola's .50
      : this.human()?.mount && this.seatGuns() ? `Seated: mouse lays the gun · hold click to fire · R reload the belt · E get out${swap}${menu}`   // 65.22: a bullet gun
      : this.human()?.mount && this.seatLauncher() ? (l => `Seated: mouse lays the ${l.kind === 'rocket' ? 'tubes' : 'tube'} · click fire${l.kind === 'missile' ? ' (on the lock)' : ''} · ${l.reloading ? 'reloading' : `${l.loaded} loaded`} · ${l.store} in the rack${l.loaded <= 0 && l.store <= 0 && !l.reloading ? ' — dry' : ' · R reload'} · E get out${swap}${menu}`)(this.seatLauncher()!)   // 69.21: a launcher's rounds and its store (DECISIONS 240)
      : this.human()?.mount && this.seatMortar() ? `${this.seatMortar()} · E get out${swap}${menu}`   // 69.23: the tube answers calls; nothing to lay
      : seatDef?.role === 'passenger' ? `Riding: look about · E get out${ride ? ` · ${ride.toFixed(0)} people/s` : ''}${swap}${menu}`   // 69.33
      : this.human()?.mount ? `Seated: mouse or A / D traverse${this.pitBearing()} · W / S power · X shell · click fire · E get out${swap}${menu}`   // 63.1f's follow-up
      : `WASD move · mouse aim · wheel zoom · click fire · R reload · H bandage ·${kit}${this.o.match.state.config.beacons && beaconsLeft(this.human() ?? { beacons: 0 }) > 0 ? ' B beacon ·' : ''} X ${this.cycleText()} · E seat · Space jump · C crouch · Z prone${(this.human()?.drones ?? 0) > 0 && this.o.onDroneTablet ? ' · G drone' : ''}${this.o.onDroneFeed && (this.o.match.state.drones ?? []).some(d => d.alive && d.teamIndex === this.human()?.teamIndex) ? ' · V drone feed' : ''}${this.o.onMapZoom ? ' · − = map zoom' : ''} · Esc release mouse${swap}${menu}`;
    // 67.35: on foot the ground HUD's keys card and prompts carry the keys; this line stays for watching, commanding, a seat and a ride
    const onFoot = this.controlled !== null && !this.riding() && !this.human()?.mount;
    const line = onFoot ? '' : help;
    if (this.el.help.textContent !== line) { this.el.help.textContent = line; this.el.help.style.display = line ? '' : 'none'; }
    // 68.2b: the order note on the ground (the table draws its own in the overlay)
    const note = !this.commanding && this.commandNote && this.commandNote.until > this.time ? this.commandNote.text : '';
    if (this.el.ordernote.textContent !== note) { this.el.ordernote.textContent = note; this.el.ordernote.style.display = note ? 'block' : 'none'; }   // ('block': '' fell back to the sheet's none — the note never showed)
    // 63.13 step 2: the side's gunships — inbound, on station (the time left, the load), Winchester, returning and why (ui/gunshipStatus.ts)
    const air = this.team !== null && countOf(this.o.match.state.config, 'gunships') ? gunshipLines(this.o.match.state, this.team, this.o.match.actionTick).join('\n') : '';
    if (this.el.airnote.textContent !== air) { this.el.airnote.textContent = air; this.el.airnote.style.display = air ? 'block' : 'none'; }
  }

  private updateHud(): void {
    const t0 = performance.now();
    this.updateHelp();
    const state = this.o.match.state, me = this.human();
    // the message in the middle: down and the next wave, out of tickets, click to play
    let message = { title: '', sub: '' };
    if (me && !this.over) {
      const respawn = state.modeState.respawn;
      const ride = !me.alive || me.boarded ? this.ride : null, now = ride ? this.rideNow : null, flight = now?.flight;
      const takeoff = me.boarded ? shipTakeoff(state, me.id, this.o.match.actionTick) : null;
      const view = ride ? ` · ${l0Keys('rideView').join(' / ')}: ${ride.off ? 'the seat' : 'the usual view'}` : '';
      if (takeoff !== null) {
        // DECISIONS 208: in the can on the pad — alive, its own unit, till the ship takes off (how many aboard: the cabin's reading)
        // (69.20, DECISIONS 237: E gets out down the ramp before it lifts)
        message = { title: 'Aboard', sub: `${now?.aboard.length ?? 1} aboard · takes off in ${Math.ceil(takeoff)} s · ${l0Keys('use').join(' / ')}: get out${view}` };
      } else if (ride && flight) {
        // 207 (7): riding in — how many aboard (the sim's), your turn at the ramp (207 (2)'s shipEta, as the countdown), the view's key
        const tick = this.o.match.actionTick, eta = Math.ceil(shipEta(state, me.id, tick) ?? 0);
        const when = tick < flight.landTick ? `out in ${eta} s` : `ramp down — out in ${eta} s`;
        message = { title: 'Riding in', sub: `${now!.aboard.length} aboard · ${when}${view}` };
      } else if (!me.alive && respawn && (respawn.tickets[me.teamIndex] ?? 0) > 0) {
        const wave = Math.round(personSetOf(state).respawnWave / PHYS_DT);
        const other = this.teammates().some(p => p.alive) ? ` · ${l0Keys('take').join(' / ')}: take another unit` : '';
        // DECISIONS 207 (2): bound for a ship, its real arrival (core/dropship.ts shipEta); with wave ships, the wave before that boards it
        const eta = shipEta(state, me.id, this.o.match.actionTick), next = Math.ceil((wave - (this.o.match.actionTick % wave)) * PHYS_DT);
        message = { title: 'Down', sub: eta !== null ? `Back in ${Math.ceil(eta)} s by dropship${other}` : state.config.waveShips ? `Next wave in ${next} s${other}` : `Back in ${next} s${other}` };
      } else if (!me.alive) message = { title: 'Down', sub: respawn ? 'Out of tickets — watching your squad' : 'Watching your squad' };
      else if (!this.commanding && document.pointerLockElement !== this.o.canvas && !this.softCapture) message = { title: 'Click to play', sub: '' };
      else if (me.alive && !me.boarded && state.shipPads?.[me.teamIndex]?.dest && boardingOpen(state, me.teamIndex)) {
        // DECISIONS 209 (5): back at the base with the ship taking riders — where it goes and when, while near it
        const pad = state.shipPads[me.teamIndex], R = state.planets[pad.planetIndex].radius, person = PERSON_BODY_HEIGHT * personSetOf(state).k;
        if (me.planetIndex === pad.planetIndex && Math.acos(Math.min(1, (() => { const n = normalize(me.surfaceDir); return n.x * pad.dir.x + n.y * pad.dir.y + n.z * pad.dir.z; })())) * R <= SHIP_RIDE.basePeople * person) {
          const call = waveShipCall(state, me.teamIndex, this.o.match.actionTick), aboard = boardingSortie(state, me.teamIndex)?.units.some(id => state.players.find(p => p.id === id)?.boarded);
          message = { title: '', sub: `Dropship ${aboard ? `leaves in ${Math.ceil(call?.seconds ?? 0)} s` : `leaves ${DROPSHIP.sortieSeconds} s after the first aboard`} → ${shipDestLabel(state, pad.dest!.at)} · up its ramp or ${l0Keys('use').join(' / ')} to board` };
        }
      }
    }
    // the seated gun's dial and the ride, the host's own state
    const seat = me?.mount ? state.structures.find(s => s.id === me.mount!.bodyId)?.seats?.[me.mount.seat] : undefined;
    const speed = this.riding();
    const seconds = this.o.match.actionTick * PHYS_DT;
    // 69.17 step 3b: flying, a readout under the crosshair's place (the ship's speed, height, climb; its hull) and no aim
    const flown = this.piloting(), flownNow = flown ? flightNow(state, flown, this.o.match.actionTick) : null;
    let pilotNote: string | undefined;
    if (!flown && me?.alive && !me.boarded && !this.commanding && shipInReach(state, me, this.o.match.actionTick)) pilotNote = `${l0Keys('pilot').join('/')} fly this ship`;   // (a ship in reach: P takes its seat)
    if (flown && flownNow) {
      const person = PERSON_BODY_HEIGHT * personSetOf(state).k, pose = flownNow.flight!.pose, up = normalize(pose.dir);
      const climb = (flownNow.vel.x * up.x + flownNow.vel.y * up.y + flownNow.vel.z * up.z) / person, speed = Math.hypot(flownNow.vel.x, flownNow.vel.y, flownNow.vel.z) / person;
      const hull = flown.health !== undefined ? ` · hull ${Math.round((100 * flown.health) / shipBodyOf(flown).health)} %` : '';   // (69.74 step 2c: a helicopter's out of its own)
      pilotNote = `Flying · ${Math.round(speed)} people/s · ${Math.round(pose.height / person)} up · climb ${climb >= 0 ? '+' : ''}${climb.toFixed(1)}${hull} · ${l0Keys('pilot').join('/')} get out · ${l0Keys('rideView').join('/')} ${this.pilotCockpit ? 'the chase' : 'the cockpit'}`;
    }
    const crosshair = this.controlled === null || !me?.alive || this.commandBlend >= 0.2 || !!this.ride || !!flown ||   // (208: aboard, alive — in the seat or (63.16) the view after the ship — no aim)
      (this.radial?.open && !this.commanding) ? 'hidden' as const   // (68.2b: the menu's hub sits on the crosshair)
      : this.xh.friend ? 'friend' as const : this.xh.blocked ? 'blocked' as const : this.xh.far ? 'far' as const : 'aim' as const;
    const view = groundHudView(state, {
      viewerId: this.controlled === null ? null : me?.id ?? null, time: seconds, heldSince: this.heldSince, helpHeld: this.helpHeld, commanding: this.commandBlend > 0.05,
      ...(this.nearMissAt !== undefined ? { sinceNearMiss: this.time - this.nearMissAt } : {}),   // 65.28: the cue's pulse
      ...(seat?.weapons.length ? { gun: { shell: seat.weapons[this.gunWeapon % seat.weapons.length], power: this.gunPower } } : {}),
      ...(speed !== null ? { ride: { speed, throttle: this.keys.has('w') ? 1 : 0 } } : {}),
      crosshair, hit: this.time < this.hitMarkerUntil, hurt: this.time < this.hurtUntil,
      medicTarget: me && me.healLeft === undefined && me.treating === undefined ? this.medicTarget() : null,
      note: pilotNote ?? (this.aidNote && this.time < this.aidNote.until ? this.aidNote.text : undefined),
      message, secondsLeft: this.o.tickLimit ? (this.o.tickLimit - this.o.match.actionTick) * PHYS_DT : null, mapShown: this.mapShown,
      squads: this.commanding || this.controlled === null ? [] : this.stripLines(),   // 68.3b
      ...(me && !this.commanding ? { coverHeld: this.coverOf(me), ...(this.gaitNow ? { gait: this.gaitNow } : {}) } : {}),   // (63.8: the crosshair's cue for the gun down and the sprint)   // 66.7 (a): the crosshair's bracket and the corner's lean keys
      seatOffer: me && !this.commanding ? this.seatOffer()?.name ?? null : null,   // 63.1d's follow-up: what E gets into
    });
    this.ground.update(view, this.time, crosshair !== 'hidden' && this.dot ? { ...this.dot, impact: this.launcherAim } : null, crosshair !== 'hidden' && this.assist ? this.onScreen(this.assist.at) : null);   // 65.25a: the body the solution is for
    this.updateMissileHud(me ?? undefined, crosshair !== 'hidden');   // 65.49 step 2
    // 67.35 / decision 136: the HUD's own cost a frame (a rolling mean, ms), read by the measurement
    this.hudMs += (performance.now() - t0 - this.hudMs) * 0.05;
  }

  /** 67.36: the wind's drift on a puff of smoke at `at` (world units a second): `windAt` there, felt (windView), null in calm air or with the switch off. */
  private smokeDriftAt(at: Vec3, planetIndex: number): THREE.Vector3 | undefined {
    const state = this.o.match.state, planet = state.planets[planetIndex];
    if (!planet || !state.config.wind) return undefined;
    const r = sub(at, planet.position), dist = Math.hypot(r.x, r.y, r.z);
    if (dist < 1e-9) return undefined;
    const n = { x: r.x / dist, y: r.y / dist, z: r.z / dist };
    const felt = windFelt(windAt(state, planetIndex, n, windSeconds(state)), n, (G * planet.mass) / (dist * dist));
    return felt.strength > 0 ? smokeDrift(felt, PERSON_BODY_HEIGHT * personSetOf(state).k) : undefined;
  }

  private feed(l: KillLine): void {
    const v = this.o.match.state.players.find(p => p.id === l.victimId);
    const mine = this.controlled !== null && (l.killerId === this.controlled || l.victimId === this.controlled);
    this.ground.addFeed({ killer: l.killer, killerTeam: l.killerTeam, victim: v?.name ?? '?', victimTeam: v?.teamIndex ?? 0, kind: l.kind, mine });
  }

  // ---------------------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------------------

  /** The commander's team, in id order (the 1–9 keys). */
  /**
   * 65.8 (the medic): the hurt teammate your bandage would go on — the nearest in reach that you are looking at
   * (within 35° of the look line); null when none, and H bandages you.
   */
  /** 63.1d's follow-up: a seat's name for the prompt — the driver's, a gunner's, a bay; a bike's saddle is a ride. */
  private seatName(s: StructureState, i: number): string {
    if (s.type === 'motorcycle') return 'ride';
    const def = BODIES[s.type as BodyKey].seats[i];
    if (def?.name) return def.name;   // (249: the tank's gunner and commander, both weapon seats, told apart)
    return def?.role === 'driver' ? 'driver' : def?.role === 'weapon' ? 'gunner' : def?.entry === 'bay' ? 'bay' : 'seat';
  }

  /**
   * 63.1d's follow-up (the owner: "I can't ever get into the driver seat"): the seat E takes now, on foot — on a vehicle in
   * reach that the crosshair rests on, the free seat whose entry is nearest where it rests (`pick`: sent with E, the sim
   * takes it when it can); else the nearest free seat in reach (the sim's own choice, nothing sent). Null: no seat to take.
   *
   * 63.1k (the owner: "player needs the ability to take a seat / take over an emplacement"): a seat its own side's bot
   * holds is offered as well — "take over" — on a vehicle by the same nearest entry as a free one, and a gun's when no free
   * seat is in reach; sent as a pick, the sim takes it from the bot (core/seats.ts stepSeats). An enemy's seat, or one a
   * person holds, is not offered.
   */
  private seatOffer(): { structureId: number; seat: number; name: string; pick: boolean } | null {
    const u = this.human();
    if (!u || !u.alive || u.mount || u.airborne) return null;
    const state = this.o.match.state, planet = state.planets[u.planetIndex];
    if (!planet) return null;
    const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k;
    if (state.structures.some(drives)) {
      const muzzle = muzzlePoint(u, state.planets, ps.eyeHeight, ps), ray = aimDirection(normalize(u.surfaceDir), headingOf(u), this.pitch);
      const far = add(muzzle, scale(ray, 8 * person));
      const hit = firstMovingHit(muzzle, far, u.planetIndex, state);
      const s = hit?.ref.kind === 'structure' ? state.structures.find(x => x.id === hit.ref.id) : undefined;
      if (hit && s && hullOffersSeats(s) && SEAT_POINTS[s.type as BodyKey] && vehicleInReach(s, u, ps.seatReach, planet.radius, person)) {   // (63.1q: never a wreck)
        const at = add(muzzle, scale(sub(far, muzzle), hit.t));
        let best = -1, bestD = Infinity;
        for (let i = 0; i < (s.seats?.length ?? 0); i++) {
          const e = seatTaken(s.seats![i]) && !this.takeable(s, i) ? null : seatPoint(s, i, 'entry', planet.radius, person);
          if (!e) continue;
          const d = dist(add(planet.position, scale(e, this.groundAt(planet, e))), at);
          if (d < bestD) { bestD = d; best = i; }
        }
        if (best >= 0) return { structureId: s.id, seat: best, name: this.takeable(s, best) ? `take over ${this.seatName(s, best)}` : this.seatName(s, best), pick: true };
      }
    }
    const c = seatChoiceInReach(state, u, ps.seatReach, person);
    const cs = c ? state.structures.find(x => x.id === c.structureId) : undefined;
    if (c && cs) return { ...c, name: this.seatName(cs, c.seat), pick: false };
    // 63.1k: no free seat in reach — the nearest seat in reach its own side's bot holds (a gun's: the pit's, the HMG's)
    let take: { s: StructureState; seat: number; d: number } | null = null;
    for (const s of state.structures) {
      if (s.planetIndex !== u.planetIndex || drives(s) || !s.seats?.length || s.health <= 0) continue;
      for (let i = 0; i < s.seats.length; i++) {
        if (!this.takeable(s, i)) continue;
        const d = arcDistance(u.surfaceDir, s.surfaceDir, planet.radius);
        if (d <= ps.seatReach && (!take || d < take.d)) take = { s, seat: i, d };
      }
    }
    return take ? { structureId: take.s.id, seat: take.seat, name: `take over ${this.seatName(take.s, take.seat)}`, pick: true } : null;
  }

  /** 63.1k: is seat `i` of `s` held by a bot of the human's side (living, held by no person)? Then E takes it over. */
  private takeable(s: StructureState, i: number): boolean {
    const me = this.human(), state = this.o.match.state, occ = seatUnit(s.seats?.[i]);
    if (!me || occ === null || occ === me.id || state.controllers?.[occ] !== undefined) return false;
    const bot = state.players.find(p => p.id === occ);
    return !!bot && bot.alive && bot.teamIndex === me.teamIndex;
  }

  /** 63.1d's follow-up: seated in a vehicle's bay, the front seat a second E moves up to (the sim's rule, core/seats.ts moveUpSeat) and its name; null: E gets out. */
  private moveUp(): { seat: number; name: string } | null {
    const u = this.human();
    if (!u?.mount) return null;
    const state = this.o.match.state, to = moveUpSeat(state, u);
    const s = to === null ? undefined : state.structures.find(x => x.id === u.mount!.bodyId);
    return to !== null && s ? { seat: to, name: this.seatName(s, to) } : null;
  }

  /**
   * DECISIONS 250: seated as a driver or a gunner, the seat the switch moves to (the sim's rule, core/seats.ts crewSwitchSeat)
   * and its name, and whether a bot of the side sits there (the two change places); null when there is none, or a person or
   * a crew record holds it (the sim refuses that).
   */
  private crewSwitch(): { seat: number; name: string; bot: boolean } | null {
    const u = this.human();
    if (!u?.mount) return null;
    const state = this.o.match.state, to = crewSwitchSeat(state, u);
    const s = to === null ? undefined : state.structures.find(x => x.id === u.mount!.bodyId);
    if (to === null || !s) return null;
    const there = s.seats?.[to], bot = seatTaken(there) && this.takeable(s, to);
    return seatTaken(there) && !bot ? null : { seat: to, name: this.seatName(s, to), bot };
  }

  private medicTarget(): PlayerState | null {
    const me = this.human(), state = this.o.match.state;
    if (!me || !me.alive || me.airborne || me.mount || state.config.medical === false) return null;
    const planets = state.planets, n = normalize(me.surfaceDir), ray = aimDirection(n, headingOf(me), this.pitch);
    const eye = muzzlePoint(me, planets, personSetOf(state).eyeHeight, personSetOf(state));
    let best: PlayerState | null = null, bestDot = Math.cos(35 * Math.PI / 180);
    for (const p of state.players) {
      if (p.teamIndex !== me.teamIndex || p.id === me.id || !p.alive || p.health >= 100 || p.healLeft !== undefined) continue;
      const at = hitboxCenter(p, planets), d = { x: at.x - eye.x, y: at.y - eye.y, z: at.z - eye.z }, l = len(d);
      if (l > medicReach(state) * 1.5 || l < 1e-9) continue;
      const dot = (d.x * ray.x + d.y * ray.y + d.z * ray.z) / l;
      if (dot > bestDot && nearestPatient(me, { ...state, players: [p] }) === p) { best = p; bestDot = dot; }
    }
    return best;
  }

  /**
   * 69.47 step 1 (GameConfig.charges): K — the engineer kneels to set a charge where it stands (on the face of what is within
   * reach), on its timer or the wire, as an input from the seat that holds the unit; the Match says why not on the note line.
   */
  private setCharge(fuse: 'timer' | 'wire'): void {
    const me = this.human();
    if (!me || this.team === null || !this.o.match.state.config.charges) return;
    const r = this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'charge', unitId: me.id, step: 'place', fuse }));
    this.note(r.ok ? (fuse === 'wire' ? 'Setting a charge on the wire — hold still (I sets it off)' : 'Setting a charge on a 10 s timer — hold still') : r.reason);
  }

  /** 69.47 step 1: I — the engineer's wire pulled: its charges on the wire within reach go off. */
  private pullWire(): void {
    const me = this.human();
    if (!me || this.team === null || !this.o.match.state.config.charges) return;
    const r = this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'charge', unitId: me.id, step: 'blow' }));
    if (!r.ok) this.note(r.reason);
  }

  /**
   * 65.15: F / T — throw a grenade along your aim, lofted THROW_LOFT above it (a throw is a lob, not a shot), as an
   * input from the seat that holds the unit; the match refuses it when none is left or the last throw is still going.
   */
  private throwGrenade(kind: GrenadeKind): void {
    const me = this.human();
    if (!me || this.team === null) return;
    // 63.1u: driving a vehicle with smoke charges, the smoke key pops its dispensers (a ring of smoke round the hull, fuller
    // ahead of the vehicle: the owner's "a focus on the front of the vehicle"); the Match says why not (no charges, not ready,
    // not the driver)
    if (kind === 'smoke' && me.mount && (this.o.match.state.config.vehicleSmoke ?? 0) > 0) {
      this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'vehicleSmoke', unitId: me.id }));
      return;
    }
    if (!((me.grenades?.[kind] ?? 0) > 0)) return;
    const n = vecNormalize(me.surfaceDir);
    const h = headingOf({ surfaceDir: n, heading: me.heading });
    const lift = this.pitch + THROW_LOFT;
    const dir = vecNormalize(vecAdd(vecScale(h, dcos(lift)), vecScale(n, dsin(lift))));
    this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'throw', unitId: me.id, grenade: kind, dir }));
  }

  /** 125a: B — a task for the unit you drive: plant the beacon where you stand (through apply, as a commander's order is). */
  private plantBeacon(): void {
    const me = this.human();
    if (!me || this.team === null || !this.o.match.state.config.beacons || beaconsLeft(me) <= 0) return;
    this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'order', team: this.team, to: { units: [me.id] }, tier: 'task', order: { kind: 'beacon', planetIndex: me.planetIndex, surfaceDir: me.surfaceDir }, by: 'human' }));   // (69.72 step 5, DECISIONS 304: the player's, tagged)
  }

  private teammates(): PlayerState[] {
    return this.o.match.state.players.filter(p => p.teamIndex === this.team).sort((a, b) => a.id - b.id);
  }

  /** The next living teammate after the one driven now (`dir` 1 / −1), or the current one. */
  private nextTeammate(dir: number): number | null {
    const team = this.teammates();
    const alive = team.filter(p => p.alive);
    if (alive.length === 0) return this.controlled;
    const at = team.findIndex(p => p.id === this.controlled);
    for (let k = 1; k <= team.length; k++) {
      const p = team[(at + dir * k + team.length * 2) % team.length];
      if (p.alive) return p.id;
    }
    return this.controlled;
  }

  /** Take a unit (or let go with null): the Match switches the bots; the camera and aim follow. */
  private take(unitId: number | null, onlyAlive = false): void {
    if (this.team === null || unitId === this.controlled) return;
    if (unitId !== null && onlyAlive && !this.o.match.state.players.find(p => p.id === unitId)?.alive) return;
    if (!this.o.match.apply(this.o.match.stamp(seatActor(this.team), { kind: 'possess', target: unitId === null ? null : { unit: unitId } })).ok) return;
    this.controlled = unitId;
    this.heldSince = this.o.match.actionTick * PHYS_DT;   // 67.35: the keys card opens for this body's first seconds
    this.keys.clear();
    this.mouseDown = false;
    this.pendingYaw = 0;
    const u = unitId === null ? null : this.o.match.state.players.find(p => p.id === unitId);
    if (u) { this.pitch = this.firePitch = 0; this.converge = null; this.aimHold = null; }   // the tour's shots 15/18: a bot taken mid-stare left the player looking at the sky — start level; the aim follows from here
    this.stance = u?.stance ?? 'stand';   // take a unit as it is (crouched stays crouched, the pistol stays up)
    this.weapon = u ? weaponOf(u) : 'rifle';   // 65.13 / 65.14: the primary when nothing else is up
    this.updateHelp();
  }

  private bindInput(): void {
    const canvas = this.o.canvas;
    const live = () => this.team !== null;
    // Captured = pointer lock, or (where the browser refuses lock) a soft capture after a click until Esc
    const locked = () => document.pointerLockElement === canvas || this.softCapture;
    const capture = () => {
      try {
        const r = (canvas.requestPointerLock as unknown as () => Promise<void> | void)?.call(canvas);
        if (r && typeof (r as Promise<void>).catch === 'function') (r as Promise<void>).catch(() => { this.softCapture = true; });
      } catch { this.softCapture = true; }
    };
    const onLockError = () => { this.softCapture = true; };
    document.addEventListener('pointerlockerror', onLockError);
    this.cleanup.push(() => document.removeEventListener('pointerlockerror', onLockError));
    const on = <K extends keyof WindowEventMap>(type: K, fn: (e: WindowEventMap[K]) => void) => {
      window.addEventListener(type, fn as EventListener, true);
      this.cleanup.push(() => window.removeEventListener(type, fn as EventListener, true));
    };
    on('keydown', e => {
      if (e.key === '`') return;                       // the dev console keeps its key
      if (this.suspended) return;                     // I8: the drone tablet has the keys
      const key = e.key.toLowerCase();
      if (key === 'r' && this.over) { this.o.onRestart?.(); }
      if (key === 'escape' && this.radial?.open && !this.commanding) { this.ringCancel(); e.stopImmediatePropagation(); return; }   // 68.2b: no order (68.41: nor from G's)
      if (key === 'escape' && this.commanding && this.layoutC && this.radial?.open) { this.ringCancel(); e.stopImmediatePropagation(); return; }   // 68.41: the table's ring, cancelled
      if (key === 'escape' && this.commanding && this.card?.back()) { e.stopImmediatePropagation(); return; }   // 68.41: the card's armed order dropped, or its page closed
      // 68.41, layout C: while a ring is open the number keys pick its list's rows (a row past the list's end does nothing)
      if (this.layoutC && this.radial instanceof CommandRing && this.radial.open && /^[0-9]$/.test(key)) { if (!e.repeat && this.radial.pickNumber(Number(key))) this.ringPicked(); e.preventDefault(); e.stopImmediatePropagation(); return; }
      if (key === 'escape' && this.picker?.open) { this.picker.close(); this.tabDownAt = null; e.stopImmediatePropagation(); return; }   // 68.3: nobody taken
      if (key === 'escape' && this.memberPick && !this.commanding) { this.memberPick = null; this.note('Ordering your squad again'); e.stopImmediatePropagation(); return; }   // 68.5: the pick dropped
      if (key === 'escape' && this.commanding && this.pendingRide) { this.pendingRide = null; this.note('Ride cancelled'); e.stopImmediatePropagation(); return; }   // 68.18
      if (key !== 'shift') this.coverTap = false;   // 66.7 (a): Shift with another key (? for the keys card) is that key's
      if (key === 'escape') { this.softCapture = false; this.mouseDown = false; if (this.commanding) { this.setCommanding(false); e.stopImmediatePropagation(); return; } }
      if (l0KeyIs('topo', key) && !e.repeat) { this.topoOn = !this.topoOn; e.stopImmediatePropagation(); return; }   // 68.17: the topo overlay (the commander's view)
      if (key === '.' && !e.repeat && this.planetTable?.active) { this.planetTable.toggle(); e.stopImmediatePropagation(); return; }   // 68.42: globe ↔ flat map
      if (key === 'o' && !e.repeat && commandViewEnabled()) { this.setCommanding(!this.commanding); e.stopImmediatePropagation(); return; }
      if (key === 'k' && !e.repeat) { this.friendliesKey = !this.friendlies; e.stopImmediatePropagation(); return; }   // 68.7: your side at a glance
      if (this.commanding && key === 'f' && !e.repeat) { if (!this.radial?.open) this.openRadial('f'); e.stopImmediatePropagation(); return; }
      if (this.commanding && this.layoutC && key === 'g' && !e.repeat) { if (!this.radial?.open) this.openRadial('g'); e.stopImmediatePropagation(); return; }   // 68.41: the side's calls at the cursor
      if (this.commanding && this.card && !e.repeat && !this.radial?.open) { const a = this.card.pressKey(key); if (a !== undefined) { this.cardAction(a); e.preventDefault(); e.stopImmediatePropagation(); return; } }   // 68.41: the card's keys
      if (this.commanding && /^[0-9]$/.test(key) && !e.repeat) { this.selectByNumber(Number(key)); e.stopImmediatePropagation(); return; }
      if (this.commanding && /^(w|a|s|d|q|e|arrowup|arrowdown|arrowleft|arrowright)$/.test(key)) { this.panKeys.add(key); e.preventDefault(); e.stopImmediatePropagation(); return; }   // 68.14: pan and turn the view
      if (this.commanding && key === 'r' && !this.over && !e.repeat) { this.radarDetail = !this.radarDetail; this.note(this.radarDetail ? 'Radar detail on' : 'Radar detail off'); e.stopImmediatePropagation(); return; }   // 68.43: the radars' detail
      if (this.commanding && (key === 'c' || key === ' ') && !e.repeat) { this.cmdPan = null; e.preventDefault(); e.stopImmediatePropagation(); return; }   // 68.14: back to the team (or the selection)
      if (this.commanding && key !== 'm' && !(key === 'r' && this.over)) { e.stopImmediatePropagation(); return; }   // the unit stands while you command
      // 69.17 step 3b: P takes a ship's pilot seat in reach (on foot) or leaves it (flying); flying, the keys fly it (2b's map) and V the cockpit
      if (l0KeyIs('pilot', key) && !e.repeat) { if (live() && !this.over) this.pilotQueued = true; e.stopImmediatePropagation(); return; }
      if (this.piloting()) {
        if (l0KeyIs('rideView', key) && !e.repeat) { this.pilotCockpit = !this.pilotCockpit; e.stopImmediatePropagation(); return; }
        if (/^(w|a|s|d|q|e|c| |arrowup|arrowdown|arrowleft|arrowright)$/.test(key)) { this.keys.add(key); e.preventDefault(); e.stopImmediatePropagation(); return; }
      }
      if (l0KeyIs('cover', key)) { if (!e.repeat) { this.coverTap = live() && !this.over; this.shiftDownAt = this.time; } e.stopImmediatePropagation(); return; }   // 66.7 (a): taken on release (63.8: held, a sprint)
      if (key === 'm' && !e.repeat) { this.o.onExit?.(); e.stopImmediatePropagation(); return; }
      if ((key === '-' || key === '=' || key === '+') && this.o.onMapZoom) { if (!e.repeat) this.o.onMapZoom(key === '-' ? -1 : 1); e.stopImmediatePropagation(); return; }
      if (this.ride && l0KeyIs('rideView', key) && !e.repeat) { this.ride.off = !this.ride.off; e.stopImmediatePropagation(); return; }   // 207 (7): the seat · the usual view
      if (l0KeyIs('map', key) && !e.repeat) { this.mapShown = !this.mapShown; writeMapShown(this.mapShown); e.stopImmediatePropagation(); return; }   // 67.35: the corner map on / off
      if (l0KeyIs('help', key)) { this.helpHeld = true; e.stopImmediatePropagation(); return; }   // 67.35: the keys card while held
      // 59.6c possession: Tab — the next teammate; 1–9 — that teammate. 68.2b: the carried items, the order menu and let go
      // bind from the one key map (ui/l0Keys.ts: G grenade, T smoke, Q the drone tablet, F orders, Backspace let go)
      if (live() && !e.repeat && !this.over) {
        // 68.3: Tab — a tap takes the next teammate, held it opens the picker; 1–4 select a squad for the menu, 0 all of them
        const lean = this.cover?.phase === 'held' ? leanOf(key) : null;   // 66.7 (a): in cover Q / E lean (out of it, the tablet and use)
        if (lean) { this.leanKey = lean; e.stopImmediatePropagation(); return; }
        if (key === 'tab') { this.tabDownAt = performance.now(); e.preventDefault(); }
        else if (/^[0-9]$/.test(key)) this.selectSquads(Number(key));
        else if (l0KeyIs('letGo', key)) { this.take(null); e.preventDefault(); }
        else if (l0KeyIs('tablet', key) && this.o.onDroneTablet) { this.o.onDroneTablet(); e.stopImmediatePropagation(); return; }
        else if (l0KeyIs('orders', key) && this.controlled !== null && !(this.layoutC && (this.radial?.open || this.gDownAt !== null))) { this.l0FDownAt = performance.now(); this.fOnMate = this.teammateUnderCrosshair()?.id ?? null; }   // hold: the menu; tap: the contextual order (68.5: on a teammate, its own)
        else if (key === 'v' && this.turretSeat()) { this.gunsight = !this.gunsight; e.stopImmediatePropagation(); return; }   // 69.29 step 3b: the gunner's sight
        else if (key === 'v' && this.o.onDroneFeed) { this.o.onDroneFeed(); e.stopImmediatePropagation(); return; }
        // 62.2: C — crouch or stand up; Z — go prone or stand up
        else if (key === 'c') this.stance = this.stance === 'crouch' ? 'stand' : 'crouch';
        // Phase 65: R — reload; X — the other weapon (the pistol, or back to the rifle)
        else if (key === 'r') this.reloadQueued = true;
        else if (key === 'b') this.plantBeacon();   // 125a: plant the beacon here
        else if (l0KeyIs('he', key) && this.layoutC) { if (!this.radial?.open && this.l0FDownAt === null) this.gDownAt = performance.now(); }   // 68.41: a tap throws (on release), held the calls
        else if (l0KeyIs('he', key)) this.throwGrenade('he');         // 65.15: an HE grenade along your aim, lofted (G)
        else if (l0KeyIs('smoke', key)) this.throwGrenade('smoke');   // 65.15: a smoke grenade (T)
        else if (l0KeyIs('demoCharge', key)) this.kDownAt = this.time;   // 69.47 step 1: the engineer's charge — set on K's release (a tap: the timer; held: the wire)
        else if (l0KeyIs('wire', key)) this.pullWire();   // 69.47 step 1: I — the wire pulled
        else if (key === 'h') this.healQueued = true;   // 65.7: apply a bandage
        else if (l0KeyIs('ammoCase', key)) this.caseQueued = true;   // 69.45: the support's case down or up
        else if (key === 'x' && this.human()?.mount) {   // 64.11: seated, X picks the gun's shell (69.29: in a tank's turret, the round loaded next)
          // (69.51, DECISIONS 260: with a round in the breech, no reload under way and the other kind stowed, X swaps it — back to
          // its store, the other loaded in a reload's time; else X picks the round loaded next, as before)
          const t = this.turretSeat(), m = t?.seat.main;
          if (m && m.loaded && m.reloadLeft === undefined && m[m.loaded === 'he' ? 'solid' : 'he'] > 0) { this.tankRound = m.loaded === 'he' ? 'solid' : 'he'; this.tankSwapQueued = true; }
          else if (t) this.tankRound = this.tankRound === 'he' ? 'solid' : 'he';
          else this.gunWeapon++;
        }
        // S7: X cycles the long gun → pistol → rocket tube (with the match's switch on) → the long gun (65.13 / 65.14: the kit's primary)
        else if (key === 'x') { const h = this.human(); if (h) { const cyc = weaponCycle(h, this.o.match.state.config); this.weapon = cyc[(cyc.indexOf(this.weapon) + 1) % cyc.length]; } }   // (65.32: the player's own loadout's list, else primary / pistol / tube)
        else if (l0KeyIs('seatSwitch', key) && this.human()?.mount) this.switchQueued = true;   // DECISIONS 250: driver ↔ gunner
        // 64.11: E — get into the nearest free seat in reach, or out of the one you are in
        else if (key === 'e') {
          // 63.1d's follow-up: in a bay with a front seat free, a tap moves up and a hold gets out (decided on release or at the hold)
          const me = this.human();
          if (me?.mount && this.inBay(me) && this.moveUp()) { if (this.eHeldSince === null) this.eHeldSince = this.time; }
          else this.seatQueued = true;
        }
        else if (key === 'z') this.stance = this.stance === 'prone' ? 'stand' : 'prone';
        else if (l0KeyIs('dig', key)) this.digHeld = true;   // 66.8: held, it digs
      }
      if (live()) {
        this.keys.add(key);
        if (key === ' ' && !e.repeat) this.jumpQueued = true;
        if (!e.repeat && (key === ' ' || key === 'w' || key === 'a' || key === 's' || key === 'd' || key.startsWith('arrow'))) this.healHold = null;   // a fresh step breaks off the bandage (65.11)
      }
      if (key === ' ' || key.startsWith('arrow')) e.preventDefault();
      e.stopImmediatePropagation();
    });
    on('keyup', e => {
      if (this.suspended) return;
      if (l0KeyIs('help', e.key.toLowerCase()) || e.key === '/') this.helpHeld = false;   // (Shift may lift first: the key reads /)
      if (this.commanding && e.key.toLowerCase() === 'f') { if (this.ringKey === 'f') this.releaseRadial(); }
      else if (this.commanding && this.layoutC && e.key.toLowerCase() === 'g') { if (this.ringKey === 'g') this.releaseRadial(); }   // 68.41: the calls' choice
      else if (l0KeyIs('orders', e.key.toLowerCase()) && this.l0FDownAt !== null) this.releaseL0Orders();
      else if (this.layoutC && l0KeyIs('he', e.key.toLowerCase()) && this.gDownAt !== null) { if (this.radial?.open && this.ringKey === 'g') this.releaseL0Calls(); else { this.gDownAt = null; if (live() && !this.over) this.throwGrenade('he'); } }   // 68.41: held, the calls; a tap, the grenade
      else if (e.key.toLowerCase() === 'tab' && this.tabDownAt !== null) this.releasePicker();   // 68.3: the picker's choice, or (a tap) the next teammate   // 68.2b: give what the menu points at (a tap: the contextual order)
      if (e.key.toLowerCase() === 'e' && this.eHeldSince !== null) { if (this.time - this.eHeldSince < E_HOLD_S) this.upQueued = true; this.eHeldSince = null; }   // 63.1d's follow-up: a tap
      if (this.leanKey && leanOf(e.key.toLowerCase()) === this.leanKey) this.leanKey = null;   // 66.7 (a): back down behind it
      if (l0KeyIs('dig', e.key.toLowerCase())) this.digHeld = false;   // 66.8: let go — the dig stops, its depth kept
      if (l0KeyIs('demoCharge', e.key.toLowerCase()) && this.kDownAt !== null) { const wire = this.time - this.kDownAt >= CHARGE_HOLD_S; this.kDownAt = null; this.setCharge(wire ? 'wire' : 'timer'); }   // 69.47 step 1
      if (l0KeyIs('cover', e.key.toLowerCase())) {
        // 66.7 (a): a tap takes cover — 63.8: one shorter than GAIT.sprintHold; held longer it was a sprint, and a sprint's
        // release never takes cover
        const tap = this.shiftDownAt !== null && this.time - this.shiftDownAt < GAIT.sprintHold;
        if (this.coverTap && tap && !this.commanding) this.coverQueued = true;
        this.coverTap = false; this.shiftDownAt = null;
      }
      this.keys.delete(e.key.toLowerCase()); this.panKeys.delete(e.key.toLowerCase()); e.stopImmediatePropagation();
    });
    on('mousedown', e => {
      if (this.suspended) return;
      if (this.commanding) {
        // 68.41, layout C: an open ring takes the click (the left picks what a release would, the right cancels); an armed
        // card order is given where the left clicks (Shift keeps it), the right drops it
        if (this.layoutC && this.radial instanceof CommandRing && this.radial.open) { if (e.button === 0 && this.radial.click()) this.ringPicked(); else if (e.button === 2) this.ringCancel(); e.preventDefault(); e.stopImmediatePropagation(); return; }
        if (this.card?.armed && e.target === canvas && (e.button === 0 || e.button === 2)) {
          if (e.button === 2) this.card.back();
          else { this.cursor = { x: e.clientX, y: e.clientY }; const here = this.hereAtCursor(); if (!here) this.note('Point at the ground'); else if (this.giveAtTable(this.card.armed, here)) this.card.given(e.shiftKey); }
          e.preventDefault(); e.stopImmediatePropagation(); return;
        }
        // 68.4: a click or a drag selects (finished on mouseup)
        if (e.target === canvas && e.button === 0) this.dragFrom = { x: e.clientX, y: e.clientY };
        if (e.target === canvas && (e.button === 2 || e.button === 1)) { this.panFrom = { x: e.clientX, y: e.clientY }; e.preventDefault(); }   // 68.14: drag the ground
        e.stopImmediatePropagation();
        return;
      }
      if (live() && !this.over && e.target === canvas && !locked()) capture();
      else if (this.layoutC && this.radial instanceof CommandRing && this.radial.open) { if (e.button === 0 && this.radial.click()) this.ringPicked(); else if (e.button === 2) this.ringCancel(); }   // 68.41: the ring takes the click
      else if (this.radial?.open || this.picker?.open) { /* 68.2b / 68.3: a menu has the mouse — no shot */ }
      else if (live() && locked() && e.button === 0) { this.mouseDown = true; this.fireQueued = true; }
      else if (live() && locked() && e.button === 2) this.peekHeld = true;   // 66.7 (a): out of cover while held
      if (e.target === canvas) e.stopImmediatePropagation();
    });
    on('mouseup', e => { if (this.suspended) return; if (e.button === 2 || e.button === 1) this.panFrom = null; if (e.button === 2) this.peekHeld = false; if (this.commanding && e.button === 0 && this.dragFrom) this.finishSelect(e.clientX, e.clientY, e.shiftKey); if (e.button === 0) this.mouseDown = false; e.stopImmediatePropagation(); });
    on('mousemove', e => {
      if (this.suspended) return;
      if (this.commanding) { if (this.panFrom && !this.radial?.open) { this.dragPan(e.clientX - this.panFrom.x, e.clientY - this.panFrom.y); this.panFrom = { x: e.clientX, y: e.clientY }; } this.cursor = { x: e.clientX, y: e.clientY }; this.radial?.move(e.clientX, e.clientY); e.stopImmediatePropagation(); return; }
      // 68.2b: the order menu on the ground takes the (locked) mouse while it is open — its travel, from the crosshair; the aim stays
      const open = this.radial?.open ? this.radial : this.picker?.open ? this.picker : null;   // (68.3: the picker too)
      if (open) {
        const x = this.l0Vec.x + e.movementX, y = this.l0Vec.y + e.movementY, r = Math.hypot(x, y), cap = 240;
        this.l0Vec = r > cap ? { x: x * cap / r, y: y * cap / r } : { x, y };
        open.move(window.innerWidth / 2 + this.l0Vec.x, window.innerHeight / 2 + this.l0Vec.y);
        e.stopImmediatePropagation();
        return;
      }
      if (this.ride?.cabin && locked()) { this.ride.yaw -= e.movementX * MOUSE_SENSITIVITY; this.ride.pitch -= e.movementY * MOUSE_SENSITIVITY; e.stopImmediatePropagation(); return; }   // 207 (7): the rider's head
      if (live() && locked()) {
        this.pendingYaw += e.movementX * MOUSE_SENSITIVITY;
        this.pitch = Math.max(-MAX_PITCH, Math.min(MAX_PITCH, this.pitch - e.movementY * MOUSE_SENSITIVITY));
        if (this.aim !== 'solved') this.firePitch = this.pitch;
      }
      e.stopImmediatePropagation();
    });
    on('wheel', e => {
      if (this.suspended) return;
      e.stopImmediatePropagation();
      const we = e as WheelEvent;
      if (we.deltaY === 0) return;
      if (this.layoutC && this.radial instanceof CommandRing && this.radial.open) { this.radial.wheel(we.deltaY, we.deltaMode); return; }   // 68.41: the wheel steps the ring's list
      // 68.13: in the commander's view the wheel moves along its stops (overview → squad → diorama)
      if (this.commanding) { const s = this.wheelSteps.push(we.deltaY, we.deltaMode, performance.now()); if (s) this.commandStopTarget = Math.max(0, Math.min(LADDER_LAST, this.commandStopTarget + s)); return; }   // … diorama, orbit (63.7 c); 68.14: a flick is one step
      this.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, this.zoom * (we.deltaY > 0 ? 1.15 : 1 / 1.15)));
      saveZoom(this.zoom);
    });
    on('contextmenu', e => { if (e.target === canvas) { e.preventDefault(); e.stopImmediatePropagation(); } });
    on('blur', () => { this.keys.clear(); this.panKeys.clear(); this.mouseDown = false; });
  }

  /** X31: the bike the human rides, as its speed in people a second (absent when not riding). */
  private riding(): number | null {
    const me = this.human();
    const s = me?.mount ? this.o.match.state.structures.find(x => x.id === me.mount!.bodyId) : undefined;
    if (!s || !drives(s)) return null;
    return Math.abs(s.drive!.speed) / (PERSON_BODY_HEIGHT * personSetOf(this.o.match.state).k);
  }

  dispose(): void {
    this.explosions?.dispose(); this.explosions = null;   // 67.54
    this.flares?.dispose(); this.flares = null;   // 69.58 step 2
    for (const w of this.wreckViews.values()) { this.o.scene.remove(w.root); w.dispose(); }   // 69.3b, the ship's crash
    this.wreckViews.clear();
    for (const v of this.dropshipViews.values()) if (v.fall) { this.o.scene.remove(v.fall.pieces); v.fall.dispose(); }
    for (const v of this.heliViews.values()) this.o.scene.remove(v.m.root);   // 69.59 step 1
    this.heliViews.clear();
    this.motorPools?.dispose(); this.motorPools = undefined;   // 67.75
    this.rearmArms?.dispose(); this.rearmArms = undefined;   // 69.31
    this.lighterViews?.dispose(); this.lighterViews = null;   // 69.10
    this.tilt?.dispose(); this.tilt = null;
    this.wearView?.dispose(); this.wearView = null;   // 63.5
    this.missileFlights?.dispose(); this.missileFlights = null; this.missileHud?.dispose(); this.missileHud = null;   // 65.49 step 2
    if (this.baseFov) { this.o.camera.fov = this.baseFov; this.o.camera.updateProjectionMatrix(); }
    this.radial = null; this.rings.today?.dispose(); this.rings.f?.dispose(); this.rings.g?.dispose(); this.rings = {};   // (68.41: C's rings)
    this.card?.dispose(); this.card = null;
    this.commandOverlay?.dispose(); this.commandOverlay = null;
    setScoreboardSource(null); hideEndCard();   // 67.14
    for (const m of this.bikeViews.values()) this.o.scene.remove(m.root);
    this.bikeViews.clear();
    this.rampDown.clear(); this.vehicleForms.clear(); this.vehicleHinges.clear(); this.lidShares.clear();
    for (const v of this.personViews.values()) v.dispose();
    this.crowd?.dispose(); this.crowd = null;
    this.deaths?.dispose(); this.deaths = null;
    this.sound.dispose();   // 67.27 (b): the drones' hum stops
    this.landers?.dispose(); this.landers = null;
    this.callMarks?.dispose(); this.callMarks = null;
    this.personViews.clear();
    for (const f of this.cleanup) f();
    this.cleanup.length = 0;
    if (this.brass) { this.o.scene.remove(this.brass.group); this.brass.dispose(); this.brass = null; }   // 65.47
    for (const f of this.flashes) { this.o.scene.remove(f.mesh); if (!f.shared) f.mesh.geometry.dispose(); releaseFlashMaterial(f.mesh.material as THREE.Material); }
    this.puffGeo?.dispose(); this.puffGeo = null;
    if (this.guidedRockets) { this.guidedRockets.dispose(this.o.scene); this.guidedRockets = null; }   // 63.13 step 2
    this.flashes.length = 0;
    for (const m of this.shellMeshes) { this.o.scene.remove(m); m.geometry.dispose(); (m.material as THREE.Material).dispose(); }
    this.shellMeshes.length = 0;
    this.tracers.dispose();
    this.markers?.dispose();
    this.markers = null;
    this.planetTable?.dispose();
    this.radarReach?.dispose();
    this.radarReach = null;
    this.planetTable = null;
    this.markerGround.clear();
    this.hud.remove();
    document.body.classList.remove('action-mode');
    if (document.pointerLockElement) document.exitPointerLock();
  }
}

/** 63.1d's follow-up: how long E is held, in a bay with a front seat free, to get out instead of moving up (s). */
const E_HOLD_S = 0.4;
/** 69.47 step 1: how long K is held to set a charge on the wire instead of its timer (s). */
const CHARGE_HOLD_S = 0.4;

// Small vector helpers on plain {x,y,z} (rendering side; no determinism needed)
function add(a: Vec3, b: Vec3): Vec3 { return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z }; }
function sub(a: Vec3, b: Vec3): Vec3 { return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z }; }
function scale(a: Vec3, k: number): Vec3 { return { x: a.x * k, y: a.y * k, z: a.z * k }; }
function len(a: Vec3): number { return Math.sqrt(a.x * a.x + a.y * a.y + a.z * a.z); }
function dist(a: Vec3, b: Vec3): number { return len(sub(a, b)); }
function normalize(a: Vec3): Vec3 { const l = len(a) || 1; return { x: a.x / l, y: a.y / l, z: a.z / l }; }
/** 66.9: a slot as the cover point the rest of take-cover reads (its prop for the aim's look-past, its facing, its shelter). */
function slotPoint(s: CoverSlot): CoverPoint {
  return { planetIndex: s.planetIndex, key: s.key, dir: s.dir, kind: 'rock', prop: s.prop, facing: s.facing, shelter: s.shelter, peeks: [] };
}
/** 66.7 (a): the lean a key gives in cover — the peek row's first key leans left, its second right (Q, E). */
function leanOf(key: string): 'left' | 'right' | null { const [l, r] = l0Keys('peek'); return key === l?.toLowerCase() ? 'left' : key === r?.toLowerCase() ? 'right' : null; }
